import { ChecklistVariantWarning } from '../catalog/ChecklistVariantWarning';
import { useCcgPolicy } from '../marketplace/useCcgPolicy';
import { CcgItemDetails } from './CcgItemDetails';
import {
  listingCategoryForLabel,
  ccgReviewScopeKey,
  serializeCcgReview,
  validateCcgLocalSpecifics,
  ccgPresentationCompatibility,
  ccgWorkflowFailure,
  type CcgFieldDecision,
} from '../marketplace/ccg-review-state';
import { ebayCategoryPolicyReviewIdentity, type MarketplaceCcgReviewRequest } from '@tcvs/shared';
import type {
  CardLookupRequest,
  ReviewChecklistSelection,
  ReviewChecklistSelectionRevision,
} from '@tcvs/shared';
import {
  ImportedResearchDetails,
  ImportedPriceSuggestion,
  getImportedCardResearch,
  escapeImportedDescription,
  type ImportedListingField,
} from './ImportedResearchDetails';
import { CardLookupPanel, type CardLookupItem } from './CardLookupPanel';
import {
  useChecklistSubsets,
  allSubsetOptions,
  subsetKey,
  subsetDefinitionsForCard,
} from './useChecklistSubsets';
import {
  readShowMatchDetailsPreference,
  writeShowMatchDetailsPreference,
} from './review-display-preferences';
import { useChecklistSearch } from '../checklists/useChecklistSearch';
import {
  classifyCardReviewState,
  isChecklistPlaceholderLabel,
  type CardReviewState,
} from '@tcvs/shared';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Archive, PackageMinus } from 'lucide-react';
import { useStorageApi } from '../storage/storage-api';
import {
  useVersionedImageReview,
  VersionedImageReviewStatus,
} from '../catalog/useVersionedImageReview';
import type {
  CardIdentity,
  ChecklistContextSuggestionCandidate,
  ChecklistEntry,
  ChecklistIssueGuidanceResponse,
  ChecklistIssueType,
  ChecklistParallelDefinition,
  ChecklistParallelMatch,
  ChecklistSourceSelection,
  CardPresentation,
  ReviewChecklistProduct,
} from '../shared/types';
import { EMPTY_IDENTITY, IdentityForm } from '../shared/IdentityForm';
import { FilterableSelect, type FilterableSelectOption } from '../shared/FilterableSelect';
import { formatIdentity } from '../shared/PredictionView';
import {
  type CatalogListCardRecord,
  type ChecklistResolveResponse,
  useCatalogApi,
} from '../catalog/catalog-api';
import { checklistSuggestionStatusLabel } from './batch-checklist-onboarding';
import { loadOrderedBatchCards, positionBatchCards } from './batch-card-order';
import {
  CardResearchPricing,
  ListingPriceInput,
  adjustUsdInputByDollars,
  cardResearchPricingDraft,
  type CardResearchPricingDraft,
} from '../catalog/CardResearchPricing';
import { centsToUsdInput, usdInputToCents } from '../catalog/card-research';
import { CopyIdButton } from '../shared/CopyIdButton';
import { reviewListReturnContextFrom } from './review-list-return-state';
import {
  applicableParallelDefinitions,
  checklistSelectionConflictMessages,
  checklistCandidateLabel,
  checklistEntryForParallelChoice,
  checklistRosterSubset,
  checklistSubsetChoiceForEntry,
  checklistSubsetChoices,
  NO_PARALLEL_DEFINITION_ID,
  parallelSelectionOptions,
  reviewedIdentityFromChecklist,
  checklistEntryPolicy,
  checklistSelectionSource,
  samePhysicalChecklistSelection,
  checklistPlainChoiceAllowed,
} from '../catalog/checklist-parallel-selection';
import { ChecklistParallelFallback } from '../catalog/ChecklistParallelFallback';
import {
  EBAY_LISTING_BASE_TITLE_MAX_LENGTH,
  EBAY_SPORT_CARD_FEATURES,
  EBAY_NON_SPORT_CARD_FEATURES,
  CHECKLIST_PRODUCT_SUGGESTION_MODEL_ID,
  LISTING_DESCRIPTION_MAX_LENGTH,
  cardIdentitySerialIsValid,
  ebayTitleIdentityEvidence,
  listingTemplateValuesForCard,
  renderListingTemplateDescription,
  renderListingTemplateTitle,
  type MarketplaceCardComparablesResponse,
  type MarketplaceComparableItem,
  type EbayActiveComparableItem,
  type EbayActiveComparablesResponse,
  checklistComparableKey,
  normalizeChecklistParallelName,
  cardPresentationLabel,
  suggestedEbayFeatures,
  type MarketplaceListingTemplate,
  type MarketplaceListingTemplateSource,
  type EbayItemSpecifics,
  type EbaySellerHubSinglesCategory,
} from '@tcvs/shared';
import { useMarketplaceApi, type MarketplaceListingEditLock } from '../marketplace/marketplace-api';
import { useAuth } from '../auth/AuthContext';
import { hasAnyGroup } from '../auth/cognito';
import { reviewTemplateEditorHref } from '../marketplace/listing-template-navigation';
import { BatchIdentificationGrid } from './BatchIdentificationGrid';
import { BatchTransferTools } from './BatchTransferTools';
import { BulkImportedListingTools } from './BulkImportedListingTools';
import { ChecklistSelectionChanges } from './ChecklistSelectionChanges';
import {
  OwnershipLabel,
  OwnershipSummary,
  useOwnershipLabels,
} from '../consignment/OwnershipLabel';
import { CardPresentationEditor } from './CardPresentationEditor';
import { EbayItemDetailsModal } from './EbayItemDetailsModal';
import { cardResearchQuery } from '../catalog/card-research';
import {
  REVIEW_IMAGE_ROTATION_MAX,
  REVIEW_IMAGE_ROTATION_MIN,
  REVIEW_IMAGE_ROTATION_NUDGE,
  REVIEW_IMAGE_QUARTER_TURN,
  REVIEW_IMAGE_ROTATION_STEP,
  clampReviewImageRotation,
  estimateReviewImageRotation,
  normalizeReviewImageRotation,
  reviewImageRotationParts,
  reviewImageQuarterTurnScale,
  rotateReviewImageToJpeg,
} from './review-image-rotation';

export type WorkbenchState = CardReviewState;

export type ReviewCondition =
  | 'needs-checklist'
  | 'missing-number'
  | 'no-match'
  | 'parallel-decision'
  | 'ready';

const STATE_ORDER: WorkbenchState[] = ['ready', 'awaiting-images', 'image-issues', 'completed'];

const STATE_LABELS: Record<WorkbenchState, string> = {
  ready: 'Ready to Review',
  'awaiting-images': 'Awaiting image approval',
  'image-issues': 'Image Issues',
  completed: 'Completed',
};

export function imageQualityUpdateForIssue(imageIssue: boolean): {
  status: 'ACCEPTED' | 'REJECTED';
  rejectedReason?: string;
} {
  return imageIssue
    ? {
        status: 'REJECTED',
        rejectedReason: 'Flagged during identity review',
      }
    : { status: 'ACCEPTED' };
}

export function pricingDraftWithBatchDefault(
  card: CatalogListCardRecord,
  identity: Partial<CardIdentity>,
  batchDefaultPrice: string,
): CardResearchPricingDraft {
  const draft = cardResearchPricingDraft(card, identity);
  if (
    card.listingPrice !== undefined ||
    draft.price.trim() !== '' ||
    usdInputToCents(batchDefaultPrice) === null
  ) {
    return draft;
  }
  return { ...draft, price: batchDefaultPrice.trim() };
}

export function checklistSourceSelectionForReview(
  checklistId: string,
  entry: ChecklistEntry | undefined,
  identity: CardIdentity,
  parallel?: ChecklistParallelDefinition,
  card?: CatalogListCardRecord,
): ChecklistSourceSelection | null {
  if (entry?.source === undefined || entry.unresolvedClassification !== undefined) return null;
  const expected = reviewedIdentityFromChecklist(
    entry,
    parallel,
    card ? { card, checklistId } : undefined,
  );
  if (expected === null) return null;
  const ownKeys = new Set([
    ...Object.keys(identity),
    ...Object.keys(expected),
  ] as (keyof CardIdentity)[]);
  if ([...ownKeys].some((field) => identity[field] !== expected[field])) return null;
  const same = (left: string | undefined, right: string | undefined) =>
    (left ?? '').trim() === (right ?? '').trim();
  if (
    !same(identity.category, expected.category) ||
    !same(identity.set, expected.set) ||
    !same(identity.number, expected.number) ||
    !same(identity.name, expected.name) ||
    !same(identity.year, expected.year) ||
    !same(identity.brand, expected.brand) ||
    !same(identity.subset, expected.subset) ||
    !same(identity.variation, expected.variation)
  ) {
    return null;
  }
  return {
    checklistId,
    ...entry.source,
    naturalKey: entry.key,
    subset: checklistRosterSubset(entry),
    ...(parallel === undefined
      ? {}
      : {
          parallel: {
            definitionId: parallel.definitionId,
            name: parallel.name,
            sourceSubsetId: parallel.source.subsetId,
            snapshotFingerprint: parallel.source.snapshotFingerprint,
          },
        }),
  };
}

/** Source evidence does not assert the operator identity matches the unresolved source. */
export function unresolvedSourceEvidenceSelection(
  checklistId: string | null,
  entry: ChecklistEntry | undefined,
): ChecklistSourceSelection | undefined {
  if (
    checklistId === null ||
    entry?.source === undefined ||
    entry.unresolvedClassification === undefined
  )
    return undefined;
  return {
    checklistId,
    ...entry.source,
    naturalKey: entry.key,
    subset: checklistRosterSubset(entry),
  };
}

export function storedChecklistSourceSelectionForReview(
  card: CatalogListCardRecord,
  checklistId: string,
  identity: CardIdentity,
): ChecklistSourceSelection | null {
  if (card.reviewChecklistSelection?.requiresReview) return null;
  const lineage =
    card.identityReviewDraft !== undefined
      ? card.identityReviewDraft.checklistSourceSelection
      : card.checklistSourceLineage &&
          ['DRAFT', 'VERIFIED'].includes(card.checklistSourceLineage.status)
        ? card.checklistSourceLineage
        : undefined;
  if (!lineage || lineage.checklistId !== checklistId) return null;
  if (identity.number.trim() !== lineage.naturalKey.number.trim()) return null;

  const normalizedSubset = (value: string | undefined) => (value ?? '').trim();
  const identitySubset = normalizedSubset(identity.subset);
  const lineageSubset = normalizedSubset(lineage.subset);
  if (
    (identitySubset === '' && lineageSubset !== '' && lineageSubset !== 'base') ||
    (identitySubset !== '' && identitySubset !== lineageSubset)
  ) {
    return null;
  }

  const identityParallel = (identity.variation ?? '').trim();
  const lineageParallel = (lineage.parallel?.name ?? '').trim();
  if (identityParallel !== lineageParallel) return null;
  return lineage;
}

/** Historical source context is read advice for pricing only, never a restored write selection. */
function storedPricingSourceSelectionForReview(
  card: CatalogListCardRecord,
  checklistId: string,
  identity: CardIdentity,
): ChecklistSourceSelection | null {
  if (card.reviewChecklistSelection?.requiresReview) return null;
  const source =
    card.identityReviewDraft !== undefined
      ? card.identityReviewDraft.checklistSourceSelection
      : card.checklistSourceLineage;
  if (
    !source ||
    source.checklistId !== checklistId ||
    identity.number.trim() !== source.naturalKey.number.trim() ||
    (identity.subset ?? '').trim() !== (source.subset ?? '').trim()
  )
    return null;
  return normalizeChecklistParallelName(identity.variation) ===
    normalizeChecklistParallelName(source.parallel?.name)
    ? source
    : null;
}

const CHECKLIST_ISSUE_OPTIONS: Array<{ value: ChecklistIssueType; label: string }> = [
  { value: 'MISSING_CARD', label: 'Card is missing' },
  { value: 'INCORRECT_CARD', label: 'Card details are incorrect' },
  { value: 'MISSING_SUBSET', label: 'Set/subset is missing' },
  { value: 'INCORRECT_SUBSET', label: 'Set/subset is incorrect' },
  { value: 'MISSING_PARALLEL', label: 'Parallel is missing' },
  { value: 'INCORRECT_PARALLEL', label: 'Parallel is incorrect' },
  { value: 'OTHER', label: 'Other checklist issue' },
];

export function shouldAutomaticallyResolveCard(condition: ReviewCondition): boolean {
  return condition !== 'needs-checklist';
}

export function cardChecklistSelectOptions(
  checklists: readonly ReviewChecklistProduct[],
  batchChecklistId: string | null,
): FilterableSelectOption[] {
  return [
    {
      value: '',
      label: batchChecklistId === null ? 'Batch default: none' : 'Batch default',
    },
    ...batchChecklistSelectOptions(checklists),
  ];
}

export function checklistProductLabel(checklist: ReviewChecklistProduct): string {
  const year = checklist.product.year.trim();
  const brand = checklist.product.brand.trim();
  const product = checklist.product.product.trim();
  const prefix = [year, brand].filter((value) => value !== '').join(' ');
  const productLabel =
    prefix !== '' && product.toLocaleLowerCase().startsWith(prefix.toLocaleLowerCase())
      ? product
      : [prefix, product].filter((value) => value !== '').join(' ');
  return `${productLabel} · ${checklist.category}`;
}

export function batchChecklistSelectOptions(
  checklists: readonly ReviewChecklistProduct[],
): FilterableSelectOption[] {
  return checklists.map((checklist) => ({
    value: checklist.checklistId,
    label: checklistProductLabel(checklist),
    searchText: [
      checklist.category,
      checklist.product.year,
      checklist.product.brand,
      checklist.product.product,
      checklist.source?.targetId,
    ]
      .filter((value) => value !== undefined)
      .join(' '),
  }));
}

function mergeChecklistProducts(
  current: readonly ReviewChecklistProduct[],
  incoming: readonly ReviewChecklistProduct[],
): ReviewChecklistProduct[] {
  const merged = new Map(current.map((item) => [item.checklistId, item]));
  for (const item of incoming) merged.set(item.checklistId, item);
  return [...merged.values()];
}

export function cardAfterChecklistChange(card: CatalogListCardRecord): CatalogListCardRecord {
  const next = { ...card };
  delete next.checklistSuggestion;
  delete next.identityReviewDraft;
  if (card.identityStatus !== 'IDENTIFIED') delete next.checklistSourceLineage;
  return next;
}

interface WorkbenchChecklistResolution {
  candidates: ChecklistEntry[];
  parallelDefinitions: ChecklistParallelDefinition[];
  parallelMatch?: ChecklistParallelMatch;
}

export function structuredParallelSelectionComplete(
  definitions: readonly ChecklistParallelDefinition[],
  selection: string | null,
): boolean {
  return definitions.length === 0 || selection !== null;
}

export function legacyParallelSelectionComplete(
  condition: ReviewCondition,
  structuredSelectionRequired: boolean,
  pendingParallel: string,
  identityVariation: string | undefined,
): boolean {
  return (
    condition !== 'parallel-decision' ||
    structuredSelectionRequired ||
    pendingParallel.trim() !== '' ||
    (identityVariation?.trim() ?? '') !== ''
  );
}

function evidenceNumber(card: CatalogListCardRecord): string {
  if (card.identityReviewDraft?.importedFrom)
    return card.identityReviewDraft.identity.number.trim();
  const ml =
    card.suggestion?.identification.status === 'known'
      ? card.suggestion.identification.identity.number
      : undefined;
  return (
    card.checklistSuggestion?.number ??
    card.csvSuggestion?.number ??
    ebayTitleIdentityEvidence(card.marketplaceSource?.title).number ??
    card.ocrSuggestion?.number ??
    ml ??
    ''
  ).trim();
}

function evidenceSubset(card: CatalogListCardRecord): string {
  if (card.identityReviewDraft?.importedFrom)
    return card.identityReviewDraft.identity.subset?.trim() ?? '';
  const ml =
    card.suggestion?.identification.status === 'known'
      ? card.suggestion.identification.identity.subset
      : undefined;
  return (
    card.checklistSuggestion?.subset ??
    card.csvSuggestion?.subset ??
    card.ocrSuggestion?.subset ??
    ml ??
    ''
  ).trim();
}

export function workbenchState(card: CatalogListCardRecord): WorkbenchState {
  return classifyCardReviewState(card);
}

export function reviewCondition(
  card: CatalogListCardRecord,
  checklistConfigured: boolean,
  structuredParallelRequired = false,
  candidateCount = 0,
): ReviewCondition {
  if (!checklistConfigured) return 'needs-checklist';
  if (evidenceNumber(card) === '') return 'missing-number';
  const suggestion = card.checklistSuggestion;
  if (suggestion === undefined) return candidateCount > 0 ? 'parallel-decision' : 'no-match';
  if (structuredParallelRequired) return 'parallel-decision';
  return suggestion.variation?.trim() ? 'ready' : 'parallel-decision';
}

export function reviewSelectionAfterChecklistChange(
  card: CatalogListCardRecord,
  checklistId: string,
  checklist: ReviewChecklistProduct | undefined,
): CatalogListCardRecord['reviewChecklistSelection'] {
  if (card.reviewChecklistSelection?.checklistId === checklistId)
    return card.reviewChecklistSelection;
  if (checklistId === '' && !card.reviewChecklistSelection?.requiresReview) return undefined;
  return {
    checklistId,
    selectedAt: '',
    selectedBy: 'current-operator',
    ...(card.reviewChecklistSelection?.requiresReview
      ? {
          requiresReview: true,
          identityResolution: 'UNRESOLVED' as const,
          reason: 'checklist_item_not_found',
          ...(checklist === undefined
            ? {}
            : { product: checklist.product.product, category: checklist.category }),
        }
      : {}),
  };
}

export function effectiveCardChecklistId(
  card: CatalogListCardRecord,
  explicitChecklistId: string | undefined,
  batchChecklistId: string | null,
): string | null {
  if (card.identityReviewDraft?.importedFrom)
    return (
      explicitChecklistId ?? card.identityReviewDraft.checklistSourceSelection?.checklistId ?? null
    );
  if (card.reviewChecklistSelection?.requiresReview) {
    return (
      explicitChecklistId?.trim() ||
      card.reviewChecklistSelection.checklistId.trim() ||
      batchChecklistId ||
      null
    );
  }
  return (
    explicitChecklistId ??
    card.reviewChecklistSelection?.checklistId ??
    card.identityReviewDraft?.checklistSourceSelection?.checklistId ??
    (card.identityStatus === 'IDENTIFIED' && card.checklistSourceLineage?.status === 'VERIFIED'
      ? card.checklistSourceLineage.checklistId
      : undefined) ??
    batchChecklistId ??
    (card.checklistProductSuggestion?.model === CHECKLIST_PRODUCT_SUGGESTION_MODEL_ID
      ? card.checklistProductSuggestion.candidates[0]?.checklistId
      : undefined) ??
    ambiguousChecklistCandidates(card)[0]?.checklistId ??
    null
  );
}

export function ambiguousChecklistCandidates(
  card: CatalogListCardRecord,
): ChecklistContextSuggestionCandidate[] {
  return rankedChecklistCandidates(card);
}

export function rankedChecklistCandidates(
  card: CatalogListCardRecord,
): ChecklistContextSuggestionCandidate[] {
  const suggestionCandidates =
    card.checklistProductSuggestion?.model === CHECKLIST_PRODUCT_SUGGESTION_MODEL_ID
      ? card.checklistProductSuggestion.candidates
      : [];
  const failure = card.checklistProductSuggestionFailure;
  const failureCandidates =
    failure?.model !== CHECKLIST_PRODUCT_SUGGESTION_MODEL_ID ||
    (failure.status !== 'no-confident-match' && failure.status !== 'no-candidates')
      ? []
      : (failure.candidates ?? []);
  const seenChecklistIds = new Set<string>();
  return [...suggestionCandidates, ...failureCandidates]
    .filter((candidate) => {
      if (
        candidate.checklistId.trim() === '' ||
        candidate.confidence < 0 ||
        candidate.confidence > 1 ||
        seenChecklistIds.has(candidate.checklistId)
      ) {
        return false;
      }
      seenChecklistIds.add(candidate.checklistId);
      return true;
    })
    .slice(0, 10);
}

export function checklistCandidateStrength(confidence: number): 'Strong' | 'Possible' | 'Weak' {
  if (confidence >= 0.85) return 'Strong';
  if (confidence >= 0.7) return 'Possible';
  return 'Weak';
}

export function shouldAutomaticallySuggestChecklist(
  card: CatalogListCardRecord,
  explicitChecklistId: string | undefined,
  batchChecklistId: string | null,
): boolean {
  return (
    card.identityReviewDraft?.importedFrom === undefined &&
    explicitChecklistId === undefined &&
    card.reviewChecklistSelection === undefined &&
    batchChecklistId === null &&
    (card.checklistProductSuggestion?.model !== CHECKLIST_PRODUCT_SUGGESTION_MODEL_ID ||
      card.checklistProductSuggestion.candidates[0] === undefined) &&
    ambiguousChecklistCandidates(card).length === 0
  );
}

export const AUTOMATIC_OCR_SUGGESTION_MAX_ATTEMPTS = 3;
export const AUTOMATIC_OCR_SUGGESTION_RETRY_DELAY_MS = 1_500;
export const AUTOMATIC_OCR_BATCH_CATCHUP_LIMIT = 50;
export const AUTOMATIC_OCR_BATCH_CATCHUP_CONCURRENCY = 2;

export function shouldRetryAutomaticChecklistSuggestion(
  card: CatalogListCardRecord,
  completedAttempts: number,
): boolean {
  return (
    completedAttempts < AUTOMATIC_OCR_SUGGESTION_MAX_ATTEMPTS &&
    [card.marketplaceSource?.title, card.ocr?.front?.text, card.ocr?.back?.text].some(
      (text) => typeof text === 'string' && text.trim() !== '',
    ) &&
    card.checklistProductSuggestion?.candidates[0] === undefined &&
    ambiguousChecklistCandidates(card).length === 0
  );
}

export async function settleAutomaticChecklistCatchupRequest<T>(
  request: () => Promise<T>,
): Promise<T | undefined> {
  try {
    return await request();
  } catch {
    return undefined;
  }
}

export function cardsNeedingAutomaticChecklistCatchup(
  cards: readonly CatalogListCardRecord[],
  batchChecklistId: string | null,
  activeCardId: string | null,
): CatalogListCardRecord[] {
  if (batchChecklistId !== null) return [];
  return cards
    .filter(
      (card) =>
        card.cardId !== activeCardId &&
        shouldAutomaticallySuggestChecklist(card, undefined, batchChecklistId) &&
        [card.marketplaceSource?.title, card.ocr?.front?.text, card.ocr?.back?.text].some(
          (text) => typeof text === 'string' && text.trim() !== '',
        ),
    )
    .slice(0, AUTOMATIC_OCR_BATCH_CATCHUP_LIMIT);
}

function variationOf(entry: ChecklistEntry): string {
  return entry.identity.variation?.trim() || entry.key.cardSet?.trim() || '';
}

function identityForDisplay(card: CatalogListCardRecord): CardIdentity | undefined {
  if (card.identityReviewDraft !== undefined) return card.identityReviewDraft.identity;
  if (card.identityStatus === 'IDENTIFIED') return card.identity;
  return card.checklistSuggestion;
}

/** Seed the inline review form from the strongest available identity evidence. */
export function hasAssignedHierarchy(card: CatalogListCardRecord | null | undefined): boolean {
  return (
    card?.reviewChecklistSelection?.subset !== undefined ||
    card?.reviewChecklistSelection?.parallel !== undefined
  );
}

export function reviewAssignmentIdentity(
  card: CatalogListCardRecord,
  identity: CardIdentity,
): CardIdentity {
  if (
    card.identityReviewDraft !== undefined ||
    (card.identityStatus === 'IDENTIFIED' &&
      !card.reviewChecklistSelection?.requiresReview &&
      card.identityReviewDraft === undefined)
  )
    return identity;
  const assignment = card.reviewChecklistSelection;
  if (!assignment || !hasAssignedHierarchy(card)) return identity;
  const next = {
    ...identity,
    set: assignment.product ?? identity.set,
    subset: assignment.subset?.name,
  };
  if (assignment.parallel?.kind === 'DEFINITION') next.variation = assignment.parallel.name;
  else delete next.variation;
  return next;
}

function assignmentResolutionKey(card: CatalogListCardRecord, checklistId: string): string {
  const suffix = card.reviewChecklistSelection
    ? `|${JSON.stringify(card.reviewChecklistSelection)}`
    : '';
  return `${card.cardId}|${checklistId}${suffix}`;
}

export function parallelBelongsToAssignedSubset(
  definition: ChecklistParallelDefinition,
  subset: NonNullable<ReviewChecklistSelection['subset']>,
): boolean {
  return (
    !!subset.sourceFamilyId?.trim() &&
    !!definition.source.familyId?.trim() &&
    subset.sourceFamilyId.trim() === definition.source.familyId.trim() &&
    definition.applicability.subset?.trim() === subset.name.trim()
  );
}

export function sourceHonorsAssignment(
  assignment: ReviewChecklistSelection | undefined,
  source: ChecklistSourceSelection | null,
): boolean {
  if (!assignment) return true;
  if (!source || (assignment.checklistId && assignment.checklistId !== source.checklistId))
    return false;
  if (
    source.sourceDefinitionType === 'PARALLEL' &&
    source.parallel &&
    source.sourceSubsetId !== source.parallel.sourceSubsetId
  )
    return false;
  if (!assignment.subset && !assignment.parallel) return true;
  if (!['SUBSET', 'PARALLEL'].includes(source.sourceDefinitionType ?? '')) return false;
  if (
    assignment.subset &&
    (assignment.subset.name.trim() !== source.subset.trim() ||
      !assignment.subset.sourceFamilyId?.trim() ||
      !source.sourceFamilyId?.trim() ||
      assignment.subset.sourceFamilyId.trim() !== source.sourceFamilyId.trim() ||
      (source.sourceDefinitionType === 'SUBSET' &&
        source.sourceSubsetId !== assignment.subset.sourceSubsetId))
  )
    return false;
  if (assignment.parallel?.kind === 'NONE')
    return (
      source.parallel === undefined &&
      source.sourceDefinitionType === 'SUBSET' &&
      source.sourceSubsetId === assignment.subset?.sourceSubsetId
    );
  if (assignment.parallel?.kind === 'DEFINITION')
    return (
      source.parallel?.definitionId === assignment.parallel.definitionId &&
      source.parallel.sourceSubsetId === assignment.parallel.sourceSubsetId
    );
  return true;
}

export function identityDraftForCard(card: CatalogListCardRecord): CardIdentity {
  if (card.identityReviewDraft !== undefined) {
    return reviewAssignmentIdentity(card, {
      ...EMPTY_IDENTITY,
      ...card.identityReviewDraft.identity,
    });
  }
  if (card.identity !== undefined)
    return reviewAssignmentIdentity(card, { ...EMPTY_IDENTITY, ...card.identity });
  const merged: Record<string, unknown> = { ...EMPTY_IDENTITY };
  const ml =
    card.suggestion?.identification.status === 'known'
      ? card.suggestion.identification.identity
      : undefined;
  const marketplaceTitle = ebayTitleIdentityEvidence(card.marketplaceSource?.title);
  for (const evidence of [
    ml,
    card.ocrSuggestion,
    marketplaceTitle,
    card.csvSuggestion,
    card.checklistSuggestion,
  ]) {
    for (const [key, value] of Object.entries(evidence ?? {})) {
      if (value === undefined || (typeof value === 'string' && value.trim() === '')) continue;
      merged[key] = value;
    }
  }
  return reviewAssignmentIdentity(card, merged as unknown as CardIdentity);
}

export function cardIdentitiesEqual(left: CardIdentity | undefined, right: CardIdentity): boolean {
  if (left === undefined) return false;
  const keys = new Set([...Object.keys(left), ...Object.keys(right)] as (keyof CardIdentity)[]);
  return [...keys].every((key) => {
    const normalize = (value: CardIdentity[typeof key] | undefined) =>
      typeof value === 'string' ? value.trim() : value;
    const value = (identity: CardIdentity) =>
      key === 'language' ? (identity.language ?? EMPTY_IDENTITY.language) : identity[key];
    return normalize(value(left)) === normalize(value(right));
  });
}

/** Presentation defaults are compared locally; retention sends the exact saved identity. */
export function canRetainVerifiedIdentity(
  card: CatalogListCardRecord | null,
  draft: CardIdentity | null,
  checklistId: string | null,
  selection: ChecklistSourceSelection | null,
  manualOverrideEnabled: boolean,
  manualOverrideReason: string,
): boolean {
  if (
    card?.identityStatus !== 'IDENTIFIED' ||
    card.reviewChecklistSelection?.requiresReview === true ||
    draft === null ||
    !cardIdentitiesEqual(card.identity, draft)
  )
    return false;
  if (manualOverrideEnabled) {
    return (
      card.manualIdentityOverride?.status === 'VERIFIED' &&
      card.manualIdentityOverride.reason === manualOverrideReason.trim()
    );
  }
  const lineage = card.checklistSourceLineage;
  if (lineage === undefined)
    return (
      checklistId === null &&
      selection === null &&
      card.identityReviewDraft?.checklistSourceSelection == null
    );
  if (lineage.status !== 'VERIFIED' || lineage.checklistId !== checklistId) return false;
  const selectedSource = selection ?? card.identityReviewDraft?.checklistSourceSelection;
  return (
    selectedSource == null ||
    (selectedSource.sourceSystem === lineage.sourceSystem &&
      selectedSource.sourceTargetId === lineage.sourceTargetId &&
      selectedSource.sourceItemId === lineage.sourceItemId &&
      selectedSource.parallel?.definitionId === lineage.parallel?.definitionId &&
      selectedSource.parallel?.sourceSubsetId === lineage.parallel?.sourceSubsetId)
  );
}

export function reviewSaveFailureMessage(message: string): string {
  if (message === 'checklist_assignment_changed')
    return 'This card’s assigned checklist changed. Reload the card and confirm its identity again.';
  if (message === 'checklist_source_selection_mismatch' || message === 'checklist_not_found')
    return 'This saved checklist selection is no longer available in the current catalog. Your review content is preserved. Choose a current checklist row before verifying a new identity.';
  if (
    message === 'unresolved_classification_requires_manual_completion' ||
    message === 'manual_classification_confirmation_required'
  )
    return 'This source classification needs manual review. Replace unresolved labels with values you can establish and explicitly confirm completion, or save a draft.';
  if (message === 'invalid_manual_source_evidence')
    return 'The unresolved source evidence changed or is unavailable. Reload the checklist before completing this review.';
  if (message === 'retained_identity_changed')
    return 'The saved card changed while you were reviewing it. Reload the card before saving; your edits have not been applied.';
  return message;
}

export function persistedStructuredChecklistChoice(
  card: CatalogListCardRecord,
  candidates: readonly ChecklistEntry[],
  definitions: readonly ChecklistParallelDefinition[],
): { candidateIndex: string; parallelChoice: string } | null {
  if (candidates.length === 0) return null;
  if (card.identityReviewDraft?.importedFrom && !card.identityReviewDraft.checklistSourceSelection)
    return null;
  const identity = identityDraftForCard(card);
  const lineage =
    card.identityReviewDraft !== undefined
      ? card.identityReviewDraft.checklistSourceSelection
      : !card.reviewChecklistSelection?.requiresReview &&
          card.checklistSourceLineage &&
          ['DRAFT', 'VERIFIED'].includes(card.checklistSourceLineage.status)
        ? card.checklistSourceLineage
        : undefined;
  if (!lineage) return null;
  const normalize = (value: string | undefined) => (value ?? '').trim();
  const subsetChoices = checklistSubsetChoices(candidates);
  const sourceMatches = candidates
    .map((candidate, index) => ({ candidate, index }))
    .filter(
      ({ candidate }) =>
        lineage !== undefined &&
        candidate.source?.sourceSystem === lineage.sourceSystem &&
        candidate.source.sourceTargetId === lineage.sourceTargetId &&
        candidate.source.sourceItemId === lineage.sourceItemId &&
        candidate.source.sourceSubsetId === lineage.sourceSubsetId &&
        candidate.source.sourceDefinitionType === lineage.sourceDefinitionType &&
        !!candidate.source.sourceFamilyId &&
        candidate.source.sourceFamilyId === lineage.sourceFamilyId &&
        candidate.source.snapshotFingerprint === lineage.snapshotFingerprint &&
        samePhysicalChecklistSelection(
          card,
          checklistSelectionSource(
            lineage.checklistId,
            candidate,
            definitions.find(
              (definition) => definition.definitionId === lineage.parallel?.definitionId,
            ),
          )!,
        ),
    );
  const sourceMatch = sourceMatches.length === 1 ? sourceMatches[0] : undefined;
  if (
    lineage !== undefined &&
    sourceMatch === undefined &&
    (card.identityStatus === 'IDENTIFIED' || card.identityReviewDraft !== undefined)
  )
    return null;
  const sourceChoice =
    sourceMatch === undefined
      ? undefined
      : checklistSubsetChoiceForEntry(candidates, sourceMatch.candidate);
  const identityMatches = subsetChoices
    .map(({ entry: candidate, index }) => ({ candidate, index }))
    .filter(
      ({ candidate }) =>
        normalize(candidate.identity.number || candidate.key.number) ===
          normalize(identity.number) &&
        normalize(checklistRosterSubset(candidate)) === normalize(identity.subset) &&
        normalize(candidate.identity.name) === normalize(identity.name),
    );
  const matches =
    sourceChoice === undefined
      ? identityMatches
      : [{ candidate: sourceChoice.entry, index: sourceChoice.index }];
  if (matches.length !== 1) return null;

  const selected = matches[0];
  if (selected === undefined || selected.candidate.unresolvedClassification !== undefined)
    return null;
  const applicableDefinitions = applicableParallelDefinitions(selected.candidate, definitions);
  const lineageDefinitionId = lineage?.parallel?.definitionId;
  if (
    lineageDefinitionId !== undefined &&
    applicableDefinitions.some((definition) => definition.definitionId === lineageDefinitionId)
  ) {
    return { candidateIndex: String(selected.index), parallelChoice: lineageDefinitionId };
  }

  const variation = normalize(identity.variation);
  const parallelMatches = applicableDefinitions.filter(
    (definition) => normalize(definition.name) === variation,
  );
  if (variation !== '' && parallelMatches.length === 1) {
    return {
      candidateIndex: String(selected.index),
      parallelChoice: parallelMatches[0]!.definitionId,
    };
  }
  if (checklistPlainChoiceAllowed(selected.candidate) && variation === '') {
    return { candidateIndex: String(selected.index), parallelChoice: NO_PARALLEL_DEFINITION_ID };
  }
  return null;
}

export function identityDraftIsSubmittable(identity: CardIdentity): boolean {
  return [identity.category, identity.set, identity.name].every((value) => value.trim() !== '');
}

export function showIdentityDraftValidation(
  identityDraftEdited: boolean,
  identity: CardIdentity,
): boolean {
  return identityDraftEdited && !identityDraftIsSubmittable(identity);
}

export function identityDraftForParallel(entry: ChecklistEntry): CardIdentity {
  return { ...EMPTY_IDENTITY, ...entry.identity };
}

export function identityDraftForChecklist(
  current: CardIdentity,
  checklist: ReviewChecklistProduct,
): CardIdentity {
  const next = {
    ...current,
    category: checklist.category,
    set: checklist.product.product,
    year: checklist.product.year,
    brand: checklist.product.brand,
    ...(checklist.language === undefined ? {} : { language: checklist.language }),
  };
  delete next.subset;
  delete next.variation;
  delete next.serialTo;
  delete next.serialNumber;
  return next;
}

export function mergeChecklistChoiceIntoDraft(
  _current: CardIdentity,
  selected: CardIdentity,
): CardIdentity {
  // Source composition is a full proposal: absent corrected fields must not resurrect old values.
  return { ...selected };
}
export function identityDraftFromChecklistCandidates(
  _entries: readonly ChecklistEntry[],
  fallback: CardIdentity,
): CardIdentity {
  // Resolver advice never accepts a new identity or removes physical facts automatically.
  return fallback;
}

export function identityDraftFromOcrResolution(
  card: CatalogListCardRecord,
  _checklist: ReviewChecklistProduct,
  _entries: readonly ChecklistEntry[],
  _evidenceNumber: string | undefined,
): CardIdentity {
  // Metadata/assistance can offer choices; it cannot accept a new identity or remove physical facts.
  return identityDraftForCard(card);
}

function normalizedReviewerText(value: string | undefined): string {
  return (value ?? '')
    .normalize('NFKD')
    .toLocaleLowerCase('en-US')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

export function preferredNumberLookupCandidate(
  entries: readonly ChecklistEntry[],
  reviewerName: string | undefined,
  rawOcrText = '',
): number | null {
  if (entries.length === 1) return 0;
  const ocr = normalizedReviewerText(rawOcrText);
  if (ocr !== '') {
    const ocrMatches = entries
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry }) => {
        const candidateName = normalizedReviewerText(entry.identity.name);
        return candidateName !== '' && ` ${ocr} `.includes(` ${candidateName} `);
      });
    if (ocrMatches.length === 1) return ocrMatches[0]?.index ?? null;
  }
  const name = normalizedReviewerText(reviewerName);
  if (name === '') return null;
  const matches = entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => normalizedReviewerText(entry.identity.name) === name);
  return matches.length === 1 ? (matches[0]?.index ?? null) : null;
}

export function defaultParallelChoiceForSubset(
  subset: string | undefined,
  entry?: ChecklistEntry,
): string | null {
  if (!entry || (subset ?? '').trim() !== checklistRosterSubset(entry)) return null;
  const policy = checklistEntryPolicy(entry);
  return policy?.rowTreatmentStatus === 'INTRINSIC'
    ? policy.intrinsicTreatment!.definitionId
    : checklistPlainChoiceAllowed(entry)
      ? NO_PARALLEL_DEFINITION_ID
      : null;
}

export function listingTitleForReview(
  template: MarketplaceListingTemplate,
  identity: CardIdentity,
  card: Pick<CatalogListCardRecord, 'sku' | 'shortCode'>,
  cardPresentation?: CardPresentation,
): string {
  const title = renderListingTemplateTitle(
    template.titleBlocks,
    listingTemplateValuesForCard(identity, {
      ...(card.sku === undefined ? {} : { sku: card.sku }),
      ...(card.shortCode === undefined ? {} : { shortCode: card.shortCode }),
      ...(cardPresentation === undefined ? {} : { cardPresentation }),
    }),
  );
  return listingTitleWithTeamFallback(title, identity.team);
}

function normalizedTitlePart(value: string): string {
  return value
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function listingTitleWithTeamFallback(
  title: string,
  team: string | undefined,
  limit = EBAY_LISTING_BASE_TITLE_MAX_LENGTH,
): string {
  const fullTeam = team?.trim();
  if (fullTeam === undefined || fullTeam === '') return title;
  const normalizedTitle = ` ${normalizedTitlePart(title)} `;
  const normalizedTeam = normalizedTitlePart(fullTeam);
  const shortTeam = fullTeam.split(/\s+/).at(-1) ?? fullTeam;
  const normalizedShortTeam = normalizedTitlePart(shortTeam);
  if (normalizedTeam !== '' && normalizedTitle.includes(` ${normalizedTeam} `)) {
    if (title.length <= limit || shortTeam === fullTeam) return title;
    const escapedTeam = fullTeam.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const shortened = title.replace(new RegExp(escapedTeam, 'i'), shortTeam);
    return shortened.length <= limit ? shortened : title;
  }
  if (
    shortTeam !== fullTeam &&
    normalizedShortTeam !== '' &&
    normalizedTitle.includes(` ${normalizedShortTeam} `)
  ) {
    return title;
  }
  const withFullTeam = `${title} ${fullTeam}`.trim();
  if (withFullTeam.length <= limit) return withFullTeam;
  if (shortTeam !== fullTeam) {
    const withShortTeam = `${title} ${shortTeam}`.trim();
    if (withShortTeam.length <= limit) return withShortTeam;
  }
  return title;
}

export function listingTemplateAvailabilityMessage(
  assignedTemplateCount: number,
  activeTemplateCount: number,
): string | null {
  if (activeTemplateCount > 0) return null;
  return assignedTemplateCount > 0
    ? 'This checklist has assigned templates, but none are Active.'
    : 'No listing template is assigned to this checklist.';
}

export function listingTemplateIdAfterRefresh(
  currentTemplateId: string,
  templates: readonly MarketplaceListingTemplate[],
): string {
  return templates.some((template) => template.templateId === currentTemplateId)
    ? currentTemplateId
    : (templates[0]?.templateId ?? '');
}

export function listingDescriptionForTemplate(
  savedDescription: string | undefined,
  template: MarketplaceListingTemplate | undefined,
  identity?: CardIdentity,
  card: Pick<CatalogListCardRecord, 'sku' | 'shortCode'> = {},
  cardPresentation?: CardPresentation,
): string {
  if (savedDescription?.trim()) return savedDescription;
  if (template === undefined) return '';
  if ((template.descriptionBlocks ?? []).length > 0 && identity !== undefined) {
    return renderListingTemplateDescription(
      template.descriptionBlocks,
      listingTemplateValuesForCard(identity, {
        ...(card.sku === undefined ? {} : { sku: card.sku }),
        ...(card.shortCode === undefined ? {} : { shortCode: card.shortCode }),
        ...(cardPresentation === undefined ? {} : { cardPresentation }),
      }),
    );
  }
  return template.defaultDescription;
}

export function presentationDraftForCard(card: CatalogListCardRecord): CardPresentation {
  return (
    card.identityReviewDraft?.cardPresentation ??
    card.cardPresentation ?? { kind: 'RAW', condition: 'NEAR_MINT_OR_BETTER' }
  );
}

export function itemSpecificsDraftForReview(
  saved: EbayItemSpecifics | undefined,
  identity: CardIdentity,
  category: EbaySellerHubSinglesCategory | null,
): EbayItemSpecifics {
  const draft = { ...(saved ?? {}) };
  if (
    category !== null &&
    category !== 'CCG_SINGLES' &&
    !Object.prototype.hasOwnProperty.call(draft, 'Features')
  ) {
    draft.Features = suggestedEbayFeatures(identity, category);
  }
  if (!Object.prototype.hasOwnProperty.call(draft, 'Language') && identity.language?.trim()) {
    draft.Language = [identity.language.trim()];
  }
  return draft;
}

export function sellerHubCategoryForChecklist(
  checklist: ReviewChecklistProduct | undefined,
): EbaySellerHubSinglesCategory | null {
  return listingCategoryForLabel(checklist?.category);
}
function sellerHubCategoryForIdentity(identity: CardIdentity): EbaySellerHubSinglesCategory | null {
  return listingCategoryForLabel(identity.category);
}

function savedSellerHubCategory(
  saved: { itemSpecificsCategory?: EbaySellerHubSinglesCategory } | null,
  fallback: EbaySellerHubSinglesCategory | null,
): EbaySellerHubSinglesCategory | null {
  if (saved && Object.hasOwn(saved, 'itemSpecificsCategory'))
    return ['SPORT_SINGLES', 'NON_SPORT_SINGLES', 'CCG_SINGLES'].includes(
      String(saved.itemSpecificsCategory),
    )
      ? saved.itemSpecificsCategory!
      : null;
  return fallback;
}

export function isListingTemplateNotAssignedFailure(result: {
  error: string;
  message?: string;
}): boolean {
  return (
    result.error === 'listing_template_not_assigned' ||
    result.message === 'listing_template_not_assigned'
  );
}

function parallelLabel(entry: ChecklistEntry): string {
  const variation = variationOf(entry) || 'Unspecified';
  return entry.identity.serialTo === undefined
    ? variation
    : `${variation} · /${entry.identity.serialTo}`;
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

interface FloatingInspectorPosition {
  left: number;
  top: number;
}

interface MagnifierLensGeometry {
  left: number;
  top: number;
  diameter: number;
  backgroundSize: string;
  backgroundPosition: string;
}

const APP_HEADER_HEIGHT = 64;
const FLOATING_INSPECTOR_GUTTER = 12;
const FLOATING_INSPECTOR_DEFAULT_TOP = APP_HEADER_HEIGHT + FLOATING_INSPECTOR_GUTTER;
const MAGNIFIER_DIAMETER = 160;
const MAGNIFIER_ZOOM = 2;

export function clampFloatingInspectorPosition(
  position: FloatingInspectorPosition,
  panel: { width: number; height: number },
  viewport: { width: number; height: number; topInset?: number },
): FloatingInspectorPosition {
  const minimumTop = Math.max(0, viewport.topInset ?? 0);
  return {
    left: Math.min(Math.max(0, position.left), Math.max(0, viewport.width - panel.width)),
    top: Math.min(
      Math.max(minimumTop, position.top),
      Math.max(minimumTop, viewport.height - panel.height),
    ),
  };
}

function floatingInspectorViewport(): {
  width: number;
  height: number;
  topInset: number;
} {
  const headerBottom =
    document.querySelector<HTMLElement>('.tcvs-topbar')?.getBoundingClientRect().bottom ??
    APP_HEADER_HEIGHT;
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    topInset: Math.ceil(headerBottom) + FLOATING_INSPECTOR_GUTTER,
  };
}

export function magnifierLensGeometry(
  pointer: { x: number; y: number },
  frame: { width: number; height: number },
  lensDiameter = MAGNIFIER_DIAMETER,
  zoom = MAGNIFIER_ZOOM,
): MagnifierLensGeometry {
  const x = Math.min(Math.max(0, pointer.x), frame.width);
  const y = Math.min(Math.max(0, pointer.y), frame.height);
  const diameter = Math.max(0, Math.min(lensDiameter, frame.width, frame.height));
  const left = Math.min(Math.max(0, x - diameter / 2), Math.max(0, frame.width - diameter));
  const top = Math.min(Math.max(0, y - diameter / 2), Math.max(0, frame.height - diameter));
  const safeZoom = Math.max(1, zoom);
  return {
    left,
    top,
    diameter,
    backgroundSize: `${frame.width * safeZoom}px ${frame.height * safeZoom}px`,
    backgroundPosition: `${x - left - x * safeZoom}px ${y - top - y * safeZoom}px`,
  };
}

export function MagnifiableCardImage({
  src,
  alt,
  imageClassName = 'tcvs-image-approval-image-fullscreen',
  rotationDegrees = 0,
  alignmentGrid = false,
  onImageLoadChange,
}: {
  src: string;
  alt: string;
  imageClassName?: string;
  rotationDegrees?: number;
  alignmentGrid?: boolean;
  onImageLoadChange?: (loaded: boolean) => void;
}): JSX.Element {
  const imageLoadCallback = useRef(onImageLoadChange);
  imageLoadCallback.current = onImageLoadChange;
  const lensRef = useRef<HTMLSpanElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageFit, setImageFit] = useState({
    src: '',
    width: 0,
    height: 0,
    frameWidth: 0,
    frameHeight: 0,
  });
  const measureImage = () => {
    const image = imageRef.current;
    if (image === null) return;
    setImageFit({
      src,
      width: image.naturalWidth,
      height: image.naturalHeight,
      frameWidth: image.clientWidth,
      frameHeight: image.clientHeight,
    });
  };
  useEffect(() => {
    const image = imageRef.current;
    if (image === null) return;
    const measure = () =>
      setImageFit({
        src,
        width: image.naturalWidth,
        height: image.naturalHeight,
        frameWidth: image.clientWidth,
        frameHeight: image.clientHeight,
      });
    measure();
    imageLoadCallback.current?.(
      image.complete && image.naturalWidth > 0 && image.naturalHeight > 0,
    );
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(image);
    return () => observer.disconnect();
  }, [src]);
  const adjustingRotation = rotationDegrees !== 0;
  const quarterTurnScale =
    imageFit.src === src
      ? reviewImageQuarterTurnScale(
          imageFit.width,
          imageFit.height,
          imageFit.frameWidth,
          imageFit.frameHeight,
          rotationDegrees,
        )
      : 1;

  const hideLens = () => {
    if (lensRef.current !== null) lensRef.current.hidden = true;
  };

  const moveLens = (event: ReactPointerEvent<HTMLDivElement>) => {
    const lens = lensRef.current;
    if (lens === null || event.pointerType !== 'mouse' || adjustingRotation) {
      hideLens();
      return;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    const geometry = magnifierLensGeometry(
      { x: event.clientX - bounds.left, y: event.clientY - bounds.top },
      { width: bounds.width, height: bounds.height },
    );
    lens.style.left = `${geometry.left}px`;
    lens.style.top = `${geometry.top}px`;
    lens.style.width = `${geometry.diameter}px`;
    lens.style.height = `${geometry.diameter}px`;
    lens.style.backgroundSize = geometry.backgroundSize;
    lens.style.backgroundPosition = geometry.backgroundPosition;
    lens.hidden = false;
  };

  return (
    <div
      className="tcvs-workbench-image-magnifier"
      onPointerEnter={moveLens}
      onPointerMove={moveLens}
      onPointerLeave={hideLens}
    >
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        className={imageClassName}
        style={{
          transform: `rotate(${rotationDegrees}deg) scale(${quarterTurnScale})`,
        }}
        onLoad={(event) => {
          measureImage();
          imageLoadCallback.current?.(
            event.currentTarget.naturalWidth > 0 && event.currentTarget.naturalHeight > 0,
          );
        }}
        onError={() => imageLoadCallback.current?.(false)}
      />
      {alignmentGrid ? (
        <span className="tcvs-review-image-alignment-grid" aria-hidden="true" />
      ) : null}
      <span
        ref={lensRef}
        className="tcvs-workbench-image-magnifier-lens"
        style={{ backgroundImage: `url(${JSON.stringify(src)})` }}
        aria-hidden="true"
        hidden
      />
    </div>
  );
}

export function formatRotationDegrees(degrees: number): string {
  const normalized = Math.abs(degrees) < REVIEW_IMAGE_ROTATION_STEP / 2 ? 0 : degrees;
  return `${normalized > 0 ? '+' : ''}${normalized.toFixed(2).replace(/\.?0+$/, '')}°`;
}

export type ReviewImageSide = 'front' | 'back';
export type ReviewImageRotations = Record<ReviewImageSide, number>;

export const EMPTY_REVIEW_IMAGE_ROTATIONS: ReviewImageRotations = { front: 0, back: 0 };

export function ReviewImageRotationEditor({
  side,
  rotationDegrees,
  alignmentGrid,
  busySide,
  pairReset,
  onRotationChange,
  children,
}: {
  side: ReviewImageSide;
  rotationDegrees: number;
  alignmentGrid: boolean;
  busySide: ReviewImageSide | null;
  pairReset?: {
    disabled: boolean;
    onReset: () => void;
  };
  onRotationChange: (side: ReviewImageSide, degrees: number) => void;
  children: (rotationDegrees: number, alignmentGrid: boolean) => JSX.Element;
}): JSX.Element {
  const { quarterTurnDegrees, fineDegrees } = reviewImageRotationParts(rotationDegrees);
  const setFineRotation = (degrees: number) => {
    onRotationChange(
      side,
      normalizeReviewImageRotation(quarterTurnDegrees + clampReviewImageRotation(degrees)),
    );
  };
  const rotateQuarterTurn = (degrees: number) => {
    onRotationChange(side, normalizeReviewImageRotation(rotationDegrees + degrees));
  };

  return (
    <div className="tcvs-review-image-editor">
      {children(rotationDegrees, alignmentGrid)}
      <div className="tcvs-review-image-rotation">
        <div className="tcvs-review-image-rotation-controls">
          <button
            type="button"
            className="tcvs-icon-button tcvs-review-image-rotation-nudge"
            aria-label={`Rotate ${side} image ${REVIEW_IMAGE_ROTATION_NUDGE} degrees counterclockwise`}
            title={`Rotate ${REVIEW_IMAGE_ROTATION_NUDGE}° counterclockwise`}
            disabled={busySide !== null}
            onClick={() => setFineRotation(fineDegrees - REVIEW_IMAGE_ROTATION_NUDGE)}
          >
            <span aria-hidden="true">−</span>
          </button>
          <input
            type="range"
            min={REVIEW_IMAGE_ROTATION_MIN}
            max={REVIEW_IMAGE_ROTATION_MAX}
            step={REVIEW_IMAGE_ROTATION_STEP}
            value={fineDegrees}
            disabled={busySide !== null}
            aria-label={`Adjust ${side} image rotation`}
            onChange={(event) => setFineRotation(Number(event.currentTarget.value))}
          />
          <button
            type="button"
            className="tcvs-icon-button tcvs-review-image-rotation-nudge"
            aria-label={`Rotate ${side} image ${REVIEW_IMAGE_ROTATION_NUDGE} degrees clockwise`}
            title={`Rotate ${REVIEW_IMAGE_ROTATION_NUDGE}° clockwise`}
            disabled={busySide !== null}
            onClick={() => setFineRotation(fineDegrees + REVIEW_IMAGE_ROTATION_NUDGE)}
          >
            <span aria-hidden="true">+</span>
          </button>
        </div>
        <div className="tcvs-review-image-rotation-actions">
          <output aria-live="polite">{formatRotationDegrees(rotationDegrees)}</output>
          <div className="tcvs-review-image-quarter-turns" aria-label={`${side} image orientation`}>
            <button
              type="button"
              className="tcvs-icon-button tcvs-review-image-quarter-turn"
              aria-label={`Rotate ${side} image 90 degrees counterclockwise`}
              title="Rotate 90° counterclockwise"
              disabled={busySide !== null}
              onClick={() => rotateQuarterTurn(-REVIEW_IMAGE_QUARTER_TURN)}
            >
              <span aria-hidden="true">↶</span>
            </button>
            <button
              type="button"
              className="tcvs-icon-button tcvs-review-image-quarter-turn"
              aria-label={`Rotate ${side} image 90 degrees clockwise`}
              title="Rotate 90° clockwise"
              disabled={busySide !== null}
              onClick={() => rotateQuarterTurn(REVIEW_IMAGE_QUARTER_TURN)}
            >
              <span aria-hidden="true">↷</span>
            </button>
          </div>
          {pairReset === undefined ? null : (
            <button
              type="button"
              className="tcvs-linklike tcvs-review-image-reset"
              aria-label="Reset front and back image rotations"
              disabled={busySide !== null || pairReset.disabled}
              onClick={pairReset.onReset}
            >
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function ReviewImageGridAction({
  alignmentGrid,
  busy,
  onAlignmentGridChange,
}: {
  alignmentGrid: boolean;
  busy: boolean;
  onAlignmentGridChange: (enabled: boolean) => void;
}): JSX.Element {
  return (
    <div className="tcvs-review-image-grid-action">
      <label>
        <input
          type="checkbox"
          checked={alignmentGrid}
          disabled={busy}
          onChange={(event) => onAlignmentGridChange(event.currentTarget.checked)}
        />
        <span>Grid</span>
      </label>
    </div>
  );
}

function WorkbenchImageReview({
  card,
  imageIssue,
  imageIssueSaving,
  imageAdjustmentBusySide,
  imageRotations,
  imageAlignmentGrid,
  onImageIssueChange,
  onImageRotationChange,
  onImageAlignmentGridChange,
  onImageRotationsReset,
  onClose,
}: {
  card: CatalogListCardRecord;
  imageIssue: boolean;
  imageIssueSaving: boolean;
  imageAdjustmentBusySide: ReviewImageSide | null;
  imageRotations: ReviewImageRotations;
  imageAlignmentGrid: boolean;
  onImageIssueChange: (checked: boolean) => void;
  onImageRotationChange: (side: ReviewImageSide, degrees: number) => void;
  onImageAlignmentGridChange: (enabled: boolean) => void;
  onImageRotationsReset: () => void;
  onClose: () => void;
}): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  const dragOffsetRef = useRef<FloatingInspectorPosition | null>(null);
  const [position, setPosition] = useState<FloatingInspectorPosition>({
    left: FLOATING_INSPECTOR_GUTTER,
    top: FLOATING_INSPECTOR_DEFAULT_TOP,
  });
  useEffect(() => {
    const keepInsideViewport = () => {
      const panel = panelRef.current;
      if (panel === null) return;
      const bounds = panel.getBoundingClientRect();
      setPosition((current) =>
        clampFloatingInspectorPosition(
          current,
          { width: bounds.width, height: bounds.height },
          floatingInspectorViewport(),
        ),
      );
    };
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(keepInsideViewport);
    if (panelRef.current !== null) observer?.observe(panelRef.current);
    window.addEventListener('resize', keepInsideViewport);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', keepInsideViewport);
    };
  }, []);

  const startDrag = (event: ReactPointerEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest('button, input, label') !== null) return;
    const panel = panelRef.current;
    if (panel === null) return;
    const bounds = panel.getBoundingClientRect();
    dragOffsetRef.current = {
      left: event.clientX - bounds.left,
      top: event.clientY - bounds.top,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const drag = (event: ReactPointerEvent<HTMLElement>) => {
    const offset = dragOffsetRef.current;
    const panel = panelRef.current;
    if (offset === null || panel === null) return;
    const bounds = panel.getBoundingClientRect();
    setPosition(
      clampFloatingInspectorPosition(
        {
          left: event.clientX - offset.left,
          top: event.clientY - offset.top,
        },
        { width: bounds.width, height: bounds.height },
        floatingInspectorViewport(),
      ),
    );
  };

  const stopDrag = (event: ReactPointerEvent<HTMLElement>) => {
    dragOffsetRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div
      ref={panelRef}
      className="tcvs-image-approval-popout tcvs-workbench-image-float"
      role="dialog"
      aria-modal="false"
      aria-label="Card image review"
      style={{ left: position.left, top: position.top }}
    >
      <header
        className="tcvs-image-approval-popout-header tcvs-workbench-image-float-handle"
        onPointerDown={startDrag}
        onPointerMove={drag}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
      >
        <div>
          <h3>Card image review</h3>
          <span className="tcvs-mono tcvs-muted">{card.shortCode ?? card.cardId.slice(0, 8)}</span>
        </div>
        <button
          type="button"
          className="tcvs-icon-button tcvs-image-approval-popout-close"
          aria-label="Close card image pop-out"
          title="Close"
          onClick={onClose}
        >
          <span aria-hidden="true">X</span>
        </button>
      </header>
      <main className="tcvs-image-approval-popout-main">
        <div className="tcvs-workbench-image-batch-tools">
          <ReviewImageGridAction
            alignmentGrid={imageAlignmentGrid}
            busy={imageAdjustmentBusySide !== null}
            onAlignmentGridChange={onImageAlignmentGridChange}
          />
          <label className="tcvs-image-issue-toggle tcvs-review-image-issue-leading">
            <input
              type="checkbox"
              checked={imageIssue}
              disabled={imageIssueSaving || imageAdjustmentBusySide !== null}
              onChange={(event) => onImageIssueChange(event.target.checked)}
            />
            <span>{imageIssueSaving ? 'Saving image issue…' : 'Image issue'}</span>
          </label>
        </div>
        <div className="tcvs-image-approval-grid-fullscreen tcvs-image-approval-grid-vertical">
          {(['front', 'back'] as const).map((side) => {
            const url = card.imageUrls?.[side];
            return url === undefined ? null : (
              <section key={side} className="tcvs-image-approval-side-fullscreen">
                <strong>{side === 'front' ? 'Front' : 'Back'}</strong>
                <ReviewImageRotationEditor
                  side={side}
                  rotationDegrees={imageRotations[side]}
                  alignmentGrid={imageAlignmentGrid}
                  busySide={imageAdjustmentBusySide}
                  pairReset={
                    side === 'back'
                      ? {
                          disabled: imageRotations.front === 0 && imageRotations.back === 0,
                          onReset: onImageRotationsReset,
                        }
                      : undefined
                  }
                  onRotationChange={onImageRotationChange}
                >
                  {(rotationDegrees, alignmentGrid) => (
                    <MagnifiableCardImage
                      src={url}
                      alt={`${side} card full screen`}
                      rotationDegrees={rotationDegrees}
                      alignmentGrid={alignmentGrid}
                    />
                  )}
                </ReviewImageRotationEditor>
              </section>
            );
          })}
        </div>
      </main>
    </div>
  );
}

const COMPARABLE_STATE_LABELS: Record<MarketplaceComparableItem['state'], string> = {
  NON_LISTED: 'Not listed',
  LISTED: 'Listed',
  SOLD: 'Sold',
};

function comparablePrice(item: MarketplaceComparableItem): string {
  if (item.amountMinor === undefined) return 'No reviewed price';
  return comparableAmount(item.amountMinor, item.currency);
}

function comparableAmount(
  amountMinor: number,
  currency: MarketplaceComparableItem['currency'],
): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amountMinor / 100);
}

function comparableBatchLabel(batchId: string): string {
  return batchId.replace(/^tcvs:batch:/, '').slice(-6);
}

function comparableProviderLabel(item: MarketplaceComparableItem): string {
  if (item.providerStatus === 'ACTIVE') return 'eBay listing active';
  if (item.providerStatus === 'MISSING') return 'eBay listing not found in latest sync';
  if (item.providerStatus === 'ENDED') return 'eBay listing ended';
  if (item.providerStatus === 'SOLD') return 'eBay listing sold';
  if (item.priceSource === 'TCVS_REVIEWED') return 'TCVS reviewed';
  if (item.priceSource === 'EBAY_OBSERVED') return 'eBay listing status unavailable';
  return 'Identification complete';
}

function comparableEbayListingUrl(externalListingId: string | undefined): string | undefined {
  return externalListingId !== undefined && /^\d{9,15}$/.test(externalListingId)
    ? `https://www.ebay.com/itm/${externalListingId}`
    : undefined;
}

function comparableSortAmount(item: MarketplaceComparableItem): number | undefined {
  if (item.state === 'SOLD') {
    return (
      item.soldAmountMinor ?? (item.priceSource === 'EBAY_SOLD' ? item.amountMinor : undefined)
    );
  }
  if (item.state === 'LISTED') return item.listedAmountMinor ?? item.amountMinor;
  return item.amountMinor;
}

function compareOptionalAmounts(
  leftAmount: number | undefined,
  rightAmount: number | undefined,
  leftId: string,
  rightId: string,
): number {
  if (leftAmount === undefined && rightAmount !== undefined) return 1;
  if (leftAmount !== undefined && rightAmount === undefined) return -1;
  if (leftAmount !== rightAmount) return (leftAmount ?? 0) - (rightAmount ?? 0);
  return leftId.localeCompare(rightId);
}

export function sortMarketplaceComparableItems(
  items: MarketplaceComparableItem[],
): MarketplaceComparableItem[] {
  return [...items].sort((left, right) =>
    compareOptionalAmounts(
      comparableSortAmount(left),
      comparableSortAmount(right),
      left.comparableId,
      right.comparableId,
    ),
  );
}

export function sortEbayActiveComparableItems(
  items: EbayActiveComparableItem[],
): EbayActiveComparableItem[] {
  return [...items].sort((left, right) =>
    compareOptionalAmounts(left.amountMinor, right.amountMinor, left.itemId, right.itemId),
  );
}

function ComparableEmptyThumbnail({ name }: { name: string }): JSX.Element {
  return (
    <span
      className="tcvs-comparable-thumb tcvs-comparable-thumb-empty"
      role="img"
      aria-label={`No image available for ${name}`}
    >
      <span>No image</span>
    </span>
  );
}

function ComparableImageThumbnail({
  imageUrl,
  name,
  to,
  href,
  ariaLabel,
}: {
  imageUrl: string | undefined;
  name: string;
  to?: string;
  href?: string;
  ariaLabel: string;
}): JSX.Element {
  const [preview, setPreview] = useState<{ left: number; top: number } | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    setImageFailed(false);
    setPreview(null);
  }, [imageUrl]);
  const showPreview = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const width = 216;
    const height = 302;
    const gutter = 10;
    const left =
      rect.right + gutter + width <= window.innerWidth
        ? rect.right + gutter
        : Math.max(gutter, rect.left - width - gutter);
    const top = Math.min(
      Math.max(gutter, rect.top),
      Math.max(gutter, window.innerHeight - height - gutter),
    );
    setPreview({ left, top });
  };

  if (imageUrl === undefined || imageFailed) {
    return <ComparableEmptyThumbnail name={name} />;
  }
  const image = <img src={imageUrl} alt="" onError={() => setImageFailed(true)} />;
  return (
    <>
      {to !== undefined ? (
        <Link
          className="tcvs-comparable-thumb"
          to={to}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={ariaLabel}
          onPointerEnter={(event) => showPreview(event.currentTarget)}
          onPointerLeave={() => setPreview(null)}
          onFocus={(event) => showPreview(event.currentTarget)}
          onBlur={() => setPreview(null)}
        >
          {image}
        </Link>
      ) : href !== undefined ? (
        <a
          className="tcvs-comparable-thumb"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={ariaLabel}
          onPointerEnter={(event) => showPreview(event.currentTarget)}
          onPointerLeave={() => setPreview(null)}
          onFocus={(event) => showPreview(event.currentTarget)}
          onBlur={() => setPreview(null)}
        >
          {image}
        </a>
      ) : (
        <span
          className="tcvs-comparable-thumb"
          role="img"
          tabIndex={0}
          aria-label={ariaLabel}
          onPointerEnter={(event) => showPreview(event.currentTarget)}
          onPointerLeave={() => setPreview(null)}
          onFocus={(event) => showPreview(event.currentTarget)}
          onBlur={() => setPreview(null)}
        >
          {image}
        </span>
      )}
      {preview === null
        ? null
        : createPortal(
            <img
              className="tcvs-comparable-magnified"
              src={imageUrl}
              alt={`${name} enlarged`}
              style={preview}
              onError={() => setImageFailed(true)}
            />,
            document.body,
          )}
    </>
  );
}

function ComparableThumbnail({ item }: { item: MarketplaceComparableItem }): JSX.Element {
  return (
    <ComparableImageThumbnail
      imageUrl={item.imageUrl}
      name={item.identity.name}
      to={item.cardId === undefined ? undefined : `/catalog/${encodeURIComponent(item.cardId)}`}
      ariaLabel={
        item.cardId === undefined
          ? `${item.identity.name} listing image`
          : `Open ${item.identity.name} card details`
      }
    />
  );
}

function SameParallelComparables({
  result,
  loading,
  error,
  onPriceSelect,
}: {
  result: MarketplaceCardComparablesResponse | null;
  loading: boolean;
  error: string | null;
  onPriceSelect: (amountMinor: number) => void;
}): JSX.Element {
  return (
    <section className="tcvs-parallel-comparables">
      <header>
        <div>
          <h3>Same card and parallel pricing</h3>
          {result !== null ? (
            <p className="tcvs-muted">
              #{result.number} · {result.parallel}
            </p>
          ) : null}
        </div>
      </header>
      {loading ? <p className="tcvs-muted">Loading comparable cards…</p> : null}
      {error !== null ? <p className="tcvs-error">Error: {error}</p> : null}
      {!loading && error === null && result === null ? (
        <p className="tcvs-muted">
          Comparable pricing will appear after an exact checklist card and parallel are selected.
        </p>
      ) : null}
      {!loading && error === null && result !== null ? (
        <div className="tcvs-comparable-groups">
          {(['NON_LISTED', 'LISTED', 'SOLD'] as const).map((state) => {
            const items = sortMarketplaceComparableItems(
              result.items.filter((item) => item.state === state),
            );
            return (
              <section key={state} className="tcvs-comparable-group">
                <div className="tcvs-comparable-group-heading">
                  <h4>{COMPARABLE_STATE_LABELS[state]}</h4>
                  <span>{items.length}</span>
                </div>
                {items.length === 0 ? (
                  <p className="tcvs-muted">None found.</p>
                ) : (
                  <div className="tcvs-comparable-list">
                    {items.map((item) => {
                      const priceLabel = comparablePrice(item);
                      const amountMinor = item.amountMinor;
                      const listedAmountMinor =
                        item.listedAmountMinor ??
                        (item.state === 'LISTED' ? amountMinor : undefined);
                      const soldAmountMinor =
                        item.soldAmountMinor ??
                        (item.state === 'SOLD' && item.priceSource === 'EBAY_SOLD'
                          ? amountMinor
                          : undefined);
                      const externalListingId = item.marketplaceListingId?.replace(/^ebay:/, '');
                      const externalListingUrl = comparableEbayListingUrl(externalListingId);
                      return (
                        <article
                          key={item.comparableId ?? item.cardId ?? item.marketplaceListingId}
                          className="tcvs-comparable-item"
                        >
                          <div className="tcvs-comparable-primary-row">
                            <ComparableThumbnail item={item} />
                            <div className="tcvs-comparable-primary-copy">
                              {item.state === 'SOLD' ? (
                                <>
                                  <strong>
                                    Listed for{' '}
                                    {listedAmountMinor === undefined ? (
                                      'Unavailable'
                                    ) : (
                                      <button
                                        type="button"
                                        className="tcvs-comparable-price-choice"
                                        aria-label={`Use listed price ${comparableAmount(listedAmountMinor, item.currency)} as listing price`}
                                        title="Use this listed price"
                                        onClick={() => onPriceSelect(listedAmountMinor)}
                                      >
                                        {comparableAmount(listedAmountMinor, item.currency)}
                                      </button>
                                    )}
                                  </strong>
                                  <strong>
                                    Sold for{' '}
                                    {soldAmountMinor === undefined ? (
                                      item.priceSource === 'EBAY_OBSERVED' ? (
                                        'Not captured'
                                      ) : amountMinor === undefined ? (
                                        priceLabel
                                      ) : (
                                        <button
                                          type="button"
                                          className="tcvs-comparable-price-choice"
                                          aria-label={`Use sold price ${priceLabel} as listing price`}
                                          title="Use this sold price"
                                          onClick={() => onPriceSelect(amountMinor)}
                                        >
                                          {priceLabel}
                                        </button>
                                      )
                                    ) : (
                                      <button
                                        type="button"
                                        className="tcvs-comparable-price-choice"
                                        aria-label={`Use sold price ${comparableAmount(soldAmountMinor, item.currency)} as listing price`}
                                        title="Use this sold price"
                                        onClick={() => onPriceSelect(soldAmountMinor)}
                                      >
                                        {comparableAmount(soldAmountMinor, item.currency)}
                                      </button>
                                    )}
                                  </strong>
                                </>
                              ) : item.state === 'LISTED' ? (
                                amountMinor === undefined ? (
                                  <strong>Listed for {priceLabel}</strong>
                                ) : (
                                  <button
                                    type="button"
                                    className="tcvs-comparable-price-choice"
                                    aria-label={`Use ${priceLabel} as listing price`}
                                    title="Use this listed price"
                                    onClick={() => onPriceSelect(amountMinor)}
                                  >
                                    Listed for {priceLabel}
                                  </button>
                                )
                              ) : amountMinor === undefined ? (
                                <strong>{priceLabel}</strong>
                              ) : (
                                <button
                                  type="button"
                                  className="tcvs-comparable-price-choice"
                                  aria-label={`Use ${priceLabel} as listing price`}
                                  title="Use this reviewed price"
                                  onClick={() => onPriceSelect(amountMinor)}
                                >
                                  {priceLabel}
                                </button>
                              )}
                              <span>
                                {item.identity.name} · #{item.identity.number}
                              </span>
                              <span className="tcvs-comparable-presentation">
                                {cardPresentationLabel(item.cardPresentation)}
                              </span>
                            </div>
                          </div>
                          <div className="tcvs-comparable-secondary-copy">
                            <span>
                              {item.batchId !== undefined ? (
                                <>
                                  Batch ·{' '}
                                  <Link
                                    to={`/storage/batches/${encodeURIComponent(item.batchId)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {comparableBatchLabel(item.batchId)}
                                  </Link>
                                </>
                              ) : item.externalOnly === true ? (
                                <>
                                  eBay-only listing ·{' '}
                                  {externalListingUrl === undefined ? (
                                    (externalListingId ?? 'Unknown')
                                  ) : (
                                    <a
                                      href={externalListingUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {externalListingId}
                                    </a>
                                  )}
                                </>
                              ) : (
                                <>
                                  Card · {item.shortCode ?? item.cardId?.slice(0, 8) ?? 'Unknown'}
                                </>
                              )}
                            </span>
                            {item.state === 'SOLD' ? (
                              <>
                                <span className="tcvs-muted">
                                  {item.soldAtSource === 'TCVS_OBSERVATION'
                                    ? 'eBay observed sold'
                                    : 'eBay sold'}
                                  {item.soldAt === undefined
                                    ? ''
                                    : ` ${new Date(item.soldAt).toLocaleDateString()}`}
                                </span>
                                {item.listedDays === undefined ? null : (
                                  <span className="tcvs-muted">
                                    {item.soldAtSource === 'TCVS_OBSERVATION'
                                      ? `Observed after ${item.listedDays} day(s)`
                                      : `Listed for ${item.listedDays} day(s)`}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="tcvs-muted">{comparableProviderLabel(item)}</span>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

// Display parity with the existing API Base classifier; never source selection authority.
export function requestedResearchTreatment(identity: CardIdentity | null): string | undefined {
  const value = (identity?.variation ?? '')
    .normalize('NFKD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, ' ')
    .trim();
  return ['', 'base', 'base version', 'no parallel', 'no parallel base version'].includes(value)
    ? undefined
    : identity?.variation?.trim();
}
function titleContainsFullTreatment(title: string, treatment: string): boolean {
  const normalize = (value: string) =>
    value.normalize('NFKC').toLocaleLowerCase('en-US').trim().replace(/\s+/gu, ' ');
  const escaped = normalize(treatment).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(?<![\\p{L}\\p{N}])${escaped}(?![\\p{L}\\p{N}])`, 'u').test(normalize(title));
}

function EbayActiveComparables({
  result,
  requestedTreatment,
  loading,
  error,
  onLookup,
  onRefresh,
  canLookup,
  onPriceSelect,
}: {
  result: EbayActiveComparablesResponse | null;
  requestedTreatment?: string;
  loading: boolean;
  error: string | null;
  onLookup: () => void;
  onRefresh: () => void;
  canLookup: boolean;
  onPriceSelect: (amountMinor: number) => void;
}): JSX.Element {
  const unavailableMessage =
    result?.unavailableReason === 'NOT_CONFIGURED'
      ? 'eBay active pricing is not configured for this environment.'
      : result?.unavailableReason === 'ACCESS_DENIED'
        ? 'The eBay application does not currently have access to active marketplace pricing.'
        : 'eBay active pricing is temporarily unavailable.';
  return (
    <section className="tcvs-parallel-comparables tcvs-ebay-active-comparables">
      <header className="tcvs-ebay-active-comparables-heading">
        <div>
          <h3>Active eBay asking prices</h3>
          <p className="tcvs-muted">Current asking prices from other sellers, not sold prices.</p>
        </div>
        <div className="tcvs-ebay-lookup-actions">
          <button
            type="button"
            className="tcvs-btn-secondary tcvs-btn-compact"
            disabled={loading || !canLookup}
            onClick={onLookup}
          >
            {loading ? 'Checking eBay…' : 'Look up eBay prices'}
          </button>
          {result !== null ? (
            <button
              type="button"
              className="tcvs-link-button"
              disabled={loading || !canLookup}
              onClick={onRefresh}
            >
              Refresh live
            </button>
          ) : null}
        </div>
      </header>
      {loading && result === null ? <p className="tcvs-muted">Checking eBay…</p> : null}
      {error !== null ? <p className="tcvs-error">Error: {error}</p> : null}
      {!loading && error === null && result === null ? (
        <p className="tcvs-muted">
          {canLookup
            ? 'Look up eBay prices when you need market evidence. Recent matching results may be cached.'
            : 'Select an exact checklist card with a set and name before looking up eBay prices.'}
        </p>
      ) : null}
      {error === null && result?.status === 'UNAVAILABLE' ? (
        <p className="tcvs-muted">{unavailableMessage}</p>
      ) : null}
      {error === null && result?.status === 'AVAILABLE' ? (
        <>
          <div className="tcvs-ebay-active-summary">
            <span>
              {result.summary.count}
              {requestedTreatment ? ' treatment-supported asking-price listings' : ' matches'}
            </span>
            {requestedTreatment ? <span>{result.items.length} related listings shown</span> : null}
            {(requestedTreatment && result.summary.count === 0) ||
            result.summary.lowAmountMinor === undefined ? null : (
              <span>Low {comparableAmount(result.summary.lowAmountMinor, 'USD')}</span>
            )}
            {(requestedTreatment && result.summary.count === 0) ||
            result.summary.medianAmountMinor === undefined ? null : (
              <span>Median {comparableAmount(result.summary.medianAmountMinor, 'USD')}</span>
            )}
            {(requestedTreatment && result.summary.count === 0) ||
            result.summary.highAmountMinor === undefined ? null : (
              <span>High {comparableAmount(result.summary.highAmountMinor, 'USD')}</span>
            )}
            <span className="tcvs-muted">
              Updated {new Date(result.asOf).toLocaleTimeString()}
              {result.cached ? ' · cached' : ''}
            </span>
          </div>
          {requestedTreatment ? (
            <p className="tcvs-muted">
              Treatment support is research text/aspect evidence, not physical verification.
            </p>
          ) : null}
          {requestedTreatment && result.summary.count === 0 ? (
            <p>
              No numeric range is supported for the selected treatment in these results. Related
              asking prices remain below for inspection.
            </p>
          ) : null}
          {result.items.length === 0 ? (
            <p className="tcvs-muted">No sufficiently close active listings were found.</p>
          ) : (
            <div className="tcvs-comparable-list">
              {sortEbayActiveComparableItems(result.items).map((item, index) => (
                <article key={item.itemId} className="tcvs-comparable-item">
                  <div className="tcvs-comparable-primary-row">
                    <ComparableImageThumbnail
                      imageUrl={item.imageUrl}
                      name={item.title}
                      href={item.itemUrl}
                      ariaLabel={`Open eBay listing ${item.itemId}`}
                    />
                    <div className="tcvs-comparable-primary-copy">
                      <button
                        type="button"
                        className="tcvs-comparable-price-choice"
                        aria-label={`Use asking price ${comparableAmount(item.amountMinor, 'USD')} as listing price`}
                        title="Use this asking price"
                        aria-describedby={
                          requestedTreatment && !item.matchReasons.includes('parallel')
                            ? `treatment-research-warning-${index}`
                            : undefined
                        }
                        onClick={() => onPriceSelect(item.amountMinor)}
                      >
                        Asking {comparableAmount(item.amountMinor, 'USD')}
                      </button>
                      {requestedTreatment && !item.matchReasons.includes('parallel') ? (
                        <p id={`treatment-research-warning-${index}`} role="note">
                          Selected treatment is not supported by this listing's research signals;
                          excluded from the summary. Use this asking price only if you choose it
                          deliberately.
                        </p>
                      ) : null}
                      {item.shippingMinor === undefined ? (
                        <span className="tcvs-muted">Shipping not shown</span>
                      ) : (
                        <span className="tcvs-muted">
                          {item.shippingMinor === 0
                            ? 'Free shipping'
                            : `Shipping ${comparableAmount(item.shippingMinor, 'USD')}`}
                          {item.totalMinor === undefined
                            ? ''
                            : ` · Total ${comparableAmount(item.totalMinor, 'USD')}`}
                        </span>
                      )}
                      <a href={item.itemUrl} target="_blank" rel="noopener noreferrer">
                        {item.title}
                      </a>
                    </div>
                  </div>
                  <div className="tcvs-comparable-secondary-copy">
                    <span>
                      {item.matchQuality === 'EXACT' ? 'Exact signals' : 'Likely match'} ·{' '}
                      {item.matchReasons.join(', ')}
                    </span>
                    <span className="tcvs-muted">
                      {item.condition ?? 'Condition not shown'}
                      {item.sellerUsername === undefined ? '' : ` · ${item.sellerUsername}`}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

export function BatchIdentificationWorkbenchPage(): JSX.Element {
  const location = useLocation();
  const reviewsReturnContext = reviewListReturnContextFrom(location.state);
  const rawId = useParams<{ id: string }>().id ?? '';
  const batchId = safeDecode(rawId);
  const [searchParams] = useSearchParams();
  const requestedCardId = searchParams.get('card')?.trim() ?? '';
  const requestedListingTemplateId = searchParams.get('listingTemplate')?.trim() ?? '';
  const navigate = useNavigate();
  const catalog = useCatalogApi();
  const storage = useStorageApi();
  const marketplace = useMarketplaceApi();
  const { claims } = useAuth();
  const isAdmin = claims !== null && hasAnyGroup(claims, ['admin']);
  const batchReadKey = JSON.stringify([claims?.sub, batchId]);
  const batchReadOwner = useRef({ key: batchReadKey, catalog });
  if (batchReadOwner.current.key !== batchReadKey || batchReadOwner.current.catalog !== catalog)
    batchReadOwner.current = { key: batchReadKey, catalog };
  const batchReadVisit = batchReadOwner.current;
  const [cards, setCards] = useState<CatalogListCardRecord[] | null>(null);
  const ownership = useOwnershipLabels((cards ?? []).map((card) => card.cardId));
  const [checklistSearchQuery, setChecklistSearchQuery] = useState('');
  const checklistSearch = useChecklistSearch(catalog.reviewChecklists, checklistSearchQuery);
  const {
    items: checklists,
    setItems: setChecklists,
    loading: checklistSearchLoading,
  } = checklistSearch;
  const requestedChecklistProducts = useRef(new Set<string>());
  const [batchChecklistId, setBatchChecklistId] = useState<string | null>(null);
  const [batchChecklistDraftId, setBatchChecklistDraftId] = useState('');
  const [batchChecklistBusy, setBatchChecklistBusy] = useState(false);
  const [batchChecklistLoaded, setBatchChecklistLoaded] = useState(false);
  const [batchChecklistSourceBacked, setBatchChecklistSourceBacked] = useState(false);
  const [cardChecklistIds, setCardChecklistIds] = useState<Record<string, string>>({});
  const [state, setState] = useState<WorkbenchState>('ready');
  const [workbenchView, setWorkbenchView] = useState<'CARD' | 'GRID'>('CARD');
  const [activeId, setActiveId] = useState<string | null>(() => requestedCardId || null);
  const [candidates, setCandidates] = useState<Record<string, ChecklistEntry[]>>({});
  const [parallelDefinitions, setParallelDefinitions] = useState<
    Record<string, ChecklistParallelDefinition[]>
  >({});
  const [parallelMatches, setParallelMatches] = useState<
    Record<string, ChecklistParallelMatch | null>
  >({});
  const [resolving, setResolving] = useState<Set<string>>(new Set());
  const [resolutionErrors, setResolutionErrors] = useState<Record<string, string>>({});
  const [suggestingCardId, setSuggestingCardId] = useState<string | null>(null);
  const [cardSuggestionNote, setCardSuggestionNote] = useState<string | null>(null);
  const [cardSuggestionError, setCardSuggestionError] = useState(false);
  const [showMatchDetails, setShowMatchDetails] = useState(readShowMatchDetailsPreference);
  const [busy, setBusy] = useState<
    | 'identity'
    | 'identity-draft'
    | 'identity-correction'
    | 'image-quality'
    | 'pick'
    | 'retire'
    | 'subset'
    | null
  >(null);
  const [editingIdentityFor, setEditingIdentityFor] = useState<string | null>(null);
  const [cardLookupOpen, setCardLookupOpen] = useState(false);
  const [assistedSelectionKey, setAssistedSelectionKey] = useState<string | null>(null);
  const lookupApplyRequest = useRef(0);

  // This visit deliberately excludes assignment revisions: selection metadata may move
  // the saved Card between queues while the accepted local identity is still unsaved.
  const editorOwnerKey = JSON.stringify([claims?.sub, batchId, activeId, state, requestedCardId]);
  const editorOwner = useRef({ key: editorOwnerKey, catalog, sequence: 0 });
  if (editorOwner.current.key !== editorOwnerKey || editorOwner.current.catalog !== catalog)
    editorOwner.current = {
      key: editorOwnerKey,
      catalog,
      sequence: editorOwner.current.sequence + 1,
    };
  const [localChecklistReview, setLocalChecklistReview] = useState<{
    owner: typeof editorOwner.current;
    cardId: string;
    queue: WorkbenchState;
    index: number;
  } | null>(null);
  const [identityDraft, setIdentityDraft] = useState<CardIdentity | null>(null);
  const [identityDraftEdited, setIdentityDraftEditedState] = useState(false);
  const [manualOverrideEnabled, setManualOverrideEnabled] = useState(false);
  const [manualSourceSelection, setManualSourceSelection] = useState<
    ChecklistSourceSelection | undefined
  >();
  const [manualClassificationConfirmed, setManualClassificationConfirmed] = useState(false);
  const [manualOverrideReason, setManualOverrideReason] = useState('');
  const identityDraftEditedRef = useRef(false);
  const setIdentityDraftEdited = useCallback((edited: boolean) => {
    if (!edited) setLocalChecklistReview(null);
    identityDraftEditedRef.current = edited;
    setIdentityDraftEditedState(edited);
  }, []);
  const [pricingDraft, setPricingDraft] = useState<CardResearchPricingDraft | null>(null);
  const [cardPresentationDraft, setCardPresentationDraft] = useState<
    CardPresentation | undefined
  >();
  const [itemSpecifics, setItemSpecifics] = useState<EbayItemSpecifics>({});
  const [itemSpecificsCategory, setItemSpecificsCategory] =
    useState<EbaySellerHubSinglesCategory | null>(null);
  const [ccgDecisions, setCcgDecisions] = useState<{
    scope: string;
    fields: Partial<Record<'Finish' | 'Rarity', CcgFieldDecision>>;
  }>({ scope: '', fields: {} });
  const ccgIdentitySaved = useRef<string | null>(null);
  const [ccgCopyUnknownCard, setCcgCopyUnknownCard] = useState<string | null>(null);
  const [ccgReadyUnknownCard, setCcgReadyUnknownCard] = useState<string | null>(null);
  const [ccgStatusNotice, setCcgStatusNotice] = useState<string | null>(null);
  const [ccgListingSavedScope, setCcgListingSavedScope] = useState<string | null>(null);
  const [itemDetailsMode, setItemDetailsMode] = useState<'ALL' | 'FEATURES' | null>(null);
  const [batchDefaultPrice, setBatchDefaultPrice] = useState('');
  const [imageIssue, setImageIssue] = useState(false);
  const [replacementIntent, setReplacementIntent] = useState<{
    cardId: string;
    revision: ReviewChecklistSelectionRevision;
    kind: 'REFINE' | 'MANUAL';
  } | null>(null);
  const [pendingParallel, setPendingParallel] = useState('');
  const [selectedChecklistCandidate, setSelectedChecklistCandidate] = useState('');
  const [localStructuredParallelChoice, setStructuredParallelChoice] = useState<string | null>(
    null,
  );
  const restoredStructuredChoiceFor = useRef('');
  const checklistResolutionGeneration = useRef(0);
  const resolutionRequests = useRef(new Map<string, symbol>());
  const checklistSelectionGenerations = useRef(new Map<string, number>());
  const suggestionRequest = useRef<symbol | null>(null);
  const identityEvidenceBaseline = useRef<{ cardId: string; identity: CardIdentity } | null>(null);
  const [explicitSubset, setExplicitSubset] = useState<{
    key: string;
    subset: string;
    definitionId?: string;
    sourceFamilyId?: string;
    matched: boolean;
  } | null>(null);
  const subsetChoiceRequest = useRef(0);
  const currentSubsetScope = useRef('');
  const [checklistIssueOpen, setChecklistIssueOpen] = useState(false);
  const [pendingChecklistIdentity, setPendingChecklistIdentity] = useState<{
    scope: string;
    proposed: CardIdentity;
    commit: () => void;
  } | null>(null);
  const [checklistIdentityAccepted, setChecklistIdentityAccepted] = useState(false);
  const [pendingAssistedTreatments, setPendingAssistedTreatments] = useState<{
    scope: string;
    choices: Array<{ id: string; label: string; choose: () => void }>;
  } | null>(null);

  const [checklistIssueType, setChecklistIssueType] = useState<ChecklistIssueType>('MISSING_CARD');
  const [checklistIssueNote, setChecklistIssueNote] = useState('');
  const [checklistIssueBusy, setChecklistIssueBusy] = useState(false);
  const [checklistIssueDelivery, setChecklistIssueDelivery] = useState<{
    status: 'SENT' | 'NOT_SENT' | 'UNKNOWN';
    message: string;
  } | null>(null);
  const checklistIssueRequest = useRef(0);
  const checklistIssueInFlight = useRef(false);
  const [checklistIssueGuidance, setChecklistIssueGuidance] = useState<
    ChecklistIssueGuidanceResponse['items']
  >([]);
  const [checklistIssueGuidanceLoading, setChecklistIssueGuidanceLoading] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(true);
  const [imagePopoutOpen, setImagePopoutOpen] = useState(false);
  const [imageAdjustmentBusySide, setImageAdjustmentBusySide] = useState<ReviewImageSide | null>(
    null,
  );
  const [imageRotations, setImageRotations] = useState<ReviewImageRotations>(
    EMPTY_REVIEW_IMAGE_ROTATIONS,
  );
  const [imageAlignmentGrid, setImageAlignmentGrid] = useState(true);
  const [batchAutoStraighten, setBatchAutoStraighten] = useState(false);
  const [autoStraightenNote, setAutoStraightenNote] = useState<string | null>(null);
  const [autoStraightenError, setAutoStraightenError] = useState<string | null>(null);
  const autoStraightenRun = useRef(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchNotice, setBatchNotice] = useState<string | null>(null);
  const [listingTemplates, setListingTemplates] = useState<MarketplaceListingTemplate[]>([]);
  const [assignedListingTemplateCount, setAssignedListingTemplateCount] = useState(0);
  const [listingTemplateSource, setListingTemplateSource] =
    useState<MarketplaceListingTemplateSource>('NONE');
  const [selectedListingTemplateId, setSelectedListingTemplateId] = useState('');
  const [listingContextLoading, setListingContextLoading] = useState(false);
  const [researchCopyReadyFor, setResearchCopyReadyFor] = useState<string | null>(null);
  const [listingCopyRefreshGeneration, setListingCopyRefreshGeneration] = useState(0);
  const [importedListingReloadPending, setImportedListingReloadPending] = useState(false);
  const importedListingReloadGeneration = useRef(0);
  const [listingCopyRestoredGeneration, setListingCopyRestoredGeneration] = useState(-1);
  const [listingCopyReadyContext, setListingCopyReadyContext] = useState('');
  const [bulkReviewReadyKey, setBulkReviewReadyKey] = useState('');
  const bulkGuardRef = useRef<() => boolean>(() => true);
  const bulkReviewBaseline = useRef<{
    key: string;
    snapshot: string;
    listingSnapshot: string;
  } | null>(null);
  const [listingEditLock, setListingEditLock] = useState<MarketplaceListingEditLock | null>(null);
  const [identityCorrectionMode, setIdentityCorrectionMode] = useState(false);
  const [identityCorrectionReason, setIdentityCorrectionReason] = useState('');
  const [generatedDescription, setGeneratedDescription] = useState('');
  const [descriptionSourceTitle, setDescriptionSourceTitle] = useState('');
  const [descriptionUsesTemplateDefault, setDescriptionUsesTemplateDefault] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [listingTitleOverride, setListingTitleOverride] = useState<string | null>(null);
  const [reviewPanelTab, setReviewPanelTab] = useState<'identification' | 'pricing'>(
    'identification',
  );
  const [numberLookupBusy, setNumberLookupBusy] = useState(false);
  const [comparables, setComparables] = useState<MarketplaceCardComparablesResponse | null>(null);
  const [comparablesLoading, setComparablesLoading] = useState(false);
  const [comparablesError, setComparablesError] = useState<string | null>(null);
  const [ebayActiveComparables, setEbayActiveComparables] =
    useState<EbayActiveComparablesResponse | null>(null);
  const [ebayActiveComparablesLoading, setEbayActiveComparablesLoading] = useState(false);
  const [ebayActiveComparablesError, setEbayActiveComparablesError] = useState<string | null>(null);
  const ebayActiveComparablesRequest = useRef(0);
  const autoOcrAttempted = useRef<Set<string>>(new Set());
  const autoOcrAttempts = useRef<Map<string, number>>(new Map());
  const autoOcrRetryTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [autoOcrRetryTick, setAutoOcrRetryTick] = useState(0);
  const batchOcrCatchupAttempted = useRef<Set<string>>(new Set());
  const batchOcrCatchupInFlight = useRef<Set<string>>(new Set());
  const batchOcrCatchupRunning = useRef(false);
  const batchOcrCatchupGeneration = useRef(0);
  const [batchOcrCatchupTick, setBatchOcrCatchupTick] = useState(0);
  const handledRequestedCardId = useRef('');
  const inventoryActionPending = useRef(false);
  const removedReviewCards = useRef(new Set<string>());
  const currentReviewBatch = useRef(batchId);
  currentReviewBatch.current = batchId;

  useEffect(() => {
    removedReviewCards.current.clear();
  }, [batchId]);

  const refresh = useCallback(
    async (accept?: () => boolean) => {
      if (batchId === '' || batchReadOwner.current !== batchReadVisit || accept?.() === false)
        return;
      // Scoped imported reloads have their own visit lock; keep batch-level loading intact.
      if (accept === undefined) setBatchChecklistLoaded(false);
      const [cardsResult, contextResult] = await Promise.all([
        loadOrderedBatchCards(catalog.list, batchId),
        catalog.getBatchChecklist(batchId),
      ]);
      if (
        batchReadOwner.current !== batchReadVisit ||
        currentReviewBatch.current !== batchId ||
        accept?.() === false
      )
        return;
      if (!cardsResult.ok) {
        setBatchError(cardsResult.message ?? cardsResult.error);
        return;
      }
      // Placement-index propagation must not put a successfully removed card back in review.
      const remainingCards = positionBatchCards(
        cardsResult.data.items.filter((card) => !removedReviewCards.current.has(card.cardId)),
      );
      setCards(remainingCards);
      const configuredId = contextResult.ok
        ? (contextResult.data.context?.checklistId ?? null)
        : null;
      setBatchChecklistId(configuredId);
      setBatchChecklistDraftId(configuredId ?? '');
      setBatchChecklistLoaded(true);
      setBatchChecklistSourceBacked(
        contextResult.ok && contextResult.data.context?.source !== undefined,
      );
      setBatchError(null);
      return remainingCards;
    },
    [batchId, catalog, batchReadVisit],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(
    () => () => {
      for (const timer of autoOcrRetryTimers.current.values()) clearTimeout(timer);
      autoOcrRetryTimers.current.clear();
    },
    [],
  );

  useEffect(() => {
    batchOcrCatchupGeneration.current += 1;
    batchOcrCatchupAttempted.current.clear();
    batchOcrCatchupInFlight.current.clear();
    batchOcrCatchupRunning.current = false;
  }, [batchId]);

  const heldChecklistReview =
    identityDraftEdited &&
    localChecklistReview?.owner === editorOwner.current &&
    localChecklistReview.cardId === activeId &&
    localChecklistReview.queue === state
      ? localChecklistReview
      : null;
  // Only the displayed review position is held. Never alter the saved classifier inputs.
  const displayedQueue = (card: CatalogListCardRecord) =>
    heldChecklistReview?.cardId === card.cardId ? heldChecklistReview.queue : workbenchState(card);
  const counts = useMemo(() => {
    const next = Object.fromEntries(STATE_ORDER.map((key) => [key, 0])) as Record<
      WorkbenchState,
      number
    >;
    for (const card of cards ?? []) {
      next[displayedQueue(card)] += 1;
    }
    return next;
  }, [cards, heldChecklistReview]);

  useEffect(() => {
    if (cards === null || counts[state] > 0) return;
    setState(STATE_ORDER.find((key) => counts[key] > 0) ?? 'completed');
  }, [cards, counts, state]);

  const visible = useMemo(() => {
    const result = (cards ?? []).filter((card) => displayedQueue(card) === state);
    if (heldChecklistReview) {
      const index = result.findIndex((card) => card.cardId === heldChecklistReview.cardId);
      if (index >= 0) {
        const [held] = result.splice(index, 1);
        result.splice(Math.min(heldChecklistReview.index, result.length), 0, held!);
      }
    }
    return result;
  }, [cards, state, heldChecklistReview]);
  const sourceCard = visible.find((card) => card.cardId === activeId) ?? visible[0] ?? null;
  const photoReview = useVersionedImageReview(sourceCard, catalog, 'FINAL');
  const active = photoReview.card;
  const assignment = active?.reviewChecklistSelection;
  const replacesAssignment =
    replacementIntent !== null &&
    active !== null &&
    assignment !== undefined &&
    replacementIntent.cardId === active.cardId &&
    replacementIntent.revision.checklistId === assignment.checklistId &&
    replacementIntent.revision.selectedAt === assignment.selectedAt &&
    (replacementIntent.kind === 'REFINE' || manualOverrideEnabled);
  const savedHierarchy =
    !replacesAssignment && hasAssignedHierarchy(active) ? assignment : undefined;
  const structuredParallelChoice =
    localStructuredParallelChoice ??
    (savedHierarchy?.parallel
      ? savedHierarchy.parallel.kind === 'NONE'
        ? NO_PARALLEL_DEFINITION_ID
        : savedHierarchy.parallel.definitionId
      : null);
  useEffect(() => {
    if (replacementIntent && !replacesAssignment) setReplacementIntent(null);
  }, [replacementIntent, replacesAssignment]);

  const previousAssignment = useRef<{ cardId: string; value: string; durable: boolean } | null>(
    null,
  );
  useEffect(() => {
    if (!active) {
      previousAssignment.current = null;
      return;
    }
    const value = JSON.stringify(active.reviewChecklistSelection ?? null);
    const previous = previousAssignment.current;
    previousAssignment.current = {
      cardId: active.cardId,
      value,
      durable: Boolean(active.reviewChecklistSelection?.selectedAt),
    };
    if (
      heldChecklistReview?.cardId === active.cardId ||
      previous?.cardId !== active.cardId ||
      previous.value === value ||
      !previous.durable ||
      (active.reviewChecklistSelection !== undefined && !active.reviewChecklistSelection.selectedAt)
    )
      return;
    setReplacementIntent(null);
    setCardChecklistIds((current) => {
      if (current[active.cardId] === undefined) return current;
      const next = { ...current };
      delete next[active.cardId];
      return next;
    });
    setIdentityDraft(identityDraftForCard(active));
    setIdentityDraftEdited(false);
    setSelectedChecklistCandidate('');
    setStructuredParallelChoice(null);
    setExplicitSubset(null);
    restoredStructuredChoiceFor.current = '';
  }, [active, heldChecklistReview]);

  const currentVisitKey = `${batchId}|${active === null ? '|' : assignmentResolutionKey(active, effectiveCardChecklistId(active, cardChecklistIds[active.cardId], batchChecklistId) ?? '')}|editor:${editorOwner.current.sequence}`;
  const reviewVisit = useRef({ key: currentVisitKey, sequence: 0 });
  const acceptedSuggestionTransition = useRef<{ fromSequence: number; toKey: string } | null>(null);
  if (reviewVisit.current.key !== currentVisitKey) {
    reviewVisit.current = { key: currentVisitKey, sequence: reviewVisit.current.sequence + 1 };
  }
  const currentVisit = reviewVisit.current;
  const variantOwnerKey = JSON.stringify([
    claims?.sub,
    batchId,
    currentVisit.sequence,
    active?.cardId,
    active?.identity,
    active?.reviewChecklistSelection,
    active?.checklistSourceLineage,
    manualOverrideEnabled,
  ]);
  const variantOwner = useRef({ key: variantOwnerKey, catalog, epoch: 0 });
  if (variantOwner.current.key !== variantOwnerKey || variantOwner.current.catalog !== catalog)
    variantOwner.current = { key: variantOwnerKey, catalog, epoch: variantOwner.current.epoch + 1 };
  const variantRequests = useRef(0);
  const [variantObservation, setVariantObservation] = useState<{
    owner: number;
    request: number;
    checklistId: string;
    rows: readonly ChecklistEntry[];
    definitions: readonly ChecklistParallelDefinition[];
  } | null>(null);
  const invalidateVariantOptions = () => {
    ++variantRequests.current;
    setVariantObservation(null);
  };
  const beginVariantOptions = (cardId: string) => {
    if (latestActive.current?.cardId !== cardId) return { publish: () => undefined };
    const owner = variantOwner.current.epoch,
      request = ++variantRequests.current;
    setVariantObservation(null);
    return {
      publish: (
        checklistId: string,
        rows: readonly ChecklistEntry[],
        definitions: readonly ChecklistParallelDefinition[],
      ) => {
        if (variantOwner.current.epoch === owner && variantRequests.current === request)
          setVariantObservation({ owner, request, checklistId, rows, definitions });
      },
    };
  };

  useEffect(() => {
    checklistIssueRequest.current += 1;
    checklistIssueInFlight.current = false;
    setChecklistIssueBusy(false);
    setChecklistIssueDelivery(null);
    setPendingChecklistIdentity(null);
    setPendingAssistedTreatments(null);
    setChecklistIdentityAccepted(false);
    return () => {
      checklistIssueRequest.current += 1;
    };
  }, [currentVisit.sequence]);
  const latestActive = useRef(active);
  latestActive.current = active;
  useEffect(() => {
    importedListingReloadGeneration.current += 1;
    setImportedListingReloadPending(false);
    return () => {
      importedListingReloadGeneration.current += 1;
    };
  }, [currentVisit.sequence]);
  const latestLookupDraft = useRef({
    identityDraft,
    manualOverrideEnabled,
    selectedChecklistCandidate,
    structuredParallelChoice,
  });
  latestLookupDraft.current = {
    identityDraft,
    manualOverrideEnabled,
    selectedChecklistCandidate,
    structuredParallelChoice,
  };
  const [approvalImageLoads, setApprovalImageLoads] = useState<{
    visit: typeof currentVisit;
    sides: Partial<Record<ReviewImageSide, { url: string; loaded: boolean }>>;
  } | null>(null);
  const displayedImageSides = (['front', 'back'] as const).filter(
    (side) => active?.imageUrls?.[side] !== undefined,
  );
  const displayedImagesLoaded =
    displayedImageSides.length > 0 &&
    approvalImageLoads?.visit === currentVisit &&
    displayedImageSides.every((side) => {
      const loaded = approvalImageLoads.sides[side];
      return loaded?.loaded === true && loaded.url === active?.imageUrls?.[side];
    });
  const noteDisplayedImageLoad = (side: ReviewImageSide, url: string, loaded: boolean) => {
    if (reviewVisit.current !== currentVisit || latestActive.current?.imageUrls?.[side] !== url)
      return;
    setApprovalImageLoads((current) => {
      const prior = current?.visit === currentVisit ? current.sides : {};
      if (prior[side]?.url === url && prior[side]?.loaded === loaded) return current;
      return { visit: currentVisit, sides: { ...prior, [side]: { url, loaded } } };
    });
  };
  const feedbackCard = useRef<string | undefined>(active?.cardId);
  useEffect(() => {
    const sameFeedbackCard = feedbackCard.current === latestActive.current?.cardId;
    feedbackCard.current = latestActive.current?.cardId;
    const transition = acceptedSuggestionTransition.current;
    const preservesAcceptedNote =
      transition !== null &&
      transition.fromSequence + 1 === currentVisit.sequence &&
      transition.toKey === currentVisit.key;
    acceptedSuggestionTransition.current = null;
    setCardLookupOpen(false);
    lookupApplyRequest.current += 1;
    setError((current) =>
      sameFeedbackCard &&
      current?.startsWith('Identity was saved, but Ready to List was not queued')
        ? current
        : null,
    );
    setNotice(null);
    if (!preservesAcceptedNote) setCardSuggestionNote(null);
    setCardSuggestionError(false);
    suggestionRequest.current = null;
    setSuggestingCardId(null);
    setGeneratingDescription(false);
    setChecklistIssueBusy(false);
    setResolving(new Set());
    return () => {
      // Returning to the same card is a new visit, even if its checklist key is unchanged.
      if (reviewVisit.current === currentVisit) {
        reviewVisit.current = { ...currentVisit, sequence: currentVisit.sequence + 1 };
      }
    };
  }, [currentVisit]);

  const refreshAfterBulkEdit = async () => {
    if (reviewVisit.current === currentVisit) invalidateVariantOptions();
    const updatedCards = await refresh();
    if (updatedCards === undefined) return;
    // Invalidate only after a successful apply/read, not previews or ordinary refreshes.
    checklistResolutionGeneration.current += 1;
    subsetCatalog.invalidate();
    restoredStructuredChoiceFor.current = '';
    setCandidates({});
    setParallelDefinitions({});
    setParallelMatches({});
    setResolutionErrors({});
    setCardChecklistIds({});
    const updated = updatedCards.find((card) => card.cardId === active?.cardId);
    if (updated === undefined) return;
    const nextIdentity = identityDraftForCard(updated);
    setIdentityDraft(nextIdentity);
    setIdentityDraftEdited(false);
    setPricingDraft(pricingDraftWithBatchDefault(updated, nextIdentity, batchDefaultPrice));
    setPendingParallel('');
    setSelectedChecklistCandidate('');
    setStructuredParallelChoice(null);
    setExplicitSubset(null);
  };

  useEffect(() => {
    if (
      cards === null ||
      requestedCardId === '' ||
      handledRequestedCardId.current === requestedCardId
    ) {
      return;
    }
    const requestedCard = cards.find((card) => card.cardId === requestedCardId);
    handledRequestedCardId.current = requestedCardId;
    if (requestedCard === undefined) {
      setBatchError('The requested card is not stored in this batch.');
      return;
    }
    setState(workbenchState(requestedCard));
    setActiveId(requestedCard.cardId);
  }, [cards, requestedCardId]);

  const resolveKey = (cardId: string): string => {
    const card = (cards ?? []).find((candidate) => candidate.cardId === cardId);
    const checklistId =
      card === undefined
        ? (cardChecklistIds[cardId] ?? batchChecklistId ?? '')
        : (effectiveCardChecklistId(card, cardChecklistIds[cardId], batchChecklistId) ?? '');
    return card === undefined
      ? `${cardId}|${checklistId}`
      : assignmentResolutionKey(card, checklistId);
  };

  const restoredEditorOwner = useRef(editorOwner.current);
  useEffect(() => {
    const ownerChanged = restoredEditorOwner.current !== editorOwner.current;
    restoredEditorOwner.current = editorOwner.current;
    if (active === null) {
      restoredStructuredChoiceFor.current = '';
      setEditingIdentityFor(null);
      setAssistedSelectionKey(null);
      setIdentityDraft(null);
      setIdentityDraftEdited(false);
      setManualOverrideEnabled(false);
      setManualOverrideReason('');
      setPricingDraft(null);
      setCardPresentationDraft(undefined);
      setItemSpecifics({});
      setItemDetailsMode(null);
      setImageIssue(false);
      setPendingParallel('');
      setSelectedChecklistCandidate('');
      setStructuredParallelChoice(null);
      setExplicitSubset(null);
      setChecklistIssueOpen(false);
      setChecklistIssueType('MISSING_CARD');
      setChecklistIssueNote('');
      setChecklistIssueBusy(false);
      setCardSuggestionNote(null);
      setCardSuggestionError(false);
      setGeneratedDescription('');
      setDescriptionSourceTitle('');
      setDescriptionUsesTemplateDefault(false);
      setListingTitleOverride(null);
      setListingEditLock(null);
      setIdentityCorrectionMode(false);
      setIdentityCorrectionReason('');
      setNumberLookupBusy(false);
      setComparables(null);
      setComparablesError(null);
      setImageRotations({ ...EMPTY_REVIEW_IMAGE_ROTATIONS });
      setAutoStraightenNote(null);
      setAutoStraightenError(null);
      return;
    }
    const requestedCardIsChangingQueue =
      requestedCardId !== '' &&
      activeId === requestedCardId &&
      cards?.some((card) => card.cardId === requestedCardId) === true;
    if (active.cardId !== activeId && !requestedCardIsChangingQueue) {
      setActiveId(active.cardId);
    }
    if (editingIdentityFor !== active.cardId || ownerChanged) {
      if (ownerChanged) setCardChecklistIds({});
      restoredStructuredChoiceFor.current = '';
      setEditingIdentityFor(active.cardId);
      setAssistedSelectionKey(null);
      const nextIdentity = identityDraftForCard(active);
      identityEvidenceBaseline.current = { cardId: active.cardId, identity: nextIdentity };
      setIdentityDraft(nextIdentity);
      setIdentityDraftEdited(false);
      setManualOverrideEnabled(
        active.identityReviewDraft?.manualIdentityOverrideReason !== undefined ||
          (active.identityReviewDraft?.checklistSourceSelection == null &&
            active.manualIdentityOverride !== undefined),
      );
      setManualSourceSelection(
        active.identityReviewDraft?.manualIdentityOverrideSourceEvidence?.selection ??
          active.manualIdentityOverride?.sourceEvidence?.selection,
      );
      setManualClassificationConfirmed(false);
      setManualOverrideReason(
        active.identityReviewDraft?.manualIdentityOverrideReason ??
          active.manualIdentityOverride?.reason ??
          '',
      );
      setPricingDraft(pricingDraftWithBatchDefault(active, nextIdentity, batchDefaultPrice));
      setCardPresentationDraft(presentationDraftForCard(active));
      setItemSpecifics({});
      setItemDetailsMode(null);
      setImageIssue(active.imageQualityStatus === 'REJECTED');
      setPendingParallel('');
      setSelectedChecklistCandidate('');
      setStructuredParallelChoice(null);
      setExplicitSubset(null);
      setChecklistIssueOpen(false);
      setChecklistIssueType('MISSING_CARD');
      setChecklistIssueNote('');
      setChecklistIssueBusy(false);
      setCardSuggestionNote(null);
      setCardSuggestionError(false);
      setGeneratedDescription('');
      setDescriptionSourceTitle('');
      setDescriptionUsesTemplateDefault(false);
      setListingTitleOverride(null);
      setListingEditLock(null);
      setIdentityCorrectionMode(false);
      setIdentityCorrectionReason('');
      setNumberLookupBusy(false);
      setComparables(null);
      setComparablesError(null);
      setImageRotations({ ...EMPTY_REVIEW_IMAGE_ROTATIONS });
      setAutoStraightenNote(null);
      setAutoStraightenError(null);
    }
  }, [active, activeId, cards, editingIdentityFor, requestedCardId, editorOwner.current]);

  useEffect(() => {
    if (active === null || editingIdentityFor !== active.cardId) return;
    const next = identityDraftForCard(active);
    const previous = identityEvidenceBaseline.current;
    identityEvidenceBaseline.current = { cardId: active.cardId, identity: next };
    if (
      previous?.cardId !== active.cardId ||
      identityDraftEditedRef.current ||
      active.identity !== undefined ||
      active.identityStatus === 'IDENTIFIED' ||
      active.identityReviewDraft !== undefined ||
      manualOverrideEnabled ||
      explicitSubset !== null
    )
      return;
    // Apply only changed evidence, retaining enrichment from an already resolved roster.
    const changed = Object.fromEntries(
      Object.entries(next).filter(
        ([key, value]) =>
          value !== undefined &&
          (typeof value !== 'string' || value.trim() !== '') &&
          JSON.stringify(value) !== JSON.stringify(previous.identity[key as keyof CardIdentity]),
      ),
    );
    if (Object.keys(changed).length > 0) {
      setIdentityDraft((draft) => (draft === null ? next : { ...draft, ...changed }));
      if (['number', 'name', 'subset', 'variation', 'set'].some((key) => key in changed)) {
        const key = resolveKey(active.cardId);
        checklistResolutionGeneration.current += 1;
        restoredStructuredChoiceFor.current = '';
        setCandidates((current) => {
          const next = { ...current };
          delete next[key];
          return next;
        });
        setParallelDefinitions((current) => {
          const next = { ...current };
          delete next[key];
          return next;
        });
        setParallelMatches((current) => {
          const next = { ...current };
          delete next[key];
          return next;
        });
        setResolutionErrors((current) => {
          const next = { ...current };
          delete next[key];
          return next;
        });
        setSelectedChecklistCandidate('');
        setStructuredParallelChoice(null);
      }
    }
  }, [active, editingIdentityFor, explicitSubset, manualOverrideEnabled]);

  const changeImageRotation = useCallback((side: ReviewImageSide, degrees: number) => {
    setImageRotations((current) => ({ ...current, [side]: degrees }));
    setAutoStraightenNote(null);
    setAutoStraightenError(null);
  }, []);

  const resetImageRotations = useCallback(() => {
    setImageRotations({ ...EMPTY_REVIEW_IMAGE_ROTATIONS });
    setAutoStraightenNote(null);
    setAutoStraightenError(null);
  }, []);

  const autoStraightenCardId = active?.cardId ?? null;
  useEffect(() => {
    const runId = autoStraightenRun.current + 1;
    autoStraightenRun.current = runId;
    if (!batchAutoStraighten || autoStraightenCardId === null || !photoReview.ready) {
      setImageAdjustmentBusySide(null);
      setAutoStraightenNote(null);
      setAutoStraightenError(null);
      return;
    }

    const run = async () => {
      setAutoStraightenNote(null);
      setAutoStraightenError(null);
      const freshCard = await catalog.get(autoStraightenCardId);
      if (autoStraightenRun.current !== runId) return;
      if (!freshCard.ok) throw new Error(freshCard.message ?? freshCard.error);

      const uncertainSides: ReviewImageSide[] = [];
      let analyzedSides = 0;
      let correctedSides = 0;
      for (const side of ['front', 'back'] as const) {
        const sourceUrl = photoReview.required
          ? active?.imageUrls?.[side]
          : freshCard.data.imageUrls?.[side];
        if (sourceUrl === undefined) continue;
        setImageAdjustmentBusySide(side);
        try {
          const estimate = await estimateReviewImageRotation(sourceUrl);
          if (autoStraightenRun.current !== runId) return;
          analyzedSides += 1;
          if (estimate.correctionDegrees !== 0) correctedSides += 1;
          setImageRotations((current) => ({
            ...current,
            [side]: estimate.correctionDegrees,
          }));
          setImageAlignmentGrid(true);
        } catch (error) {
          if (autoStraightenRun.current !== runId) return;
          if (error instanceof Error && error.message === 'image_skew_uncertain') {
            uncertainSides.push(side);
            continue;
          }
          throw new Error(`Could not analyze the ${side} image. Please retry.`);
        }
      }
      if (autoStraightenRun.current !== runId) return;
      if (analyzedSides > 0 && correctedSides > 0) {
        setAutoStraightenNote(
          `Auto straighten staged ${correctedSides === 2 ? 'front and back corrections' : 'an image correction'}.`,
        );
      }
      if (uncertainSides.length > 0) {
        setAutoStraightenError(
          `Could not confidently detect the ${uncertainSides.join(' and ')} image angle. Use the grid and manual controls.`,
        );
      }
    };

    void run()
      .catch((error: unknown) => {
        if (autoStraightenRun.current !== runId) return;
        setAutoStraightenError(
          error instanceof Error ? error.message : 'image_skew_detection_failed',
        );
      })
      .finally(() => {
        if (autoStraightenRun.current === runId) setImageAdjustmentBusySide(null);
      });

    return () => {
      if (autoStraightenRun.current === runId) {
        autoStraightenRun.current += 1;
        setImageAdjustmentBusySide(null);
      }
    };
  }, [
    autoStraightenCardId,
    batchAutoStraighten,
    catalog,
    photoReview.required,
    photoReview.ready,
    photoReview.reviewId,
  ]);

  const loadCandidates = useCallback(
    async (cardId: string, retry = false): Promise<WorkbenchChecklistResolution> => {
      if (retry) setError(null);
      const card = (cards ?? []).find((candidate) => candidate.cardId === cardId);
      const checklistId =
        card === undefined
          ? (cardChecklistIds[cardId] ?? batchChecklistId)
          : effectiveCardChecklistId(card, cardChecklistIds[cardId], batchChecklistId);
      const tentativeChecklist =
        card !== undefined &&
        cardChecklistIds[cardId] === undefined &&
        card.reviewChecklistSelection === undefined &&
        batchChecklistId === null &&
        ambiguousChecklistCandidates(card)[0]?.checklistId === checklistId;
      const key =
        card === undefined
          ? `${cardId}|${checklistId ?? ''}`
          : assignmentResolutionKey(card, checklistId ?? '');
      if (!retry && resolutionErrors[key] !== undefined) {
        return { candidates: [], parallelDefinitions: [] };
      }
      const cachedCandidates = candidates[key];
      const cachedDefinitions = parallelDefinitions[key];
      const cachedMatch = parallelMatches[key];
      if (!retry && cachedCandidates !== undefined && cachedDefinitions !== undefined) {
        return {
          candidates: cachedCandidates,
          parallelDefinitions: cachedDefinitions,
          ...(cachedMatch === null || cachedMatch === undefined
            ? {}
            : { parallelMatch: cachedMatch }),
        };
      }
      const variantRead = beginVariantOptions(cardId);
      const visit = reviewVisit.current;
      const request = Symbol(key);
      resolutionRequests.current.set(key, request);
      const selectionGeneration = checklistSelectionGenerations.current.get(cardId) ?? 0;
      const ownsRequest = () => resolutionRequests.current.get(key) === request;
      const isCurrent = () =>
        reviewVisit.current === visit &&
        generation === checklistResolutionGeneration.current &&
        ownsRequest() &&
        (checklistSelectionGenerations.current.get(cardId) ?? 0) === selectionGeneration;
      setResolving((previous) => new Set(previous).add(cardId));
      const generation = checklistResolutionGeneration.current;
      let result: Awaited<ReturnType<typeof catalog.checklistResolve>>;
      try {
        result = await catalog.checklistResolve(cardId, {
          ...(checklistId === null ? {} : { checklistId }),
          ...(tentativeChecklist ? { tentativeChecklist: true } : {}),
          ...(card?.identityReviewDraft?.importedFrom
            ? { searchNumber: card.identityReviewDraft.identity.number }
            : {}),
          ...(card === undefined || evidenceSubset(card) === ''
            ? {}
            : { subset: evidenceSubset(card) }),
        });
      } catch {
        if (isCurrent()) {
          setResolutionErrors((previous) => ({
            ...previous,
            [key]: 'Could not load checklist options. Try again.',
          }));
          setError('Could not load checklist options. Try again.');
        }
        return { candidates: [], parallelDefinitions: [] };
      } finally {
        if (isCurrent())
          setResolving((previous) => {
            const next = new Set(previous);
            next.delete(cardId);
            return next;
          });
      }
      if (!isCurrent() || generation !== checklistResolutionGeneration.current) {
        return { candidates: [], parallelDefinitions: [] };
      }
      if (!result.ok) {
        setResolutionErrors((previous) => ({
          ...previous,
          [key]: result.message ?? result.error,
        }));
        setError(result.message ?? result.error);
        return { candidates: [], parallelDefinitions: [] };
      }
      setResolutionErrors((previous) => {
        const next = { ...previous };
        delete next[key];
        return next;
      });
      if (retry) restoredStructuredChoiceFor.current = '';
      const resolved = (result.data as ChecklistResolveResponse).candidates ?? [];
      const definitions = (result.data as ChecklistResolveResponse).parallelDefinitions ?? [];
      const parallelMatch = (result.data as ChecklistResolveResponse).parallelMatch;
      if (checklistId !== null) variantRead.publish(checklistId, resolved, definitions);
      setCandidates((previous) => ({ ...previous, [key]: resolved }));
      setParallelDefinitions((previous) => ({ ...previous, [key]: definitions }));
      setParallelMatches((previous) => ({
        ...previous,
        [key]: parallelMatch ?? null,
      }));
      if (latestActive.current?.cardId === cardId) {
        if (
          !identityDraftEditedRef.current &&
          !hasAssignedHierarchy(latestActive.current) &&
          latestActive.current?.identityStatus !== 'IDENTIFIED' &&
          latestActive.current?.identity === undefined &&
          latestActive.current?.identityReviewDraft === undefined &&
          resolved.length > 0 &&
          (card === undefined ||
            persistedStructuredChecklistChoice(card, resolved, definitions) === null)
        ) {
          setIdentityDraft((current) =>
            identityDraftFromChecklistCandidates(
              resolved,
              current ?? (card === undefined ? { ...EMPTY_IDENTITY } : identityDraftForCard(card)),
            ),
          );
        }
      }
      if (
        !tentativeChecklist &&
        result.data.checklistSuggestion !== undefined &&
        resolved.length < 2
      ) {
        void refresh();
      }
      return {
        candidates: resolved,
        parallelDefinitions: definitions,
        ...(parallelMatch === undefined ? {} : { parallelMatch }),
      };
    },
    [
      batchChecklistId,
      candidates,
      cardChecklistIds,
      cards,
      catalog,
      parallelDefinitions,
      parallelMatches,
      resolutionErrors,
      refresh,
    ],
  );

  useEffect(() => {
    if (active === null) return;
    const activeCondition = reviewCondition(
      active,
      effectiveCardChecklistId(active, cardChecklistIds[active.cardId], batchChecklistId) !== null,
      (parallelDefinitions[resolveKey(active.cardId)]?.length ?? 0) > 0,
      candidates[resolveKey(active.cardId)]?.length ?? 0,
    );
    if (!shouldAutomaticallyResolveCard(activeCondition)) return;
    void loadCandidates(active.cardId);
  }, [active, batchChecklistId, cardChecklistIds, currentVisit, loadCandidates]);

  const closeImagePopout = useCallback(() => {
    setImagePopoutOpen(false);
  }, []);

  const openImagePopout = useCallback(() => {
    if (active === null) return;
    setImagePopoutOpen(true);
  }, [active]);

  useEffect(() => {
    if (!imagePopoutOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeImagePopout();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [closeImagePopout, imagePopoutOpen]);

  const switchState = (next: WorkbenchState) => {
    restoredStructuredChoiceFor.current = '';
    setState(next);
    setEditingIdentityFor(null);
    setIdentityDraft(null);
    setIdentityDraftEdited(false);
    setPricingDraft(null);
    setImageIssue(false);
    setPendingParallel('');
    setSelectedChecklistCandidate('');
    setStructuredParallelChoice(null);
    setExplicitSubset(null);
    setListingTitleOverride(null);
    setListingEditLock(null);
    setIdentityCorrectionMode(false);
    setIdentityCorrectionReason('');
    closeImagePopout();
    setNotice(null);
    setCardSuggestionNote(null);
    setCardSuggestionError(false);
  };

  const scheduleAutomaticOcrRetry = (
    card: CatalogListCardRecord,
    completedAttempts: number,
    onRetryReady?: () => void,
  ) => {
    if (
      !shouldRetryAutomaticChecklistSuggestion(card, completedAttempts) ||
      autoOcrRetryTimers.current.has(card.cardId)
    ) {
      return;
    }
    const timer = setTimeout(() => {
      autoOcrRetryTimers.current.delete(card.cardId);
      onRetryReady?.();
      setAutoOcrRetryTick((value) => value + 1);
    }, AUTOMATIC_OCR_SUGGESTION_RETRY_DELAY_MS * completedAttempts);
    autoOcrRetryTimers.current.set(card.cardId, timer);
  };

  const suggestActiveFromOcr = async (
    requestedCard: CatalogListCardRecord,
    automaticAttempt: number,
    forceRefresh = false,
  ) => {
    if (busy !== null || suggestingCardId !== null) return;
    if (hasAssignedHierarchy(requestedCard)) {
      setNotice(
        'Saved checklist choices are preserved. Use Refine matches to choose a different identity.',
      );
      return;
    }
    const visit = reviewVisit.current;
    const request = Symbol(requestedCard.cardId);
    suggestionRequest.current = request;
    const selectionGeneration =
      checklistSelectionGenerations.current.get(requestedCard.cardId) ?? 0;
    const isCurrent = () =>
      reviewVisit.current === visit &&
      suggestionRequest.current === request &&
      (checklistSelectionGenerations.current.get(requestedCard.cardId) ?? 0) ===
        selectionGeneration;
    if (forceRefresh) subsetCatalog.invalidate();
    setSuggestingCardId(requestedCard.cardId);
    setCardSuggestionNote(null);
    setCardSuggestionError(false);
    setError(null);
    setNotice(null);
    let result: Awaited<ReturnType<typeof catalog.checklistResolve>>;
    try {
      result = await catalog.checklistResolve(requestedCard.cardId, {
        suggestProductFromOcr: true,
        ...(forceRefresh ? { refreshProductSuggestion: true } : {}),
      });
    } catch {
      if (!isCurrent()) return;
      setCardSuggestionNote(
        'Could not check the latest checklists. Check your connection and try again.',
      );
      setCardSuggestionError(true);
      scheduleAutomaticOcrRetry(requestedCard, automaticAttempt);
      return;
    } finally {
      if (isCurrent()) setSuggestingCardId(null);
    }
    if (!isCurrent()) return;
    if (!result.ok) {
      setCardSuggestionNote(result.message ?? result.error);
      setCardSuggestionError(true);
      scheduleAutomaticOcrRetry(requestedCard, automaticAttempt);
      return;
    }
    const suggested = result.data.productSuggestion?.candidates[0];
    if (suggested === undefined) {
      autoOcrAttempts.current.set(requestedCard.cardId, AUTOMATIC_OCR_SUGGESTION_MAX_ATTEMPTS);
      if (!forceRefresh) {
        setCardChecklistIds((previous) => {
          const next = { ...previous };
          delete next[requestedCard.cardId];
          return next;
        });
      }
      const ambiguousCandidates = result.data.productSuggestionFailure?.candidates ?? [];
      if (ambiguousCandidates.length >= 2) {
        setCards(
          (previous) =>
            previous?.map((card) =>
              card.cardId === requestedCard.cardId
                ? {
                    ...card,
                    checklistProductSuggestionFailure: result.data.productSuggestionFailure,
                  }
                : card,
            ) ?? previous,
        );
        setCardSuggestionNote(null);
        setCardSuggestionError(false);
        setState('ready');
        setActiveId(requestedCard.cardId);
        await refresh();
        return;
      }
      setCardSuggestionNote(
        checklistSuggestionStatusLabel(
          result.data.productSuggestionStatus,
          activeChecklistId !== null,
          result.data.observation?.label,
          result.data.productSuggestionFailure?.reason,
          requestedCard.marketplaceSource?.title === undefined ? 'ocr' : 'marketplace',
        ),
      );
      setCardSuggestionError(true);
      await refresh();
      return;
    }
    if (
      identityDraftEditedRef.current ||
      latestActive.current?.identity !== undefined ||
      latestActive.current?.identityStatus === 'IDENTIFIED' ||
      latestActive.current?.identityReviewDraft !== undefined
    ) {
      setCardSuggestionNote(
        `Latest checklist match: ${suggested.product.year} ${suggested.product.brand} ${suggested.product.product} · ${identityDraftEditedRef.current ? 'Unsaved identity edits preserved' : 'Saved review content preserved'}`,
      );
      setCardSuggestionError(false);
      return;
    }
    // This accepted response may legitimately move its own card to a new checklist.
    // Preserve its note for that one transition, never for subsequent navigation.
    const acceptedVisitKey = `${batchId}|${assignmentResolutionKey(latestActive.current ?? requestedCard, suggested.checklistId)}|editor:${editorOwner.current.sequence}`;
    if (acceptedVisitKey !== visit.key) {
      acceptedSuggestionTransition.current = {
        fromSequence: visit.sequence,
        toKey: acceptedVisitKey,
      };
    }
    setCardChecklistIds((previous) => ({
      ...previous,
      [requestedCard.cardId]: suggested.checklistId,
    }));
    const resolvedCard: CatalogListCardRecord = {
      ...(latestActive.current ?? requestedCard),
      ...(result.data.productSuggestion === undefined
        ? {}
        : { checklistProductSuggestion: result.data.productSuggestion }),
      ...(result.data.checklistSuggestion === undefined
        ? {}
        : { checklistSuggestion: result.data.checklistSuggestion }),
    };
    setCards(
      (previous) =>
        previous?.map((card) => (card.cardId === requestedCard.cardId ? resolvedCard : card)) ??
        previous,
    );
    const key = assignmentResolutionKey(resolvedCard, suggested.checklistId);
    invalidateVariantOptions();
    const resolvedCandidates = result.data.candidates ?? [];
    const resolvedDefinitions = result.data.parallelDefinitions ?? [];
    setCandidates((previous) => ({
      ...previous,
      [key]: resolvedCandidates,
    }));
    setParallelDefinitions((previous) => ({
      ...previous,
      [key]: resolvedDefinitions,
    }));
    setParallelMatches((previous) => ({
      ...previous,
      [key]: result.data.parallelMatch ?? null,
    }));
    setCardSuggestionNote(
      `${forceRefresh ? 'Latest checklist match' : requestedCard.marketplaceSource?.title === undefined ? 'Suggested' : 'Suggested from eBay listing'}: ${suggested.product.year} ${suggested.product.brand} ${suggested.product.product} (${Math.round(suggested.confidence * 100)}%)${identityDraftEditedRef.current ? ' · Unsaved identity edits preserved' : ''}`,
    );
    setCardSuggestionError(false);
    setState('ready');
    setActiveId(requestedCard.cardId);
    setEditingIdentityFor(requestedCard.cardId);
    const nextIdentity = identityDraftFromOcrResolution(
      resolvedCard,
      suggested,
      resolvedCandidates,
      result.data.evidenceNumber,
    );
    setIdentityDraft(nextIdentity);
    setIdentityDraftEdited(false);
    setImageIssue(resolvedCard.imageQualityStatus === 'REJECTED');
    setPendingParallel('');
    setSelectedChecklistCandidate('');
    setStructuredParallelChoice(null);
    restoredStructuredChoiceFor.current = '';
    setExplicitSubset(null);
    await refresh();
  };

  useEffect(() => {
    if (
      cards === null ||
      !batchChecklistLoaded ||
      batchOcrCatchupRunning.current ||
      batchOcrCatchupAttempted.current.size >= AUTOMATIC_OCR_BATCH_CATCHUP_LIMIT
    ) {
      return;
    }
    const remainingLimit =
      AUTOMATIC_OCR_BATCH_CATCHUP_LIMIT - batchOcrCatchupAttempted.current.size;
    const pending = cardsNeedingAutomaticChecklistCatchup(
      cards,
      batchChecklistId,
      active?.cardId ?? null,
    )
      .filter(
        (card) =>
          !batchOcrCatchupAttempted.current.has(card.cardId) &&
          !autoOcrRetryTimers.current.has(card.cardId) &&
          (autoOcrAttempts.current.get(card.cardId) ?? 0) < AUTOMATIC_OCR_SUGGESTION_MAX_ATTEMPTS,
      )
      .slice(0, remainingLimit);
    if (pending.length === 0) return;

    for (const card of pending) batchOcrCatchupAttempted.current.add(card.cardId);
    batchOcrCatchupRunning.current = true;
    const generation = batchOcrCatchupGeneration.current;
    const queue = [...pending];
    const worker = async () => {
      for (;;) {
        if (batchOcrCatchupGeneration.current !== generation) return;
        const card = queue.shift();
        if (card === undefined) return;
        const attempt = (autoOcrAttempts.current.get(card.cardId) ?? 0) + 1;
        autoOcrAttempts.current.set(card.cardId, attempt);
        batchOcrCatchupInFlight.current.add(card.cardId);
        const selectionGeneration = checklistSelectionGenerations.current.get(card.cardId) ?? 0;
        const result = await settleAutomaticChecklistCatchupRequest(() =>
          catalog.checklistResolve(card.cardId, {
            suggestProductFromOcr: true,
          }),
        ).finally(() => {
          batchOcrCatchupInFlight.current.delete(card.cardId);
        });
        if (batchOcrCatchupGeneration.current !== generation) return;
        if ((checklistSelectionGenerations.current.get(card.cardId) ?? 0) !== selectionGeneration)
          continue;
        if (result === undefined || !result.ok) {
          scheduleAutomaticOcrRetry(card, attempt, () => {
            if (batchOcrCatchupGeneration.current !== generation) return;
            batchOcrCatchupAttempted.current.delete(card.cardId);
            setBatchOcrCatchupTick((value) => value + 1);
          });
          continue;
        }
        const suggested = result.data.productSuggestion?.candidates[0];
        if (suggested === undefined) {
          autoOcrAttempts.current.set(card.cardId, AUTOMATIC_OCR_SUGGESTION_MAX_ATTEMPTS);
          if ((result.data.productSuggestionFailure?.candidates?.length ?? 0) >= 2) {
            setCards(
              (previous) =>
                previous?.map((current) =>
                  current.cardId === card.cardId
                    ? {
                        ...current,
                        checklistProductSuggestionFailure: result.data.productSuggestionFailure,
                      }
                    : current,
                ) ?? previous,
            );
          }
          continue;
        }
        setCardChecklistIds((previous) => ({
          ...previous,
          [card.cardId]: suggested.checklistId,
        }));
        setCards(
          (previous) =>
            previous?.map((current) =>
              current.cardId === card.cardId
                ? {
                    ...current,
                    ...(result.data.productSuggestion === undefined
                      ? {}
                      : { checklistProductSuggestion: result.data.productSuggestion }),
                    ...(result.data.checklistSuggestion === undefined
                      ? {}
                      : { checklistSuggestion: result.data.checklistSuggestion }),
                  }
                : current,
            ) ?? previous,
        );
      }
    };
    void Promise.all(
      Array.from(
        { length: Math.min(AUTOMATIC_OCR_BATCH_CATCHUP_CONCURRENCY, pending.length) },
        worker,
      ),
    ).finally(() => {
      if (batchOcrCatchupGeneration.current !== generation) return;
      batchOcrCatchupRunning.current = false;
      setBatchOcrCatchupTick((value) => value + 1);
    });
  }, [active?.cardId, batchChecklistId, batchChecklistLoaded, batchOcrCatchupTick, cards, catalog]);

  useEffect(() => {
    if (
      active === null ||
      !batchChecklistLoaded ||
      !shouldAutomaticallySuggestChecklist(
        active,
        cardChecklistIds[active.cardId],
        batchChecklistId,
      ) ||
      suggestingCardId !== null ||
      busy !== null ||
      batchOcrCatchupInFlight.current.has(active.cardId) ||
      (batchOcrCatchupRunning.current && batchOcrCatchupAttempted.current.has(active.cardId)) ||
      autoOcrRetryTimers.current.has(active.cardId) ||
      (autoOcrAttempts.current.get(active.cardId) ?? 0) >= AUTOMATIC_OCR_SUGGESTION_MAX_ATTEMPTS
    ) {
      return;
    }
    const attempt = (autoOcrAttempts.current.get(active.cardId) ?? 0) + 1;
    autoOcrAttempts.current.set(active.cardId, attempt);
    autoOcrAttempted.current.add(active.cardId);
    void suggestActiveFromOcr(active, attempt);
  }, [
    active,
    autoOcrRetryTick,
    batchChecklistId,
    batchChecklistLoaded,
    busy,
    cardChecklistIds,
    suggestingCardId,
  ]);

  const resetIdentityReview = () => {
    if (active === null) return;
    const discardOwner = { ...editorOwner.current, sequence: editorOwner.current.sequence + 1 };
    editorOwner.current = discardOwner;
    if (heldChecklistReview) {
      setCardChecklistIds((current) => {
        const next = { ...current };
        delete next[active.cardId];
        return next;
      });
      void refresh(() => editorOwner.current === discardOwner);
    }
    setLocalChecklistReview(null);
    const nextIdentity = identityDraftForCard(active);
    const key = resolveKey(active.cardId);
    const restored = persistedStructuredChecklistChoice(
      active,
      candidates[key] ?? [],
      parallelDefinitions[key] ?? [],
    );
    setAssistedSelectionKey(null);
    setReplacementIntent(null);
    setIdentityDraft(nextIdentity);
    setIdentityDraftEdited(false);
    setPricingDraft(pricingDraftWithBatchDefault(active, nextIdentity, batchDefaultPrice));
    setCardPresentationDraft(presentationDraftForCard(active));
    setItemSpecifics({});
    setItemDetailsMode(null);
    setImageIssue(active.imageQualityStatus === 'REJECTED');
    setPendingParallel('');
    setSelectedChecklistCandidate(restored?.candidateIndex ?? '');
    setStructuredParallelChoice(restored?.parallelChoice ?? null);
    setExplicitSubset(null);
    closeImagePopout();
    setError(null);
    setNotice(null);
  };

  const chooseActiveParallel = (variation: string) => {
    if (!active) return;
    const entry = (candidates[resolveKey(active.cardId)] ?? []).find(
      (candidate) => variationOf(candidate) === variation,
    );
    if (!entry) return;
    const checklistId = effectiveCardChecklistId(
      active,
      cardChecklistIds[active.cardId],
      batchChecklistId,
    );
    const proposed = checklistId
      ? reviewedIdentityFromChecklist(entry, undefined, { card: active, checklistId })
      : null;
    stageChecklistIdentity(proposed, () => {
      setPendingParallel(variation);
      setIdentityDraft(proposed);
      setIdentityDraftEdited(true);
      setError(null);
      setNotice(null);
    });
  };

  const activeResolutionKey = active === null ? '' : resolveKey(active.cardId);
  const activeChecklistId =
    active === null
      ? null
      : effectiveCardChecklistId(active, cardChecklistIds[active.cardId], batchChecklistId);
  const activeUsesBatchChecklist =
    active !== null &&
    active.identityReviewDraft?.importedFrom === undefined &&
    cardChecklistIds[active.cardId] === undefined &&
    batchChecklistId !== null;
  const activeChecklistIsSourceBacked =
    (activeUsesBatchChecklist && batchChecklistSourceBacked) ||
    (activeChecklistId !== null &&
      (checklists ?? []).some(
        (checklist) =>
          checklist.checklistId === activeChecklistId && checklist.source !== undefined,
      ));
  const subsetCatalog = useChecklistSubsets(
    catalog.checklistResolve,
    active?.cardId ?? null,
    activeChecklistId,
    activeChecklistIsSourceBacked && !manualOverrideEnabled,
    {
      visit: currentVisit.sequence,
      subset:
        explicitSubset?.key === activeResolutionKey
          ? explicitSubset.subset
          : (identityDraft?.subset ?? ''),
      number: identityDraft?.number ?? '',
    },
  );
  const activeCandidates = active === null ? [] : (candidates[activeResolutionKey] ?? []);
  const activeChecklistSubsetChoices = checklistSubsetChoices(activeCandidates);
  const chosenSubset =
    explicitSubset?.key === activeResolutionKey
      ? explicitSubset
      : savedHierarchy?.subset
        ? {
            key: activeResolutionKey,
            subset: savedHierarchy.subset.name,
            definitionId: savedHierarchy.subset.definitionId,
            sourceFamilyId: savedHierarchy.subset.sourceFamilyId,
            matched: false,
          }
        : null;
  const resolvedParallelMatch =
    active === null ? undefined : (parallelMatches[activeResolutionKey] ?? undefined);
  const activeParallelMatch: ChecklistParallelMatch | undefined =
    chosenSubset === null
      ? resolvedParallelMatch
      : chosenSubset.matched
        ? { tier: 1, confidence: 'exact-roster-match', subsets: [chosenSubset.subset] }
        : { tier: 2, confidence: 'subset-matched-card-not-in-roster', subset: chosenSubset.subset };
  const resolvedDefinitions =
    active === null ? [] : (parallelDefinitions[activeResolutionKey] ?? []);
  const allSubsetGroups =
    subsetCatalog.groups.length > 0
      ? subsetCatalog.groups
      : resolvedParallelMatch?.tier === 3
        ? resolvedParallelMatch.groups
        : [];
  const mergedSubsetDefinitions = [
    ...new Map(
      [...resolvedDefinitions, ...allSubsetGroups.flatMap((group) => group.definitions)].map(
        (definition) => [definition.definitionId, definition],
      ),
    ).values(),
  ];
  const explicitlySelectedEntry =
    chosenSubset !== null && selectedChecklistCandidate !== ''
      ? activeCandidates[Number(selectedChecklistCandidate)]
      : undefined;
  const activeParallelDefinitions =
    chosenSubset === null
      ? resolvedDefinitions
      : explicitlySelectedEntry !== undefined
        ? applicableParallelDefinitions(
            explicitlySelectedEntry,
            mergedSubsetDefinitions.filter(
              (definition) =>
                !explicitlySelectedEntry.source?.sourceFamilyId ||
                !definition.source.familyId ||
                explicitlySelectedEntry.source.sourceFamilyId === definition.source.familyId,
            ),
          )
        : subsetDefinitionsForCard(
            mergedSubsetDefinitions,
            chosenSubset.subset,
            identityDraft?.number ?? '',
          );
  currentSubsetScope.current = `${currentVisit.sequence}|${activeResolutionKey}|${identityDraft?.number ?? ''}`;
  useEffect(() => {
    subsetChoiceRequest.current += 1;
    setNumberLookupBusy(false);
    setBusy((current) => (current === 'subset' ? null : current));
    return () => {
      subsetChoiceRequest.current += 1;
    };
  }, [currentVisit.sequence, activeResolutionKey, identityDraft?.number]);
  const activeRankedChecklistCandidates = active === null ? [] : rankedChecklistCandidates(active);
  const activeRankedChecklistId = activeRankedChecklistCandidates.some(
    (candidate) => candidate.checklistId === activeChecklistId,
  )
    ? (activeChecklistId ?? '')
    : '';
  useEffect(() => {
    if (
      active === null ||
      activeCandidates.length === 0 ||
      identityDraftEdited ||
      restoredStructuredChoiceFor.current === activeResolutionKey
    ) {
      return;
    }
    const restored = persistedStructuredChecklistChoice(
      active,
      activeCandidates,
      activeParallelDefinitions,
    );
    if (restored === null) return;
    if (
      savedHierarchy?.parallel &&
      restored.parallelChoice !==
        (savedHierarchy.parallel.kind === 'NONE'
          ? NO_PARALLEL_DEFINITION_ID
          : savedHierarchy.parallel.definitionId)
    )
      return;
    restoredStructuredChoiceFor.current = activeResolutionKey;
    setSelectedChecklistCandidate(restored.candidateIndex);
    setStructuredParallelChoice(restored.parallelChoice);
  }, [
    active,
    activeCandidates,
    activeParallelDefinitions,
    activeResolutionKey,
    identityDraftEdited,
    savedHierarchy,
  ]);
  const activeChecklist =
    activeChecklistId === null
      ? undefined
      : (checklists ?? []).find((checklist) => checklist.checklistId === activeChecklistId);
  const listingCopyContextKey = JSON.stringify([
    batchId,
    active?.cardId ?? '',
    activeChecklistId,
    activeChecklist ?? null,
    manualOverrideEnabled,
    requestedListingTemplateId,
    listingCopyRefreshGeneration,
  ]);
  const bulkReviewKey = listingCopyContextKey;
  const listingCopyRestorationIncomplete =
    listingContextLoading ||
    (active !== null &&
      (researchCopyReadyFor !== active.cardId ||
        listingCopyRestoredGeneration !== listingCopyRefreshGeneration ||
        listingCopyReadyContext !== listingCopyContextKey ||
        bulkReviewReadyKey !== bulkReviewKey));
  const listingCopyRestorationPending =
    importedListingReloadPending || listingCopyRestorationIncomplete;
  useEffect(() => {
    if (
      activeChecklistId === null ||
      requestedChecklistProducts.current.has(activeChecklistId) ||
      (checklists ?? []).some((checklist) => checklist.checklistId === activeChecklistId)
    ) {
      return;
    }
    requestedChecklistProducts.current.add(activeChecklistId);
    let live = true;
    void catalog.reviewChecklists({ checklistId: activeChecklistId }).then((result) => {
      if (live && result.ok) {
        setChecklists((current) => mergeChecklistProducts(current ?? [], result.data.items));
      }
    });
    return () => {
      live = false;
    };
  }, [activeChecklistId, catalog, checklists]);
  useEffect(() => {
    let live = true;
    setResearchCopyReadyFor(null);
    setListingTemplates([]);
    setAssignedListingTemplateCount(0);
    setListingTemplateSource('NONE');
    setSelectedListingTemplateId('');
    setGeneratedDescription('');
    setDescriptionSourceTitle('');
    setDescriptionUsesTemplateDefault(false);
    setListingTitleOverride(null);
    setItemSpecifics({});
    setItemSpecificsCategory(sellerHubCategoryForChecklist(activeChecklist));
    setItemDetailsMode(null);
    if (active === null) {
      setListingContextLoading(false);
      return () => {
        live = false;
      };
    }
    if (manualOverrideEnabled) {
      setListingContextLoading(true);
      void Promise.all([
        marketplace.listListingTemplates(),
        marketplace.loadListingCopy(active.cardId),
      ]).then(([templatesResult, copyResult]) => {
        if (!live) return;
        setListingContextLoading(false);
        if (!templatesResult.ok) {
          setError(templatesResult.message ?? templatesResult.error);
          return;
        }
        const defaultTemplate = templatesResult.data.templates.find(
          (template) =>
            template.templateId === templatesResult.data.defaultTemplateId &&
            template.status === 'ACTIVE',
        );
        const effectiveTemplates = defaultTemplate === undefined ? [] : [defaultTemplate];
        setListingTemplates(effectiveTemplates);
        setListingTemplateSource(defaultTemplate === undefined ? 'NONE' : 'DEFAULT');
        setSelectedListingTemplateId(defaultTemplate?.templateId ?? '');
        if (!copyResult.ok) {
          setError(copyResult.message ?? copyResult.error);
          return;
        }
        setResearchCopyReadyFor(active.cardId);
        setListingCopyRestoredGeneration(listingCopyRefreshGeneration);
        setListingCopyReadyContext(listingCopyContextKey);
        const savedCopy = copyResult.data.item;
        setListingEditLock(copyResult.data.editLock ?? null);
        const identity = identityDraftForCard(active);
        setGeneratedDescription(
          listingDescriptionForTemplate(savedCopy?.description, defaultTemplate, identity, active),
        );
        setDescriptionUsesTemplateDefault(
          !savedCopy?.description?.trim() && defaultTemplate !== undefined,
        );
        setDescriptionSourceTitle(savedCopy?.description?.trim() ? savedCopy.title : '');
        if (savedCopy !== null) {
          setListingTitleOverride(savedCopy.title);
          setCardPresentationDraft(savedCopy.cardPresentation ?? presentationDraftForCard(active));
          const category = savedSellerHubCategory(
            savedCopy,
            sellerHubCategoryForIdentity(identity),
          );
          setItemSpecificsCategory(category);
          setItemSpecifics(
            itemSpecificsDraftForReview(savedCopy.itemSpecifics, identity, category),
          );
        } else {
          const category = sellerHubCategoryForIdentity(identity);
          setItemSpecificsCategory(category);
          setItemSpecifics(itemSpecificsDraftForReview(undefined, identity, category));
        }
      });
      return () => {
        live = false;
      };
    }
    if (activeChecklistId === null) {
      setListingContextLoading(true);
      void marketplace.loadListingCopy(active.cardId).then((copyResult) => {
        if (!live) return;
        setListingContextLoading(false);
        if (!copyResult.ok) {
          setError(copyResult.message ?? copyResult.error);
          return;
        }
        setResearchCopyReadyFor(active.cardId);
        setListingCopyRestoredGeneration(listingCopyRefreshGeneration);
        setListingCopyReadyContext(listingCopyContextKey);
        setListingEditLock(copyResult.data.editLock ?? null);
      });
      return () => {
        live = false;
      };
    }
    setListingContextLoading(true);
    void Promise.all([
      marketplace.getListingReviewContext(activeChecklistId),
      marketplace.loadListingCopy(active.cardId),
    ]).then(([result, copyResult]) => {
      if (!live) return;
      setListingContextLoading(false);
      if (!result.ok) {
        setError(result.message ?? result.error);
        return;
      }
      setAssignedListingTemplateCount(result.data.assignedTemplateCount);
      setListingTemplateSource(result.data.templateSource);
      setListingTemplates(result.data.templates);
      setSelectedListingTemplateId(result.data.templates[0]?.templateId ?? '');
      if (!copyResult.ok) {
        setError(
          `Listing templates loaded, but saved listing copy could not be restored: ${
            copyResult.message ?? copyResult.error
          }`,
        );
        return;
      }
      setResearchCopyReadyFor(active.cardId);
      setListingCopyRestoredGeneration(listingCopyRefreshGeneration);
      setListingCopyReadyContext(listingCopyContextKey);
      const savedCopy = copyResult.data.item;
      setListingEditLock(copyResult.data.editLock ?? null);
      const selectedTemplateId = result.data.templates.some(
        (template) => template.templateId === requestedListingTemplateId,
      )
        ? requestedListingTemplateId
        : savedCopy !== null &&
            result.data.templates.some((template) => template.templateId === savedCopy.templateId)
          ? savedCopy.templateId
          : (result.data.templates[0]?.templateId ?? '');
      setSelectedListingTemplateId(selectedTemplateId);
      const selectedTemplate = result.data.templates.find(
        (template) => template.templateId === selectedTemplateId,
      );
      setGeneratedDescription(
        listingDescriptionForTemplate(
          savedCopy?.description,
          selectedTemplate,
          identityDraftForCard(active),
          active,
        ),
      );
      setDescriptionUsesTemplateDefault(
        !savedCopy?.description?.trim() && selectedTemplate !== undefined,
      );
      setDescriptionSourceTitle(savedCopy?.description?.trim() ? savedCopy.title : '');
      if (savedCopy !== null) {
        setListingTitleOverride(savedCopy.title);
        setCardPresentationDraft(savedCopy.cardPresentation ?? presentationDraftForCard(active));
        const category = savedSellerHubCategory(
          savedCopy,
          activeChecklist === undefined
            ? sellerHubCategoryForIdentity(identityDraftForCard(active))
            : sellerHubCategoryForChecklist(activeChecklist),
        );
        setItemSpecificsCategory(category);
        setItemSpecifics(
          itemSpecificsDraftForReview(
            savedCopy.itemSpecifics,
            identityDraftForCard(active),
            category,
          ),
        );
      } else {
        const category =
          activeChecklist === undefined
            ? sellerHubCategoryForIdentity(identityDraftForCard(active))
            : sellerHubCategoryForChecklist(activeChecklist);
        setItemSpecificsCategory(category);
        setItemSpecifics(
          itemSpecificsDraftForReview(undefined, identityDraftForCard(active), category),
        );
      }
    });
    return () => {
      live = false;
    };
  }, [
    active?.cardId,
    activeChecklist,
    activeChecklistId,
    manualOverrideEnabled,
    marketplace,
    requestedListingTemplateId,
    listingCopyRefreshGeneration,
    listingCopyContextKey,
  ]);
  const selectedListingTemplate = listingTemplates.find(
    (template) => template.templateId === selectedListingTemplateId,
  );
  const autoGeneratedListingTitle =
    active === null || identityDraft === null || selectedListingTemplate === undefined
      ? ''
      : listingTitleForReview(
          selectedListingTemplate,
          identityDraft,
          active,
          cardPresentationDraft,
        );
  const currentTemplateDefaultDescription =
    active === null || identityDraft === null || selectedListingTemplate === undefined
      ? ''
      : listingDescriptionForTemplate(
          undefined,
          selectedListingTemplate,
          identityDraft,
          active,
          cardPresentationDraft,
        );
  useEffect(() => {
    if (descriptionUsesTemplateDefault) setGeneratedDescription(currentTemplateDefaultDescription);
  }, [currentTemplateDefaultDescription, descriptionUsesTemplateDefault]);
  const listingTitle = listingTitleOverride ?? autoGeneratedListingTitle;
  const ccgPolicyContext = JSON.stringify([
    currentVisit.sequence,
    active?.cardId,
    claims?.sub,
    listingCopyContextKey,
    active?.identity,
    active?.checklistSourceLineage,
    active?.manualIdentityOverride,
    active?.importedIdentification,
  ]);
  const ccgPolicy = useCcgPolicy(
    itemSpecificsCategory === 'CCG_SINGLES' && active !== null,
    ccgPolicyContext,
    marketplace.getEbayCcgCategoryPolicy,
  );
  useEffect(() => setCcgStatusNotice(null), [ccgPolicyContext]);
  const currentCcgPolicy =
    ccgPolicy.state.status === 'AVAILABLE' ? ccgPolicy.state.policy : undefined;
  const ccgScope = ccgReviewScopeKey({
    cardId: active?.cardId ?? '',
    contextKey: JSON.stringify([
      ccgPolicyContext,
      identityDraft,
      manualOverrideEnabled,
      manualSourceSelection,
      listingTitle,
      generatedDescription,
    ]),
    policyReviewIdentity: currentCcgPolicy
      ? ebayCategoryPolicyReviewIdentity(currentCcgPolicy.binding)
      : '',
    specifics: itemSpecifics,
    presentation: cardPresentationDraft,
    templateId: selectedListingTemplateId,
  });
  const ccgCurrentScope = useRef(ccgScope);
  ccgCurrentScope.current = ccgScope;
  const ccgOperationKey = JSON.stringify([
    claims?.sub,
    batchId,
    currentVisit.sequence,
    active?.cardId,
    listingCopyContextKey,
    active?.identity,
    active?.checklistSourceLineage,
    active?.manualIdentityOverride,
    active?.importedIdentification,
    currentCcgPolicy ? ebayCategoryPolicyReviewIdentity(currentCcgPolicy.binding) : '',
  ]);
  const ccgOperationEpoch = useRef({ key: ccgOperationKey, marketplace, catalog, value: 0 });
  if (
    ccgOperationEpoch.current.key !== ccgOperationKey ||
    ccgOperationEpoch.current.marketplace !== marketplace ||
    ccgOperationEpoch.current.catalog !== catalog
  )
    ccgOperationEpoch.current = {
      key: ccgOperationKey,
      marketplace,
      catalog,
      value: ccgOperationEpoch.current.value + 1,
    };
  const ccgEpoch = ccgOperationEpoch.current.value;
  const ccgBusyOwner = useRef<{ epoch: number; token: symbol } | null>(null);
  useEffect(() => {
    if (ccgBusyOwner.current && ccgBusyOwner.current.epoch !== ccgOperationEpoch.current.value) {
      ccgBusyOwner.current = null;
      setBusy(null);
      setImageAdjustmentBusySide(null);
    }
  }, [ccgEpoch]);
  useEffect(
    () => () => {
      ++ccgOperationEpoch.current.value;
      ccgBusyOwner.current = null;
    },
    [],
  );
  const beginCcgOperation = () => {
    const owner = { epoch: ++ccgOperationEpoch.current.value, token: Symbol() };
    ccgBusyOwner.current = owner;
    return {
      current: () => ccgOperationEpoch.current.value === owner.epoch,
      finish: () => {
        if (ccgBusyOwner.current === owner) {
          ccgBusyOwner.current = null;
          if (ccgOperationEpoch.current.value === owner.epoch) setBusy(null);
        }
      },
    };
  };
  const ccgSetSpecifics = (next: EbayItemSpecifics) => {
    setCcgDecisions((current) => ({
      scope: ccgScope,
      fields: Object.fromEntries(
        Object.entries(current.fields).filter(
          ([name, d]) =>
            next[name]?.[0] === d.value &&
            JSON.stringify(next[name]) === JSON.stringify(itemSpecifics[name]),
        ),
      ),
    }));
    setItemSpecifics(next);
  };
  const ccgReviewForAction = (): MarketplaceCcgReviewRequest => {
    if (!active || !currentCcgPolicy)
      throw new Error('Reload the current CCG policy and review the details.');
    const fields: Partial<Record<'Finish' | 'Rarity', CcgFieldDecision>> = {};
    for (const name of ['Finish', 'Rarity'] as const) {
      const value = itemSpecifics[name]?.[0];
      if (value === undefined) continue;
      const d = ccgDecisions.fields[name];
      if (d?.origin === 'IMPORTED_RESEARCH') {
        if (
          !d.confirmed ||
          ccgDecisions.scope !== ccgScope ||
          d.value !== value ||
          active.importedIdentification?.listing?.itemSpecifics?.[name] !== value
        )
          throw new Error(`Use current research ${name} again, or edit it for manual review.`);
        fields[name] = d;
      } else fields[name] = { origin: 'MANUAL_REVIEW', value, confirmed: true };
    }
    return serializeCcgReview(
      {
        cardId: active.cardId,
        contextKey: ccgScope,
        policyReviewIdentity: ebayCategoryPolicyReviewIdentity(currentCcgPolicy.binding),
        detailsConfirmed: true,
        specifics: fields,
      },
      itemSpecifics,
      currentCcgPolicy,
      ccgCurrentScope.current,
    );
  };
  const generatedDescriptionIsStale =
    generatedDescription !== '' &&
    descriptionSourceTitle !== '' &&
    descriptionSourceTitle !== listingTitle;
  useEffect(() => {
    if (active?.listingPrice?.researchQuery !== undefined || identityDraft === null) return;
    setPricingDraft((current) =>
      current === null
        ? current
        : { ...current, query: cardResearchQuery(identityDraft, cardPresentationDraft) },
    );
  }, [active?.cardId, active?.listingPrice?.researchQuery, cardPresentationDraft, identityDraft]);
  const structuredResolutionPending =
    active !== null && activeChecklistIsSourceBacked && resolving.has(active.cardId);
  const selectedCandidateIndex =
    chosenSubset === null &&
    !hasAssignedHierarchy(active) &&
    activeChecklistSubsetChoices.length === 1 &&
    active?.identityStatus !== 'IDENTIFIED' &&
    active?.identityReviewDraft === undefined
      ? String(activeChecklistSubsetChoices[0]!.index)
      : selectedChecklistCandidate;
  const selectedChecklistEntry =
    selectedCandidateIndex === '' ? undefined : activeCandidates[Number(selectedCandidateIndex)];
  const currentSubset =
    chosenSubset?.subset ??
    (identityDraft?.subset?.trim() || undefined) ??
    (selectedChecklistEntry === undefined
      ? activeParallelMatch?.tier === 2
        ? activeParallelMatch.subset
        : ''
      : checklistRosterSubset(selectedChecklistEntry));
  const importedSubsetDefinitions = subsetCatalog.subsetDefinitions.filter(
    (definition) => definition.definitionId,
  );
  const subsetOptions =
    importedSubsetDefinitions.length > 0
      ? importedSubsetDefinitions.map((definition) => ({
          value: definition.definitionId,
          label: `${definition.name}${definition.serialTo === undefined ? '' : ` · /${definition.serialTo}`}`,
        }))
      : allSubsetOptions(allSubsetGroups, activeCandidates, currentSubset);
  if (
    savedHierarchy?.subset &&
    !subsetOptions.some((option) => option.value === savedHierarchy.subset!.definitionId)
  )
    subsetOptions.push({
      value: savedHierarchy.subset.definitionId,
      label: `${savedHierarchy.subset.name} (saved choice)`,
    });
  const selectedSubsetValue =
    chosenSubset?.definitionId ??
    (importedSubsetDefinitions.length > 0
      ? importedSubsetDefinitions.find(
          (definition) => subsetKey(definition.name) === subsetKey(currentSubset),
        )?.definitionId
      : subsetOptions.find((option) => subsetKey(option.value) === subsetKey(currentSubset))
          ?.value) ??
    '';
  const selectedSubsetRosterChoices = activeChecklistSubsetChoices.filter(
    ({ entry }) =>
      subsetKey(checklistRosterSubset(entry)) === subsetKey(currentSubset) &&
      (!chosenSubset?.sourceFamilyId ||
        !entry.source?.sourceFamilyId ||
        chosenSubset.sourceFamilyId === entry.source.sourceFamilyId),
  );

  const applicableDefinitions =
    selectedChecklistEntry === undefined
      ? []
      : applicableParallelDefinitions(selectedChecklistEntry, activeParallelDefinitions);
  const displayedParallelOptions = parallelSelectionOptions(
    applicableDefinitions,
    selectedChecklistEntry,
  );
  const selectedParallelDefinition =
    structuredParallelChoice === null || structuredParallelChoice === NO_PARALLEL_DEFINITION_ID
      ? undefined
      : applicableDefinitions.find(
          (definition) => definition.definitionId === structuredParallelChoice,
        );
  const selectedChecklistSourceEntry = checklistEntryForParallelChoice(
    selectedChecklistEntry,
    activeCandidates,
    selectedParallelDefinition,
  );
  const unresolvedSelection = unresolvedSourceEvidenceSelection(
    activeChecklistId,
    selectedChecklistSourceEntry,
  );
  const manualEvidenceSelection = manualOverrideEnabled
    ? manualSourceSelection
    : unresolvedSelection;
  const unresolvedClassification =
    selectedChecklistSourceEntry?.unresolvedClassification ??
    (manualSourceSelection === undefined
      ? undefined
      : (active?.identityReviewDraft?.manualIdentityOverrideSourceEvidence
          ?.unresolvedClassification ??
        active?.manualIdentityOverride?.sourceEvidence?.unresolvedClassification));
  const unresolvedVerificationBlocked =
    manualEvidenceSelection !== undefined &&
    (!manualOverrideEnabled || !manualClassificationConfirmed);
  const activeChecklistSourceSelection =
    (assistedSelectionKey === activeResolutionKey && structuredParallelChoice === null) ||
    manualOverrideEnabled ||
    activeChecklistId === null ||
    identityDraft === null ||
    (active?.identityStatus === 'IDENTIFIED' &&
      !identityDraftEdited &&
      active.identityReviewDraft?.checklistSourceSelection == null &&
      cardIdentitiesEqual(active.identity, identityDraft))
      ? null
      : checklistSourceSelectionForReview(
          activeChecklistId,
          selectedChecklistSourceEntry,
          identityDraft,
          selectedParallelDefinition,
          active ?? undefined,
        );
  const storedChecklistSourceSelection =
    (assistedSelectionKey === activeResolutionKey && structuredParallelChoice === null) ||
    manualOverrideEnabled ||
    active === null ||
    activeChecklistId === null ||
    identityDraft === null
      ? null
      : storedChecklistSourceSelectionForReview(active, activeChecklistId, identityDraft);
  const retainVerifiedIdentity =
    assistedSelectionKey !== activeResolutionKey &&
    canRetainVerifiedIdentity(
      active,
      identityDraft,
      activeChecklistId,
      activeChecklistSourceSelection,
      manualOverrideEnabled,
      manualOverrideReason,
    );
  const comparableSourceSelection =
    activeChecklistSourceSelection ??
    (active &&
    activeChecklistId &&
    identityDraft &&
    !manualOverrideEnabled &&
    assistedSelectionKey !== activeResolutionKey
      ? storedPricingSourceSelectionForReview(active, activeChecklistId, identityDraft)
      : null);
  const structuredSelectionRequired =
    activeParallelDefinitions.length > 0 || assistedSelectionKey === activeResolutionKey;
  const activeParallelSelectionComplete =
    (assistedSelectionKey !== activeResolutionKey || structuredParallelChoice !== null) &&
    structuredParallelSelectionComplete(activeParallelDefinitions, structuredParallelChoice);
  const showStructuredSelectors = structuredSelectionRequired || activeChecklistIsSourceBacked;
  const activeReviewCondition =
    active === null
      ? null
      : reviewCondition(
          active,
          activeChecklistId !== null,
          structuredSelectionRequired,
          activeChecklistSubsetChoices.length,
        );
  const pricingAmountMinor =
    pricingDraft === null || pricingDraft.price.trim() === ''
      ? undefined
      : usdInputToCents(pricingDraft.price);
  const pricingDraftIsValid =
    pricingDraft === null || pricingDraft.price.trim() === '' || pricingAmountMinor !== null;
  const batchDefaultPriceAmountMinor =
    batchDefaultPrice.trim() === '' ? undefined : usdInputToCents(batchDefaultPrice);
  const batchDefaultPriceIsValid =
    batchDefaultPrice.trim() === '' || batchDefaultPriceAmountMinor !== null;
  const updateBatchDefaultPrice = (nextPrice: string) => {
    if (listingCopyRestorationPending) return;
    const previousPrice = batchDefaultPrice.trim();
    setBatchDefaultPrice(nextPrice);
    setPricingDraft((current) => {
      if (current === null || active === null || active.listingPrice !== undefined) return current;
      const currentPrice = current.price.trim();
      if (currentPrice !== '' && currentPrice !== previousPrice) return current;
      if (nextPrice.trim() === '') return { ...current, price: '' };
      if (usdInputToCents(nextPrice) === null) return current;
      return { ...current, price: nextPrice.trim() };
    });
  };
  const applySelectedBatchChecklist = async () => {
    if (batchChecklistDraftId === '' || batchChecklistBusy) return;
    setBatchChecklistBusy(true);
    setBatchError(null);
    setBatchNotice(null);
    const result = await catalog.applyBatchChecklist(batchId, batchChecklistDraftId);
    if (!result.ok) {
      setBatchChecklistBusy(false);
      setBatchError(result.message ?? result.error);
      return;
    }
    const summary = result.data.summary;
    setBatchChecklistId(batchChecklistDraftId);
    setBatchChecklistSourceBacked(result.data.context?.source !== undefined);
    batchOcrCatchupGeneration.current += 1;
    batchOcrCatchupAttempted.current.clear();
    batchOcrCatchupInFlight.current.clear();
    batchOcrCatchupRunning.current = false;
    await refresh();
    setBatchChecklistBusy(false);
    setBatchNotice(
      summary === undefined
        ? 'Batch checklist applied to current unreviewed cards.'
        : `Batch checklist applied: ${summary.suggested} suggested, ${summary.parallelChoices} need a parallel, ${summary.noNumber + summary.noMatch} need manual review, ${summary.skippedReviewed} reviewed preserved.`,
    );
  };
  const identitySerialIsValid = identityDraft === null || cardIdentitySerialIsValid(identityDraft);
  const listingReadinessBlockers: string[] = [];
  if (pendingChecklistIdentity !== null)
    listingReadinessBlockers.push(
      'Review and accept or cancel the proposed checklist identity changes.',
    );
  if (listingCopyRestorationPending)
    listingReadinessBlockers.push('Wait for the saved card condition and listing copy to reload.');
  if (
    !retainVerifiedIdentity &&
    !replacesAssignment &&
    !sourceHonorsAssignment(
      assignment,
      activeChecklistSourceSelection ?? storedChecklistSourceSelection,
    )
  )
    listingReadinessBlockers.push(
      'Choose an exact card matching the saved Set, Subset and Parallel. Use Refine matches or manual override to replace those choices.',
    );
  if (
    active?.reviewChecklistSelection?.requiresReview &&
    !active.reviewChecklistSelection.selectedAt
  )
    listingReadinessBlockers.push(
      'Reload this card to confirm its saved checklist assignment before listing.',
    );
  if (
    !(
      (retainVerifiedIdentity &&
        (manualOverrideEnabled || active?.checklistSourceLineage?.status === 'VERIFIED')) ||
      (manualOverrideEnabled
        ? manualOverrideReason.trim().length >= 5
        : activeChecklistId !== null && activeChecklistSourceSelection !== null)
    )
  )
    listingReadinessBlockers.push(
      manualOverrideEnabled
        ? 'Add a manual override reason (at least 5 characters).'
        : 'Select a checklist identity.',
    );
  if (cardPresentationDraft === undefined)
    listingReadinessBlockers.push('Choose the card’s condition.');
  if (selectedListingTemplate === undefined)
    listingReadinessBlockers.push('Choose a listing template.');
  if (listingTitle.trim() === '') listingReadinessBlockers.push('Enter a listing title.');
  if (listingTitle.length > EBAY_LISTING_BASE_TITLE_MAX_LENGTH)
    listingReadinessBlockers.push(
      `Shorten the listing title to ${EBAY_LISTING_BASE_TITLE_MAX_LENGTH} characters or fewer.`,
    );
  if (pricingAmountMinor === undefined || pricingAmountMinor === null)
    listingReadinessBlockers.push('Enter a valid listing price.');
  if (imageIssue) listingReadinessBlockers.push('Resolve the image issue before listing.');
  if (itemSpecificsCategory === null)
    listingReadinessBlockers.push('Choose a listing category explicitly.');
  if (itemSpecificsCategory === 'CCG_SINGLES') {
    if (!currentCcgPolicy) listingReadinessBlockers.push('Reload the current CCG policy.');
    else {
      try {
        listingReadinessBlockers.push(
          ...validateCcgLocalSpecifics(currentCcgPolicy, itemSpecifics),
        );
        const compatibility = ccgPresentationCompatibility(currentCcgPolicy, cardPresentationDraft);
        if (!compatibility.ready) listingReadinessBlockers.push(compatibility.message);
      } catch {
        listingReadinessBlockers.push('Reload the current CCG policy.');
      }
    }
  }
  const listingReadinessIsValid = listingReadinessBlockers.length === 0;
  const comparableIdentityKey =
    identityDraft === null
      ? ''
      : [
          identityDraft.category,
          identityDraft.year,
          identityDraft.set,
          identityDraft.name,
          identityDraft.number,
          identityDraft.variation ?? '',
        ]
          .map((value) => value?.trim() ?? '')
          .join('|');
  const comparableSourceKey = checklistComparableKey(comparableSourceSelection) ?? '';

  useEffect(() => {
    if (
      reviewPanelTab !== 'pricing' ||
      active === null ||
      identityDraft === null ||
      identityDraft.category.trim() === '' ||
      identityDraft.set.trim() === '' ||
      identityDraft.number.trim() === '' ||
      comparableSourceSelection === null
    ) {
      setComparables(null);
      setComparablesLoading(false);
      setComparablesError(null);
      return;
    }
    let live = true;
    let inFlight = false;
    setComparables(null);
    setComparablesLoading(true);
    setComparablesError(null);
    const loadComparables = (showLoading: boolean) => {
      if (inFlight || !live) return;
      inFlight = true;
      if (showLoading) setComparablesLoading(true);
      void marketplace
        .getCardComparables(
          active.cardId,
          identityDraft,
          cardPresentationDraft,
          comparableSourceSelection,
          batchId,
        )
        .then((result) => {
          if (!live) return;
          if (!result.ok) {
            setComparablesError(result.message ?? result.error);
            return;
          }
          setComparablesError(null);
          setComparables(result.data);
        })
        .finally(() => {
          inFlight = false;
          if (live) setComparablesLoading(false);
        });
    };
    const timer = window.setTimeout(() => loadComparables(true), 200);
    const refreshTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible') loadComparables(false);
    }, 60_000);
    const refreshOnFocus = () => loadComparables(false);
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      live = false;
      window.clearTimeout(timer);
      window.clearInterval(refreshTimer);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [
    active?.cardId,
    batchId,
    comparableIdentityKey,
    comparableSourceKey,
    cardPresentationDraft,
    marketplace,
    reviewPanelTab,
  ]);

  // A render-time scope fence rejects responses even before effect cleanup runs.
  const ebayLookupScope = JSON.stringify([
    claims?.sub,
    batchId,
    currentVisit.sequence,
    active?.cardId,
    identityDraft,
    comparableSourceSelection,
    cardPresentationDraft,
    itemSpecificsCategory,
  ]);
  const ebayOwner = useRef({ scope: ebayLookupScope, marketplace, epoch: 0 });
  if (ebayOwner.current.scope !== ebayLookupScope || ebayOwner.current.marketplace !== marketplace)
    ebayOwner.current = { scope: ebayLookupScope, marketplace, epoch: ebayOwner.current.epoch + 1 };
  const ebayTabEpoch = useRef({ tab: reviewPanelTab, epoch: 0 });
  if (ebayTabEpoch.current.tab !== reviewPanelTab)
    ebayTabEpoch.current = { tab: reviewPanelTab, epoch: ebayTabEpoch.current.epoch + 1 };
  const [ebayObservation, setEbayObservation] = useState<{
    owner: number;
    request: number;
    scope: string;
    treatment?: string;
  } | null>(null);
  const latestEbayObservation = useRef(ebayObservation);
  latestEbayObservation.current = ebayObservation;
  const visibleEbayObservation =
    ebayObservation?.owner === ebayOwner.current.epoch && ebayObservation.scope === ebayLookupScope
      ? ebayObservation
      : null;
  const currentEbayLookupScope = useRef(ebayLookupScope);
  currentEbayLookupScope.current = ebayLookupScope;
  const currentPricingTab = useRef(reviewPanelTab);
  currentPricingTab.current = reviewPanelTab;
  const ebayLookupInFlight = useRef<number | null>(null);
  const canLookupEbayPrices =
    active !== null &&
    identityDraft !== null &&
    identityDraft.set.trim() !== '' &&
    identityDraft.name.trim() !== '' &&
    comparableSourceSelection !== null &&
    itemSpecificsCategory !== null;

  useEffect(() => {
    ++ebayActiveComparablesRequest.current;
    ebayLookupInFlight.current = null;
    setEbayActiveComparables(null);
    setEbayActiveComparablesLoading(false);
    setEbayActiveComparablesError(null);
    return () => {
      ++ebayActiveComparablesRequest.current;
      ebayLookupInFlight.current = null;
    };
  }, [ebayLookupScope, marketplace]);

  // Hide or reopen the tab without discarding already fetched evidence for this card.
  // Pending requests are invalidated so hidden-tab responses cannot revive stale work.
  useEffect(() => {
    ++ebayActiveComparablesRequest.current;
    ebayLookupInFlight.current = null;
    setEbayActiveComparablesLoading(false);
  }, [reviewPanelTab]);

  const lookupEbayActiveComparables = async (forceRefresh = false) => {
    if (
      reviewPanelTab !== 'pricing' ||
      currentEbayLookupScope.current !== ebayLookupScope ||
      !canLookupEbayPrices ||
      active === null ||
      identityDraft === null ||
      comparableSourceSelection === null ||
      ebayLookupInFlight.current !== null ||
      (forceRefresh && ebayActiveComparables === null)
    )
      return;
    const owner = ebayOwner.current.epoch,
      tabEpoch = ebayTabEpoch.current.epoch;
    const requestId = ++ebayActiveComparablesRequest.current;
    setEbayActiveComparables(null);
    setEbayObservation(null);
    latestEbayObservation.current = null;
    ebayLookupInFlight.current = requestId;
    const isCurrent = () =>
      ebayOwner.current.epoch === owner &&
      ebayTabEpoch.current.epoch === tabEpoch &&
      currentPricingTab.current === 'pricing' &&
      currentEbayLookupScope.current === ebayLookupScope &&
      ebayActiveComparablesRequest.current === requestId;
    setEbayActiveComparablesLoading(true);
    setEbayActiveComparablesError(null);
    try {
      const result = await marketplace.getLiveEbayComparables(
        active.cardId,
        identityDraft,
        cardPresentationDraft,
        comparableSourceSelection,
        itemSpecificsCategory!,
        forceRefresh,
      );
      if (!isCurrent()) return;
      if (!result.ok) {
        setEbayActiveComparablesError(result.message ?? result.error);
        return;
      }
      setEbayObservation({
        owner,
        request: requestId,
        scope: ebayLookupScope,
        treatment: requestedResearchTreatment(identityDraft),
      });
      setEbayActiveComparables(result.data);
    } catch {
      if (isCurrent())
        setEbayActiveComparablesError('eBay active pricing is temporarily unavailable. Try again.');
    } finally {
      if (isCurrent()) {
        ebayLookupInFlight.current = null;
        setEbayActiveComparablesLoading(false);
      }
    }
  };

  const reportChecklistIssue = async () => {
    if (
      !active ||
      checklistIssueInFlight.current ||
      checklistIssueDelivery?.status === 'UNKNOWN' ||
      checklistIssueDelivery?.status === 'SENT'
    )
      return;
    if (checklistIssueNote.trim().length > 1000) {
      setChecklistIssueDelivery({
        status: 'NOT_SENT',
        message:
          'Not sent: report details exceed 1,000 characters. Shorten these report details; your original manual reason is unchanged.',
      });
      return;
    }
    const visit = currentVisit;
    const token = ++checklistIssueRequest.current;
    const live = () => reviewVisit.current === visit && checklistIssueRequest.current === token;
    const context = {
      ...(activeChecklistId ? { checklistId: activeChecklistId } : {}),
      ...(activeChecklist
        ? {
            checklistLabel: `${activeChecklist.product.year} ${activeChecklist.product.brand} ${activeChecklist.product.product}`,
          }
        : {}),
      ...(identityDraft?.number.trim() ? { cardNumber: identityDraft.number.trim() } : {}),
      ...(identityDraft?.subset?.trim() ? { subset: identityDraft.subset.trim() } : {}),
      ...(identityDraft?.variation?.trim() ? { parallel: identityDraft.variation.trim() } : {}),
    };
    checklistIssueInFlight.current = true;
    setChecklistIssueBusy(true);
    setChecklistIssueDelivery(null);
    try {
      const result = await catalog.reportChecklistIssue({
        issueType: checklistIssueType,
        cardId: active.cardId,
        batchId,
        ...(checklistIssueNote.trim() ? { note: checklistIssueNote.trim() } : {}),
        context,
      });
      if (!live()) return;
      if (!result.ok) {
        setChecklistIssueDelivery({
          status: result.status >= 400 && result.status < 500 ? 'NOT_SENT' : 'UNKNOWN',
          message:
            result.status >= 400 && result.status < 500
              ? 'Not sent: the issue service rejected this report. Your details and manual identity remain unchanged.'
              : 'Delivery unknown. Check the engineering issue queue before sending another report; your details are preserved.',
        });
        return;
      }
      const issue = result.data?.issue;
      if (
        !issue ||
        typeof issue.issueId !== 'string' ||
        !issue.issueId.trim() ||
        typeof issue.issueKey !== 'string' ||
        !issue.issueKey.trim() ||
        issue.cardId !== active.cardId ||
        issue.batchId !== batchId ||
        issue.issueType !== checklistIssueType ||
        (issue.note ?? '') !== checklistIssueNote.trim() ||
        !issue.context ||
        Object.entries(context).some(
          ([key, value]) => issue.context[key as keyof typeof context] !== value,
        )
      ) {
        setChecklistIssueDelivery({
          status: 'UNKNOWN',
          message:
            'Delivery unknown: no matching issue receipt was returned. Check the engineering issue queue before sending again. Your details are preserved.',
        });
        return;
      }
      setChecklistIssueDelivery({
        status: 'SENT',
        message: `Checklist issue sent. Receipt ${issue.issueId} (${issue.issueKey}). This report is separate from identity Save.`,
      });
    } catch {
      if (live())
        setChecklistIssueDelivery({
          status: 'UNKNOWN',
          message:
            'Delivery unknown. Check the engineering issue queue before sending another report; your details are preserved.',
        });
    } finally {
      if (live()) {
        checklistIssueInFlight.current = false;
        setChecklistIssueBusy(false);
      }
    }
  };

  useEffect(() => {
    if (!checklistIssueOpen || active === null || activeChecklistId === null) {
      setChecklistIssueGuidance([]);
      setChecklistIssueGuidanceLoading(false);
      return;
    }
    const cardNumber = identityDraft?.number.trim();
    let cancelled = false;
    setChecklistIssueGuidanceLoading(true);
    void catalog
      .checklistIssueGuidance({
        checklistId: activeChecklistId,
        cardId: active.cardId,
        ...(cardNumber ? { cardNumber } : {}),
      })
      .then((result) => {
        if (cancelled) return;
        setChecklistIssueGuidance(result.ok ? result.data.items : []);
      })
      .finally(() => {
        if (!cancelled) setChecklistIssueGuidanceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, activeChecklistId, catalog, checklistIssueOpen, identityDraft?.number]);

  const generateDescription = async () => {
    if (
      itemSpecificsCategory === null ||
      itemSpecificsCategory === 'CCG_SINGLES' ||
      active === null ||
      (!manualOverrideEnabled && activeChecklistId === null) ||
      identityDraft === null ||
      selectedListingTemplate === undefined ||
      listingTitle === '' ||
      generatingDescription
    )
      return;
    const visit = reviewVisit.current;
    setGeneratingDescription(true);
    setError(null);
    let result: Awaited<ReturnType<typeof marketplace.generateListingDescription>>;
    try {
      result = await marketplace.generateListingDescription({
        operation: 'GENERATE_DESCRIPTION',
        cardId: active.cardId,
        identitySource: manualOverrideEnabled ? 'MANUAL_OVERRIDE' : 'CHECKLIST',
        ...(activeChecklistId === null || manualOverrideEnabled
          ? {}
          : { checklistId: activeChecklistId }),
        templateId: selectedListingTemplate.templateId,
        title: listingTitle,
        identity: identityDraft,
        ...(active.sku === undefined ? {} : { sku: active.sku }),
        ...(active.shortCode === undefined ? {} : { shortCode: active.shortCode }),
      });
    } catch {
      if (reviewVisit.current === visit) {
        setGeneratingDescription(false);
        setError('Could not generate the description. Try again.');
      }
      return;
    }
    if (reviewVisit.current !== visit) return;
    if (!result.ok) {
      if (
        isListingTemplateNotAssignedFailure(result) &&
        activeChecklistId !== null &&
        !manualOverrideEnabled
      ) {
        const refreshed = await marketplace.getListingReviewContext(activeChecklistId);
        if (reviewVisit.current !== visit) return;
        setGeneratingDescription(false);
        if (!refreshed.ok) {
          setError(refreshed.message ?? refreshed.error);
          return;
        }
        setAssignedListingTemplateCount(refreshed.data.assignedTemplateCount);
        setListingTemplateSource(refreshed.data.templateSource);
        setListingTemplates(refreshed.data.templates);
        const nextTemplateId = listingTemplateIdAfterRefresh(
          selectedListingTemplateId,
          refreshed.data.templates,
        );
        setSelectedListingTemplateId(nextTemplateId);
        setGeneratedDescription(
          listingDescriptionForTemplate(
            undefined,
            refreshed.data.templates.find((template) => template.templateId === nextTemplateId),
            identityDraft,
            active,
          ),
        );
        setDescriptionUsesTemplateDefault(nextTemplateId !== '');
        setDescriptionSourceTitle('');
        setError(
          'The checklist template assignment changed. Listing templates were refreshed; review the current template and try again.',
        );
        return;
      }
      setGeneratingDescription(false);
      setError(result.message ?? result.error);
      return;
    }
    setGeneratingDescription(false);
    setGeneratedDescription(result.data.description);
    setDescriptionUsesTemplateDefault(false);
    setDescriptionSourceTitle(listingTitle);
    if (result.data.generationSource === 'DETERMINISTIC_FALLBACK') {
      setNotice(
        'AI description capacity is temporarily unavailable. A facts-only description was generated instead.',
      );
    }
  };

  const checklistChoiceScope = JSON.stringify([
    currentVisit.sequence,
    activeChecklistId,
    active?.identity,
    active?.identityReviewDraft,
    identityDraft,
    activeCandidates.map((entry) => [entry.source, entry.key, entry.selectionPolicy]),
    resolvedDefinitions,
    manualOverrideEnabled,
    manualOverrideReason,
    manualSourceSelection,
    cardPresentationDraft,
    pricingDraft,
    listingTitleOverride,
    generatedDescription,
    itemSpecifics,
    itemSpecificsCategory,
    selectedListingTemplateId,
  ]);
  useEffect(() => {
    setChecklistIdentityAccepted(false);
  }, [checklistChoiceScope]);
  const proposedVariantIdentity =
    selectedChecklistSourceEntry && active && activeChecklistId
      ? reviewedIdentityFromChecklist(selectedChecklistSourceEntry, selectedParallelDefinition, {
          card: active,
          checklistId: activeChecklistId,
        })
      : null;
  const stagedVariant =
    pendingChecklistIdentity?.scope === checklistChoiceScope &&
    proposedVariantIdentity !== null &&
    cardIdentitiesEqual(pendingChecklistIdentity.proposed, proposedVariantIdentity);
  const variantSource =
    stagedVariant && selectedChecklistSourceEntry && activeChecklistId
      ? checklistSelectionSource(
          activeChecklistId,
          selectedChecklistSourceEntry,
          selectedParallelDefinition,
        )
      : (activeChecklistSourceSelection ?? storedChecklistSourceSelection);
  const variantEntry = selectedChecklistSourceEntry;
  const showVariant =
    !manualOverrideEnabled &&
    !cardLookupOpen &&
    pendingAssistedTreatments === null &&
    (!pendingChecklistIdentity || stagedVariant) &&
    busy === null &&
    !subsetCatalog.loading &&
    !structuredResolutionPending &&
    !numberLookupBusy &&
    suggestingCardId !== active?.cardId &&
    !listingCopyRestorationPending &&
    !importedListingReloadPending &&
    ccgCopyUnknownCard !== active?.cardId &&
    ccgReadyUnknownCard !== active?.cardId &&
    variantObservation?.owner === variantOwner.current.epoch &&
    variantObservation.request === variantRequests.current &&
    variantObservation.checklistId === activeChecklistId &&
    variantEntry !== undefined &&
    variantObservation.rows.includes(variantEntry) &&
    variantSource !== null;
  const variantAssociation = {
    cardId: active?.cardId ?? '',
    visitId: String(variantOwner.current.epoch),
    requestId: String(variantRequests.current),
    checklistId: activeChecklistId ?? '',
    printedNumber: variantEntry?.identity.number ?? '',
  };
  const variantWarning =
    showVariant && variantObservation ? (
      <div>
        {stagedVariant ? (
          <p>
            Catalog choice being reviewed; identity changes still need your existing acceptance and
            save.
          </p>
        ) : null}
        <ChecklistVariantWarning
          entry={variantEntry}
          definitions={variantObservation.definitions}
          selectedSource={variantSource}
          current={variantAssociation}
          loaded={{
            ...variantAssociation,
            visitId: String(variantObservation.owner),
            requestId: String(variantObservation.request),
            checklistId: variantObservation.checklistId,
          }}
        />
      </div>
    ) : null;
  const latestChecklistChoiceScope = useRef(checklistChoiceScope);
  latestChecklistChoiceScope.current = checklistChoiceScope;
  const latestChecklistLocalEdits = useRef<() => boolean>(() => false);
  latestChecklistLocalEdits.current = () => {
    const baseline = bulkReviewBaseline.current;
    if (!baseline || baseline.key !== bulkReviewKey || listingCopyRestorationPending) return true;
    const before = JSON.parse(baseline.snapshot) as {
      pricingDraft: CardResearchPricingDraft | null;
    };
    const beforeListing = JSON.parse(baseline.listingSnapshot) as Record<string, unknown>;
    const pricing = pricingDraft && { ...pricingDraft };
    if (
      pricing &&
      before.pricingDraft &&
      identityDraft &&
      pricing.query === cardResearchQuery(identityDraft, cardPresentationDraft)
    )
      pricing.query = before.pricingDraft.query;
    const listing = {
      cardPresentationDraft,
      itemSpecifics,
      itemSpecificsCategory,
      selectedListingTemplateId,
      generatedDescription: descriptionUsesTemplateDefault
        ? beforeListing.generatedDescription
        : generatedDescription,
      listingTitleOverride,
    };
    return (
      JSON.stringify(pricing) !== JSON.stringify(before.pricingDraft) ||
      JSON.stringify(listing) !== baseline.listingSnapshot
    );
  };
  const stageChecklistIdentity = (proposed: CardIdentity | null, commit: () => void) => {
    setChecklistIdentityAccepted(false);
    if (latestChecklistLocalEdits.current()) {
      setPendingChecklistIdentity(null);
      setError(
        'Save or discard unsaved listing, condition or pricing edits before changing checklist identity. Your edits are preserved.',
      );
      return;
    }

    if (proposed === null) {
      setPendingChecklistIdentity(null);
      setError(
        'Source policy is unavailable or this treatment conflicts. Choose an eligible treatment, retry the source lookup, or use an explicit manual override. Your identity is unchanged.',
      );
      return;
    }
    setPendingChecklistIdentity({ scope: checklistChoiceScope, proposed, commit });
    setError(null);
  };
  const lookupCardNumber = async (number: string) => {
    if (
      active === null ||
      activeChecklistId === null ||
      identityDraft === null ||
      number.trim() === '' ||
      numberLookupBusy ||
      busy !== null
    ) {
      return;
    }
    const variantRead = beginVariantOptions(active.cardId);
    const lookupScope = currentSubsetScope.current;
    const lookupRequest = ++subsetChoiceRequest.current;
    setNumberLookupBusy(true);
    setError(null);
    setNotice(null);
    let result: Awaited<ReturnType<typeof catalog.checklistResolve>>;
    try {
      result = await catalog.checklistResolve(active.cardId, {
        checklistId: activeChecklistId,
        searchNumber: number.trim(),
        ...(savedHierarchy?.subset ? { subset: savedHierarchy.subset.name } : {}),
      });
    } catch {
      if (
        lookupScope === currentSubsetScope.current &&
        lookupRequest === subsetChoiceRequest.current
      ) {
        setNumberLookupBusy(false);
        setError('Could not look up the card number. Your draft is unchanged; try again.');
      }
      return;
    }
    if (lookupScope !== currentSubsetScope.current || lookupRequest !== subsetChoiceRequest.current)
      return;
    setNumberLookupBusy(false);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }

    const resolved = result.data.candidates ?? [];
    const definitions = result.data.parallelDefinitions ?? [];
    variantRead.publish(activeChecklistId, resolved, definitions);
    setCandidates((previous) => ({ ...previous, [activeResolutionKey]: resolved }));
    setParallelDefinitions((previous) => ({
      ...previous,
      [activeResolutionKey]: definitions,
    }));
    setParallelMatches((previous) => ({
      ...previous,
      [activeResolutionKey]: result.data.parallelMatch ?? null,
    }));
    setExplicitSubset(null);
    setPendingParallel('');

    setSelectedChecklistCandidate('');
    setStructuredParallelChoice(null);
    setPendingChecklistIdentity(null);
    setChecklistIdentityAccepted(false);
    setNotice(
      resolved.length
        ? `${resolved.length} current source rows found. Choose the exact row and treatment to review its full identity changes.`
        : `No exact roster row was found for printed number ${number.trim()}. Use explicit manual review if needed.`,
    );
  };

  const chooseChecklistCandidate = (index: string) => {
    setPendingChecklistIdentity(null);
    setChecklistIdentityAccepted(false);
    const entry = index === '' ? undefined : activeCandidates[Number(index)];
    if (!entry || !active || activeChecklistId === null) return;
    // Choosing a row may expose its eligible treatments; it does not accept identity fields.
    setSelectedChecklistCandidate(index);
    setStructuredParallelChoice(null);
    setPendingParallel('');
    if (activeChecklistIsSourceBacked)
      setExplicitSubset({
        key: activeResolutionKey,
        subset: checklistRosterSubset(entry),
        matched: true,
      });
    const proposed = reviewedIdentityFromChecklist(entry, undefined, {
      card: active,
      checklistId: activeChecklistId,
    });
    stageChecklistIdentity(proposed, () => {
      setIdentityDraft(proposed);
      setIdentityDraftEdited(true);
      setStructuredParallelChoice(
        defaultParallelChoiceForSubset(checklistRosterSubset(entry), entry),
      );
    });
  };
  const chooseStructuredParallel = (definitionId: string) => {
    setPendingChecklistIdentity(null);
    setChecklistIdentityAccepted(false);
    if (!active || activeChecklistId === null || !selectedChecklistEntry) {
      setError(
        'No exact roster row is available. These definitions are research only; use an explicit manual override for independently reviewed facts.',
      );
      return;
    }
    if (definitionId === '') {
      setStructuredParallelChoice(null);
      return;
    }
    const definition =
      definitionId === NO_PARALLEL_DEFINITION_ID
        ? undefined
        : applicableDefinitions.find((item) => item.definitionId === definitionId);
    if (definitionId !== NO_PARALLEL_DEFINITION_ID && !definition) return;
    const entry = checklistEntryForParallelChoice(
      selectedChecklistEntry,
      activeCandidates,
      definition,
    );
    if (!entry) return;
    const proposed = reviewedIdentityFromChecklist(entry, definition, {
      card: active,
      checklistId: activeChecklistId,
    });
    stageChecklistIdentity(proposed, () => {
      setStructuredParallelChoice(definitionId);
      setIdentityDraft(proposed);
      setIdentityDraftEdited(true);
      setError(null);
      setNotice(null);
    });
  };

  const persistCardChecklistSelection = async (
    cardId: string,
    selectedChecklistId: string,
    effectiveChecklistId: string | null,
  ) => {
    const selectionGeneration = (checklistSelectionGenerations.current.get(cardId) ?? 0) + 1;
    checklistSelectionGenerations.current.set(cardId, selectionGeneration);
    const selectionOwner = editorOwner.current;
    const visitSequence = reviewVisit.current.sequence;
    const expectedKey = `${batchId}|${cardId}|${effectiveChecklistId ?? ''}`;
    const result = await catalog
      .checklistResolve(cardId, {
        ...(effectiveChecklistId === null ? {} : { checklistId: effectiveChecklistId }),
        reviewChecklistId: selectedChecklistId === '' ? null : selectedChecklistId,
      })
      .catch(() => ({
        ok: false as const,
        error: 'checklist_selection_failed',
        message:
          'Checklist selection could not be confirmed. Your local identity edits are preserved.',
      }));
    if (
      editorOwner.current !== selectionOwner ||
      (checklistSelectionGenerations.current.get(cardId) ?? 0) !== selectionGeneration
    )
      return;
    const ownsFeedback =
      (reviewVisit.current.key === expectedKey ||
        reviewVisit.current.key.startsWith(`${expectedKey}|`)) &&
      reviewVisit.current.sequence <= visitSequence + 1;
    if (!result.ok) {
      if (ownsFeedback) setError(result.message ?? result.error);
      // The selection request is separate from final identity Save. A failed metadata
      // request cannot revoke the human's accepted local proposal.
      await refresh(() => editorOwner.current === selectionOwner);
      return;
    }
    setCards(
      (previous) =>
        previous?.map((card) =>
          card.cardId !== cardId
            ? card
            : {
                ...cardAfterChecklistChange(card),
                reviewChecklistSelection:
                  result.data.reviewChecklistSelection === undefined
                    ? reviewSelectionAfterChecklistChange(
                        card,
                        selectedChecklistId,
                        (checklists ?? []).find(
                          (item) => item.checklistId === effectiveChecklistId,
                        ),
                      )
                    : (result.data.reviewChecklistSelection ?? undefined),
                ...(result.data.checklistSuggestion === undefined
                  ? {}
                  : { checklistSuggestion: result.data.checklistSuggestion }),
                ...(result.data.checklistSourceLineage == null
                  ? {}
                  : { checklistSourceLineage: result.data.checklistSourceLineage }),
              },
        ) ?? previous,
    );
  };

  const applyAssistedCardMatch = async (
    selected: CardLookupItem,
    request: CardLookupRequest,
    panelStillCurrent: () => boolean,
  ): Promise<void> => {
    if (active === null || identityDraft === null || listedCardReviewLocked || busy !== null) {
      throw new Error('This card cannot be changed right now.');
    }
    const variantRead = beginVariantOptions(active.cardId);
    const visit = reviewVisit.current;
    const token = ++lookupApplyRequest.current;
    const draft = latestLookupDraft.current;
    const stillCurrent = () =>
      panelStillCurrent() &&
      reviewVisit.current === visit &&
      lookupApplyRequest.current === token &&
      latestLookupDraft.current.identityDraft === draft.identityDraft &&
      latestLookupDraft.current.manualOverrideEnabled === draft.manualOverrideEnabled &&
      latestLookupDraft.current.selectedChecklistCandidate === draft.selectedChecklistCandidate &&
      latestLookupDraft.current.structuredParallelChoice === draft.structuredParallelChoice;
    const validated = await catalog.checklistCardSearch({
      ...request,
      validateResultId: selected.resultId,
    });
    if (!stillCurrent())
      throw new Error('The card or lookup changed. Review the current draft and try again.');
    if (!validated.ok) throw new Error(validated.message ?? validated.error);
    const match = validated.data.items.length === 1 ? validated.data.items[0] : undefined;
    if (match === undefined || match.resultId !== selected.resultId) {
      throw new Error(
        'This checklist row changed or is no longer available. Search again before applying it.',
      );
    }
    const number = match.entry.identity.number?.trim() || match.entry.key.number?.trim();
    if (!number || !match.entry.source?.snapshotFingerprint || !match.entry.source.sourceItemId) {
      throw new Error(
        'This row cannot be linked to a current numbered source. Choose another match.',
      );
    }
    const resolved = await catalog.checklistResolve(active.cardId, {
      checklistId: match.checklist.checklistId,
      searchNumber: number,
      tentativeChecklist: true,
    });
    if (!stillCurrent())
      throw new Error('The card or lookup changed. Review the current draft and try again.');
    if (!resolved.ok) throw new Error(resolved.message ?? resolved.error);
    const rows = resolved.data.candidates ?? [];
    variantRead.publish(match.checklist.checklistId, rows, resolved.data.parallelDefinitions ?? []);
    const exact = rows.filter(
      (entry) =>
        entry.source !== undefined &&
        entry.source.sourceSystem === match.entry.source!.sourceSystem &&
        entry.source.sourceTargetId === match.entry.source!.sourceTargetId &&
        entry.source.sourceItemId === match.entry.source!.sourceItemId &&
        entry.source.snapshotFingerprint === match.entry.source!.snapshotFingerprint,
    );
    if (exact.length !== 1)
      throw new Error('This checklist row changed. Search again before applying it.');
    const choice = exact[0]!.unresolvedClassification
      ? { entry: exact[0]!, index: rows.indexOf(exact[0]!) }
      : checklistSubsetChoiceForEntry(rows, exact[0]!);
    if (choice === undefined)
      throw new Error('The current checklist row could not be selected. Search again.');
    const adoptLookupIdentity = (
      nextIdentity: CardIdentity,
      selectedTreatment: string | null = null,
    ) => {
      if (!stillCurrent()) {
        setError('The card or lookup changed. Search again before accepting this identity.');
        return;
      }
      const key = assignmentResolutionKey(active, match.checklist.checklistId);
      checklistSelectionGenerations.current.set(
        active.cardId,
        (checklistSelectionGenerations.current.get(active.cardId) ?? 0) + 1,
      );
      checklistResolutionGeneration.current += 1;
      subsetChoiceRequest.current += 1;
      restoredStructuredChoiceFor.current = key;
      setChecklists((current) => mergeChecklistProducts(current ?? [], [match.checklist]));
      setCardChecklistIds((current) => ({
        ...current,
        [active.cardId]: match.checklist.checklistId,
      }));
      setCandidates((current) => ({ ...current, [key]: rows }));
      setParallelDefinitions((current) => ({
        ...current,
        [key]: resolved.data.parallelDefinitions ?? [],
      }));
      setParallelMatches((current) => ({ ...current, [key]: resolved.data.parallelMatch ?? null }));
      setResolutionErrors((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      setExplicitSubset({ key, subset: checklistRosterSubset(choice.entry), matched: true });
      setSelectedChecklistCandidate(String(choice.index));
      setStructuredParallelChoice(selectedTreatment);
      setPendingParallel('');
      setAssistedSelectionKey(key);
      setReplacementIntent(
        active.reviewChecklistSelection
          ? {
              cardId: active.cardId,
              revision: {
                checklistId: active.reviewChecklistSelection.checklistId,
                selectedAt: active.reviewChecklistSelection.selectedAt,
              },
              kind: 'REFINE',
            }
          : null,
      );
      setIdentityDraft(nextIdentity);
      setIdentityDraftEdited(true);
      setManualOverrideEnabled(false);
      setManualSourceSelection(undefined);
      setManualOverrideReason('');
      setManualClassificationConfirmed(false);
      setCardLookupOpen(false);
      setError(null);
      setNotice(null);
    };
    const nextIdentity = choice.entry.unresolvedClassification
      ? { ...choice.entry.identity }
      : reviewedIdentityFromChecklist(choice.entry, undefined, {
          card: active,
          checklistId: match.checklist.checklistId,
        });
    if (nextIdentity === null) {
      const definitions = resolved.data.parallelDefinitions ?? [];
      const choices = parallelSelectionOptions(definitions, choice.entry).flatMap((option) => {
        const definition = definitions.find((item) => item.definitionId === option.value);
        if (!definition) return [];
        const entry = checklistEntryForParallelChoice(choice.entry, rows, definition);
        if (!entry) return [];
        const proposed = reviewedIdentityFromChecklist(entry, definition, {
          card: active,
          checklistId: match.checklist.checklistId,
        });
        return proposed
          ? [
              {
                id: option.value,
                label: option.label,
                choose: () => {
                  if (!stillCurrent()) {
                    setError(
                      'The card or lookup changed. Search again before choosing a treatment.',
                    );
                    return;
                  }
                  setPendingAssistedTreatments(null);
                  stageChecklistIdentity(proposed, () =>
                    adoptLookupIdentity(proposed, option.value),
                  );
                },
              },
            ]
          : [];
      });
      if (choices.length === 0)
        throw new Error(
          'This row has no eligible treatment or current source policy. Retry source options or use explicit manual review; no identity was accepted.',
        );
      setPendingAssistedTreatments({ scope: checklistChoiceScope, choices });
      setNotice(
        'Choose an eligible treatment for this match. The current identity is unchanged until you review and accept all proposed changes.',
      );
      return;
    }
    setPendingAssistedTreatments(null);
    stageChecklistIdentity(nextIdentity, () =>
      adoptLookupIdentity(
        nextIdentity,
        defaultParallelChoiceForSubset(checklistRosterSubset(choice.entry), choice.entry),
      ),
    );
  };

  const selectChecklistForActiveCard = (checklistId: string) => {
    if (active === null || active.reviewChecklistSelection?.checklistId === checklistId) return;
    const selectedChecklistId = checklistId === '' ? batchChecklistId : checklistId;
    const selectedChecklist = (checklists ?? []).find(
      (candidate) => candidate.checklistId === selectedChecklistId,
    );
    const nextIdentity =
      selectedChecklist === undefined
        ? identityDraftForCard(active)
        : identityDraftForChecklist(
            identityDraft ?? identityDraftForCard(active),
            selectedChecklist,
          );
    stageChecklistIdentity(nextIdentity, () => {
      setLocalChecklistReview({
        owner: editorOwner.current,
        cardId: active.cardId,
        queue: state,
        index: Math.max(
          0,
          visible.findIndex((card) => card.cardId === active.cardId),
        ),
      });
      setCardChecklistIds((previous) => {
        const next = { ...previous };
        if (checklistId === '') delete next[active.cardId];
        else next[active.cardId] = checklistId;
        return next;
      });
      setCards(
        (previous) =>
          previous?.map((card) =>
            card.cardId !== active.cardId
              ? card
              : {
                  ...cardAfterChecklistChange(card),
                  reviewChecklistSelection: reviewSelectionAfterChecklistChange(
                    card,
                    checklistId,
                    selectedChecklist,
                  ),
                },
          ) ?? previous,
      );
      setCandidates({});
      setParallelDefinitions({});
      setParallelMatches({});
      setEditingIdentityFor(active.cardId);
      setIdentityDraft(nextIdentity);
      setIdentityDraftEdited(true);
      setPendingParallel('');
      setSelectedChecklistCandidate('');
      setStructuredParallelChoice(null);
      setExplicitSubset(null);
      setCardSuggestionNote(null);
      void persistCardChecklistSelection(active.cardId, checklistId, selectedChecklistId);
    });
  };

  const chooseChecklistSubset = async (value: string) => {
    const definition = importedSubsetDefinitions.find(
      (candidate) => candidate.definitionId === value,
    );
    const subset = definition?.name ?? value;
    if (
      active === null ||
      activeChecklistId === null ||
      identityDraft === null ||
      busy !== null ||
      numberLookupBusy
    )
      return;
    if (!subsetOptions.some((option) => option.value === value)) return;
    if (value === selectedSubsetValue && selectedChecklistEntry !== undefined) return;
    const scope = currentSubsetScope.current;
    const request = ++subsetChoiceRequest.current;
    const stillCurrent = () =>
      currentSubsetScope.current === scope && subsetChoiceRequest.current === request;
    let roster = activeCandidates;
    const matching = (entries: readonly ChecklistEntry[]) =>
      checklistSubsetChoices(entries).filter(
        ({ entry }) => subsetKey(checklistRosterSubset(entry)) === subsetKey(subset),
      );
    if (matching(roster).length === 0 && identityDraft.number.trim() !== '') {
      const variantRead = beginVariantOptions(active.cardId);
      setBusy('subset');
      setError(null);
      try {
        const result = await catalog.checklistResolve(active.cardId, {
          checklistId: activeChecklistId,
          searchNumber: identityDraft.number.trim(),
          subset,
        });
        if (!stillCurrent()) return;
        if (!result.ok) throw new Error(result.message ?? result.error);
        variantRead.publish(
          activeChecklistId,
          result.data.candidates ?? [],
          result.data.parallelDefinitions ?? [],
        );
        // Preserve original row/index identity while adding genuine rows from the read-only number lookup.
        const key = (entry: ChecklistEntry) =>
          JSON.stringify([entry.source, entry.key, entry.identity]);
        const rows = new Map(roster.map((entry) => [key(entry), entry]));
        for (const entry of result.data.candidates ?? [])
          if (!rows.has(key(entry))) rows.set(key(entry), entry);
        roster = [...rows.values()];
        setCandidates((previous) => ({ ...previous, [activeResolutionKey]: roster }));
        setParallelDefinitions((previous) => ({
          ...previous,
          [activeResolutionKey]: [
            ...new Map(
              [
                ...(previous[activeResolutionKey] ?? []),
                ...(result.data.parallelDefinitions ?? []),
              ].map((definition) => [definition.definitionId, definition]),
            ).values(),
          ],
        }));
      } catch {
        if (stillCurrent())
          setError(
            'Could not check this subset against the card number. Your current selection is unchanged. Select the subset again to retry.',
          );
        return;
      } finally {
        if (subsetChoiceRequest.current === request)
          setBusy((current) => (current === 'subset' ? null : current));
      }
    }
    if (!stillCurrent()) return;
    const choices = matching(roster);
    const sameName = choices.filter(
      ({ entry }) => subsetKey(entry.identity.name) === subsetKey(identityDraft.name),
    );
    const choice =
      choices.length === 1 ? choices[0] : sameName.length === 1 ? sameName[0] : undefined;
    setExplicitSubset({
      key: activeResolutionKey,
      subset,
      definitionId: definition?.definitionId,
      sourceFamilyId: definition?.source.familyId,
      matched: choices.length > 0,
    });
    setSelectedChecklistCandidate(choice === undefined ? '' : String(choice.index));
    setStructuredParallelChoice(null);
    setPendingParallel('');
    setPendingChecklistIdentity(null);
    if (!choice) {
      setError(
        'No exact roster row is available for this subset and printed number. Definitions are research only; use explicit manual review. Your identity is unchanged.',
      );
      return;
    }
    setNotice(
      'Choose the exact card and eligible treatment to review its full identity changes. The current identity is unchanged.',
    );
  };

  const updateImageIssue = async (checked: boolean, approveOnly = false) => {
    if (active === null || busy !== null || imageAdjustmentBusySide !== null) return;
    if (!checked && !photoReview.ready) return;
    if (
      !checked &&
      photoReview.required &&
      (imageRotations.front !== 0 || imageRotations.back !== 0)
    ) {
      setError('Save image corrections and review the updated images before clearing the issue.');
      return;
    }
    if (
      approveOnly &&
      (workbenchState(active) !== 'awaiting-images' ||
        !displayedImagesLoaded ||
        imageRotations.front !== 0 ||
        imageRotations.back !== 0 ||
        (active.imageUrls?.front === undefined && active.imageUrls?.back === undefined))
    )
      return;
    const visit = reviewVisit.current;
    const update = imageQualityUpdateForIssue(checked);
    setBusy('image-quality');
    setError(null);
    setNotice(null);
    const result = await catalog.updateImageQuality(
      active.cardId,
      update.status,
      update.rejectedReason,
      ...(photoReview.reviewId === undefined ? [] : [photoReview.reviewId]),
    );
    setBusy(null);
    if (reviewVisit.current !== visit || currentReviewBatch.current !== batchId) return;
    if (photoReview.required) photoReview.reload();
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    const updatedCard: CatalogListCardRecord = {
      ...active,
      imageQualityStatus: result.data.imageQualityStatus,
      imageQualityReviewedAt: result.data.imageQualityReviewedAt,
      ...(checked
        ? { imageQualityRejectedReason: update.rejectedReason }
        : { imageQualityRejectedReason: undefined }),
    };
    setCards(
      (previous) =>
        previous?.map((card) => (card.cardId === active.cardId ? updatedCard : card)) ?? previous,
    );
    setState(workbenchState(updatedCard));
    setActiveId(active.cardId);
    setImageIssue(checked);
    setNotice(
      checked
        ? 'Image issue saved. This card is now in Image Issues.'
        : approveOnly
          ? 'Images approved. Identification and listing details are unchanged.'
          : 'Image issue cleared.',
    );
  };

  const listingPriceForReview = () =>
    pricingDraft === null || pricingAmountMinor === undefined || pricingAmountMinor === null
      ? undefined
      : {
          amountMinor: pricingAmountMinor,
          currency: 'USD' as const,
          ...(pricingDraft.query.trim() === '' ? {} : { researchQuery: pricingDraft.query.trim() }),
          ...(pricingDraft.notes.trim() === '' ? {} : { notes: pricingDraft.notes.trim() }),
        };

  const persistPendingImageCorrections = async (
    cardId: string,
    accept: () => boolean = () => true,
  ): Promise<boolean> => {
    const pendingSides = (['front', 'back'] as const).filter((side) => imageRotations[side] !== 0);
    if (pendingSides.length === 0) return false;

    const freshCard = await catalog.get(cardId);
    if (!accept()) return false;
    if (!freshCard.ok) throw new Error(freshCard.message ?? freshCard.error);
    try {
      for (const side of pendingSides) {
        if (!accept()) return false;
        setImageAdjustmentBusySide(side);
        const sourceUrl = photoReview.required
          ? active?.imageUrls?.[side]
          : freshCard.data.imageUrls?.[side];
        if (sourceUrl === undefined) {
          throw new Error(`${side === 'front' ? 'Front' : 'Back'} image is no longer available.`);
        }
        let blob: Blob;
        try {
          blob = await rotateReviewImageToJpeg(sourceUrl, imageRotations[side]);
        } catch {
          throw new Error(
            `Could not load the ${side} image for correction. Please retry the adjustment.`,
          );
        }
        if (!accept()) return false;
        const file = new File([blob], `${cardId}-${side}-corrected.jpg`, {
          type: 'image/jpeg',
        });
        const upload = await catalog.createImageReplacementUpload(cardId, side, file);
        if (!accept()) return false;
        if (!upload.ok) throw new Error(upload.message ?? upload.error);
        const form = new FormData();
        for (const [key, value] of Object.entries(upload.data.uploadUrl.fields)) {
          form.append(key, value);
        }
        form.append('file', file);
        let uploadResponse: Response;
        try {
          uploadResponse = await fetch(upload.data.uploadUrl.url, {
            method: 'POST',
            body: form,
          });
        } catch {
          throw new Error('Could not upload the corrected image. Please retry.');
        }
        if (!accept()) return false;
        if (!uploadResponse.ok) throw new Error(`upload_failed_${uploadResponse.status}`);
        const replaced = await catalog.replaceImage(cardId, side, upload.data.s3Key);
        if (!accept()) return false;
        if (!replaced.ok) throw new Error(replaced.message ?? replaced.error);
        setImageRotations((current) => ({ ...current, [side]: 0 }));
      }
      return true;
    } finally {
      if (accept()) setImageAdjustmentBusySide(null);
    }
  };

  const saveIdentityDraft = async () => {
    if (
      active === null ||
      editingIdentityFor !== active.cardId ||
      identityDraft === null ||
      !identitySerialIsValid ||
      !pricingDraftIsValid ||
      listingCopyRestorationPending ||
      pendingChecklistIdentity !== null ||
      busy !== null ||
      imageAdjustmentBusySide !== null
    ) {
      return;
    }
    if (listingEditLock !== null) {
      setError('This card is already listed or sold. Use the audited identity correction action.');
      return;
    }
    setBusy('identity-draft');
    setError(null);
    let imageCorrectionsSaved = false;
    try {
      imageCorrectionsSaved = await persistPendingImageCorrections(active.cardId);
    } catch (imageError) {
      setBusy(null);
      await refresh();
      setError(
        `Image correction failed: ${
          imageError instanceof Error ? imageError.message : 'image_rotation_failed'
        }`,
      );
      return;
    }
    const listingPrice = listingPriceForReview();
    const draftChecklistSourceSelection =
      manualOverrideEnabled || unresolvedSelection !== undefined || retainVerifiedIdentity
        ? undefined
        : activeChecklistSourceSelection;
    const result = await catalog.update(
      active.cardId,
      reviewAssignmentIdentity(active, identityDraft),
      {
        mode: 'DRAFT',
        ...(cardPresentationDraft === undefined ? {} : { cardPresentation: cardPresentationDraft }),
        ...(draftChecklistSourceSelection === undefined
          ? {}
          : {
              checklistSourceSelection: sourceHonorsAssignment(
                active.reviewChecklistSelection,
                draftChecklistSourceSelection,
              )
                ? draftChecklistSourceSelection
                : null,
            }),
        ...(manualOverrideEnabled || unresolvedSelection !== undefined
          ? {
              manualIdentityOverrideReason: manualOverrideEnabled
                ? manualOverrideReason.trim()
                : 'Source classification unresolved; saved for operator review.',
              ...(manualEvidenceSelection === undefined || retainVerifiedIdentity
                ? {}
                : { manualIdentityOverrideSourceSelection: manualEvidenceSelection }),
            }
          : {}),
        ...(listingPrice === undefined ? {} : { listingPrice }),
      },
    );
    if (!result.ok) {
      setBusy(null);
      if (imageCorrectionsSaved) await refresh();
      setError(reviewSaveFailureMessage(result.message ?? result.error));
      return;
    }
    let listingCopySaved = false;
    const listingCopyDeferredForUnresolved =
      !manualOverrideEnabled && unresolvedSelection !== undefined;
    const listingCopySkippedForLongTitle =
      (manualOverrideEnabled || activeChecklistId !== null) &&
      selectedListingTemplate !== undefined &&
      listingTitle.trim() !== '' &&
      listingTitle.length > EBAY_LISTING_BASE_TITLE_MAX_LENGTH;
    if (
      itemSpecificsCategory !== null &&
      itemSpecificsCategory !== 'CCG_SINGLES' &&
      !listingCopyDeferredForUnresolved &&
      (manualOverrideEnabled || activeChecklistId !== null) &&
      selectedListingTemplate !== undefined &&
      listingTitle.trim() !== '' &&
      !listingCopySkippedForLongTitle
    ) {
      const copyResult = await marketplace.saveListingCopy(active.cardId, {
        title: listingTitle.trim(),
        ...(generatedDescription.trim() === '' ? {} : { description: generatedDescription.trim() }),
        templateId: selectedListingTemplate.templateId,
        identitySource: manualOverrideEnabled ? 'MANUAL_OVERRIDE' : 'CHECKLIST',
        ...(activeChecklistId === null || manualOverrideEnabled
          ? {}
          : { checklistId: activeChecklistId }),
        identity: identityDraft,
        ...(cardPresentationDraft === undefined ? {} : { cardPresentation: cardPresentationDraft }),
        ...(Object.keys(itemSpecifics).length === 0 ? {} : { itemSpecifics }),
        itemSpecificsCategory,
      });
      if (!copyResult.ok) {
        setBusy(null);
        if (imageCorrectionsSaved) await refresh();
        setError(
          `Review draft was saved, but listing copy was not: ${
            copyResult.message ?? copyResult.error
          }`,
        );
        return;
      }
      listingCopySaved = true;
    }
    setBusy(null);
    setCards(
      (previous) =>
        previous?.map((card) =>
          card.cardId === active.cardId
            ? {
                ...card,
                ...(result.data.identityReviewDraft === undefined
                  ? {}
                  : { identityReviewDraft: result.data.identityReviewDraft }),
                ...(result.data.checklistSourceLineage === undefined
                  ? {}
                  : { checklistSourceLineage: result.data.checklistSourceLineage ?? undefined }),
                ...(result.data.manualIdentityOverride === undefined
                  ? {}
                  : { manualIdentityOverride: result.data.manualIdentityOverride ?? undefined }),
                ...(result.data.listingPrice === undefined
                  ? {}
                  : { listingPrice: result.data.listingPrice }),
              }
            : card,
        ) ?? previous,
    );
    setIdentityDraftEdited(false);
    if (bulkReviewBaseline.current?.key === bulkReviewKey) {
      bulkReviewBaseline.current = {
        ...bulkReviewBaseline.current,
        snapshot: bulkReviewSnapshot,
        // A partial review Save must not mark skipped/deferred listing copy as saved.
        ...(listingCopySaved ? { listingSnapshot: bulkListingSnapshot } : {}),
      };
    }
    if (imageCorrectionsSaved) await refresh();
    setNotice(
      itemSpecificsCategory === 'CCG_SINGLES'
        ? 'Identity draft saved. CCG listing details remain unsaved; use Save reviewed CCG details separately.'
        : listingCopyDeferredForUnresolved
          ? 'Draft saved. Listing copy is deferred until you use manual override to review the unresolved classification.'
          : listingCopySkippedForLongTitle
            ? `Draft saved. Shorten the listing title from ${listingTitle.length} to ${EBAY_LISTING_BASE_TITLE_MAX_LENGTH} characters or fewer before Ready to List.`
            : active.identityStatus === 'IDENTIFIED'
              ? 'Draft saved. The verified identity is unchanged.'
              : 'Draft saved. Identity remains unverified.',
    );
  };

  const saveReviewedCcgDetails = async () => {
    if (
      !active ||
      !identityDraft ||
      !selectedListingTemplate ||
      busy !== null ||
      listingEditLock !== null ||
      listingCopyRestorationPending
    )
      return;
    if (
      active.identityStatus !== 'IDENTIFIED' ||
      (active.checklistSourceLineage?.status !== 'VERIFIED' &&
        active.manualIdentityOverride?.status !== 'VERIFIED') ||
      !active.identity ||
      !cardIdentitiesEqual(active.identity, identityDraft)
    ) {
      setError('Review identity in full card details first. Local listing details remain unsaved.');
      return;
    }
    let review: MarketplaceCcgReviewRequest;
    try {
      review = ccgReviewForAction();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Review CCG details again.');
      return;
    }
    const scope = ccgScope;
    const operation = beginCcgOperation();
    setBusy('identity-draft');
    setError(null);
    try {
      const result = await marketplace.saveListingCopy(active.cardId, {
        title: listingTitle,
        ...(generatedDescription.trim() === '' ? {} : { description: generatedDescription.trim() }),
        templateId: selectedListingTemplate.templateId,
        identitySource:
          active.manualIdentityOverride?.status === 'VERIFIED' ? 'MANUAL_OVERRIDE' : 'CHECKLIST',
        ...(activeChecklistId === null || active.manualIdentityOverride?.status === 'VERIFIED'
          ? {}
          : { checklistId: activeChecklistId }),
        identity: active.identity,
        ...(cardPresentationDraft === undefined ? {} : { cardPresentation: cardPresentationDraft }),
        itemSpecifics,
        itemSpecificsCategory: 'CCG_SINGLES',
        ccgReview: review,
      });
      if (!operation.current() || ccgCurrentScope.current !== scope) return;
      if (!result.ok) {
        const failure = ccgWorkflowFailure(result);
        if (failure?.action === 'CHECK_STATUS') {
          setCcgCopyUnknownCard(active.cardId);
          setError(
            'CCG listing save status is unconfirmed. Check the saved copy before reviewing and saving again.',
          );
          return;
        }
        setError(
          `CCG listing details were not confirmed saved: ${failure?.message ?? result.message ?? result.error}. Local details remain unsaved.`,
        );
        return;
      }
      setCcgCopyUnknownCard(null);
      setCcgListingSavedScope(scope);
      if (bulkReviewBaseline.current?.key === bulkReviewKey)
        bulkReviewBaseline.current = {
          ...bulkReviewBaseline.current,
          listingSnapshot: bulkListingSnapshot,
        };
      setNotice('Reviewed CCG listing details saved. Identity, photos and price were not changed.');
    } catch {
      if (operation.current() && ccgCurrentScope.current === scope) {
        setCcgCopyUnknownCard(active.cardId);
        setError(
          'CCG save status is unknown. Check the saved listing before retrying; local details remain unsaved.',
        );
      }
    } finally {
      operation.finish();
    }
  };

  const checkCcgListingStatus = async () => {
    if (!active || busy !== null) return;
    const cardId = active.cardId,
      scope = ccgScope;
    const operation = beginCcgOperation();
    setBusy('identity-draft');
    setCcgStatusNotice(null);
    try {
      if (ccgReadyUnknownCard === cardId) {
        if (!isAdmin) {
          const copy = await marketplace.loadListingCopy(cardId);
          if (!operation.current() || ccgCurrentScope.current !== scope) return;
          setCcgStatusNotice(
            copy.ok && copy.data.item?.status
              ? `Saved queue status observed: ${copy.data.item.status}. Ask an administrator to confirm the current CCG policy and readiness before another queue request.`
              : 'Queue status remains unconfirmed. Ask an administrator to inspect Queue for release.',
          );
          return;
        }
        const responses = await Promise.all([
          marketplace.listListingReadiness('READY_TO_LIST'),
          marketplace.listListingReadiness('APPROVED_FOR_EBAY'),
        ]);
        if (!operation.current() || ccgCurrentScope.current !== scope) return;
        // Only an exact current public readiness observation proves a queued state.
        const matched = responses
          .flatMap((r) => (r.ok ? r.data.items : []))
          .find(
            (r) =>
              r.cardId === cardId &&
              r.itemSpecificsCategory === 'CCG_SINGLES' &&
              r.ccgPolicy?.reviewIdentity ===
                (currentCcgPolicy
                  ? ebayCategoryPolicyReviewIdentity(currentCcgPolicy.binding)
                  : '') &&
              r.title === listingTitle &&
              r.templateId === selectedListingTemplateId &&
              JSON.stringify(r.itemSpecifics) === JSON.stringify(itemSpecifics),
          );
        setCcgStatusNotice(
          matched
            ? 'The current CCG listing is queued. Open Queue for release to inspect it; no new queue request was sent.'
            : 'The expected queue record is not confirmed by this bounded read. Its status remains uncertain. Check again or inspect Queue for release.',
        );
      } else {
        const result = await marketplace.loadListingCopy(cardId);
        if (!operation.current() || ccgCurrentScope.current !== scope) return;
        setCcgStatusNotice(
          result.ok && result.data.item
            ? `Saved listing observed: ${result.data.item.title}. Local edits were preserved. The public copy does not prove the review outcome; deliberately review and save again if needed.`
            : 'Saved listing status could not be confirmed. Local edits were preserved.',
        );
      }
    } catch {
      if (operation.current() && ccgCurrentScope.current === scope)
        setCcgStatusNotice('Status could not be confirmed. Local edits were preserved.');
    } finally {
      operation.finish();
    }
  };

  const applyListedCardIdentityCorrection = async () => {
    if (
      active === null ||
      identityDraft === null ||
      listingEditLock === null ||
      !isAdmin ||
      identityCorrectionReason.trim().length < 5 ||
      !identitySerialIsValid ||
      busy !== null
    ) {
      return;
    }
    const sourceSelection = activeChecklistSourceSelection ?? storedChecklistSourceSelection;
    if (sourceSelection === null) {
      setError('Choose the exact checklist row and parallel before applying the correction.');
      return;
    }
    setBusy('identity-correction');
    setError(null);
    const result = await catalog.update(active.cardId, identityDraft, {
      mode: 'CORRECT',
      correctionReason: identityCorrectionReason.trim(),
      checklistSourceSelection: sourceSelection,
    });
    setBusy(null);
    if (!result.ok) {
      setError(reviewSaveFailureMessage(result.message ?? result.error));
      return;
    }
    setIdentityCorrectionMode(false);
    setIdentityCorrectionReason('');
    setIdentityDraftEdited(false);
    setNotice(
      `TCVS identity corrected. The ${listingEditLock.state === 'SOLD' ? 'sold record' : 'active eBay listing'} was not changed or re-queued.`,
    );
    await refresh();
  };

  const submitIdentityAndNext = async () => {
    if (!photoReview.ready) return;
    if (
      active === null ||
      activeReviewCondition === null ||
      editingIdentityFor !== active.cardId ||
      identityDraft === null ||
      !identityDraftIsSubmittable(identityDraft) ||
      (!retainVerifiedIdentity &&
        (unresolvedVerificationBlocked ||
          isChecklistPlaceholderLabel(identityDraft.subset) ||
          isChecklistPlaceholderLabel(identityDraft.variation))) ||
      !identitySerialIsValid ||
      (!retainVerifiedIdentity &&
        !manualOverrideEnabled &&
        (structuredResolutionPending ||
          !activeParallelSelectionComplete ||
          !legacyParallelSelectionComplete(
            activeReviewCondition,
            structuredSelectionRequired,
            pendingParallel,
            identityDraft.variation,
          ))) ||
      !pricingDraftIsValid ||
      !listingReadinessIsValid ||
      cardPresentationDraft === undefined ||
      (!retainVerifiedIdentity && !manualOverrideEnabled && activeChecklistId === null) ||
      selectedListingTemplate === undefined ||
      pricingAmountMinor === undefined ||
      pricingAmountMinor === null ||
      busy !== null ||
      imageAdjustmentBusySide !== null
    ) {
      return;
    }
    if (listingEditLock !== null) {
      setError('This card is already listed or sold and cannot be queued again.');
      return;
    }
    let ccgReview: MarketplaceCcgReviewRequest | undefined;
    if (itemSpecificsCategory === 'CCG_SINGLES') {
      try {
        ccgReview = ccgReviewForAction();
        if (!currentCcgPolicy) throw new Error('Reload policy.');
        const c = ccgPresentationCompatibility(currentCcgPolicy, cardPresentationDraft);
        if (!c.ready) throw new Error(c.message);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Review CCG details again.');
        return;
      }
    }
    const operation = itemSpecificsCategory === 'CCG_SINGLES' ? beginCcgOperation() : undefined;
    const current = () => operation?.current() ?? true;
    const finish = () => (operation ? operation.finish() : setBusy(null));
    const ccgRetryKey = JSON.stringify([
      claims?.sub,
      currentVisit.sequence,
      active.cardId,
      identityDraft,
      cardPresentationDraft,
      pricingDraft,
      activeChecklistSourceSelection,
      manualOverrideEnabled,
      manualOverrideReason,
    ]);
    const currentIndex = visible.findIndex((card) => card.cardId === active.cardId);
    const nextCardId =
      visible[currentIndex + 1]?.cardId ??
      (currentIndex > 0 ? visible[currentIndex - 1]?.cardId : undefined) ??
      null;
    setBusy('identity');
    setError(null);
    let imageCorrectionsSaved = false;
    try {
      imageCorrectionsSaved = await persistPendingImageCorrections(active.cardId, current);
      if (!current()) return;
      if (imageCorrectionsSaved && photoReview.required) {
        photoReview.reload();
        finish();
        await refresh(current);
        if (!current()) return;
        setNotice('Corrections saved. Review the updated images before approving.');
        return;
      }
    } catch (imageError) {
      if (!current()) return;
      if (photoReview.required) photoReview.reload();
      finish();
      await refresh(current);
      if (!current()) return;
      setError(
        `Image correction failed: ${
          imageError instanceof Error ? imageError.message : 'image_rotation_failed'
        }`,
      );
      return;
    }
    if (itemSpecificsCategory === 'CCG_SINGLES' && ccgCurrentScope.current !== ccgScope) {
      finish();
      setError('The card or CCG review changed. Review current details before continuing.');
      return;
    }
    const listingPrice = listingPriceForReview();
    const result =
      itemSpecificsCategory === 'CCG_SINGLES' && ccgIdentitySaved.current === ccgRetryKey
        ? { ok: true as const, data: {} }
        : await catalog.update(
            active.cardId,
            retainVerifiedIdentity ? active.identity! : identityDraft,
            {
              mode: 'VERIFY',
              identityAction: retainVerifiedIdentity ? 'RETAIN' : 'SELECT',
              ...(active.reviewChecklistSelection &&
              (active.reviewChecklistSelection.requiresReview || replacesAssignment)
                ? {
                    expectedReviewChecklistSelection: {
                      checklistId: active.reviewChecklistSelection.checklistId,
                      selectedAt: active.reviewChecklistSelection.selectedAt,
                    },
                    ...(replacesAssignment ? { replaceChecklistAssignment: true } : {}),
                  }
                : {}),
              imageIssue,
              ...(photoReview.reviewId === undefined
                ? {}
                : { photoReviewId: photoReview.reviewId }),
              cardPresentation: cardPresentationDraft,
              ...(retainVerifiedIdentity
                ? {}
                : manualOverrideEnabled
                  ? {
                      manualIdentityOverrideReason: manualOverrideReason.trim(),
                      ...(manualEvidenceSelection === undefined
                        ? {}
                        : {
                            manualIdentityOverrideSourceSelection: manualEvidenceSelection,
                            manualIdentityOverrideCompleted: manualClassificationConfirmed,
                          }),
                    }
                  : { checklistSourceSelection: activeChecklistSourceSelection }),
              ...(listingPrice === undefined ? {} : { listingPrice }),
            },
          );
    if (!current()) return;
    if (!result.ok) {
      finish();
      if (imageCorrectionsSaved) await refresh(current);
      if (!current()) return;
      if (photoReview.required) photoReview.reload();
      setError(reviewSaveFailureMessage(result.message ?? result.error));
      return;
    }
    let ccgSavedCard: CatalogListCardRecord | undefined;
    if (itemSpecificsCategory === 'CCG_SINGLES') {
      ccgIdentitySaved.current = ccgRetryKey;
      // Re-read actual owned identity after VERIFY. Preserve unsaved local listing edits.
      try {
        const refreshed = await loadOrderedBatchCards(catalog.list, batchId);
        if (!current()) return;
        ccgSavedCard = refreshed.ok
          ? refreshed.data.items.find((card) => card.cardId === active.cardId)
          : undefined;
        if (
          !ccgSavedCard ||
          ccgSavedCard.identityStatus !== 'IDENTIFIED' ||
          !cardIdentitiesEqual(
            ccgSavedCard.identity,
            retainVerifiedIdentity ? active.identity! : identityDraft,
          ) ||
          (ccgSavedCard.manualIdentityOverride?.status !== 'VERIFIED' &&
            ccgSavedCard.checklistSourceLineage?.status !== 'VERIFIED')
        )
          throw new Error('Saved identity is not yet confirmed by the current card read.');
        for (const name of ['Finish', 'Rarity'] as const)
          if (
            ccgReview?.specifics[name]?.origin === 'IMPORTED_RESEARCH' &&
            ccgSavedCard.importedIdentification?.listing?.itemSpecifics?.[name] !==
              ccgReview.specifics[name]?.value
          )
            throw new Error('Current Card research changed. Review it again.');
        ccgReview = ccgReviewForAction();
      } catch (e) {
        if (!current()) return;
        finish();
        setError(
          `Identity was saved, but Ready to List was not queued: ${e instanceof Error ? e.message : 'Current card could not be checked.'} Local listing details remain unsaved. Check status before retrying.`,
        );
        return;
      }
    }
    if (!current()) return;
    const readinessResult = await marketplace
      .saveListingReadiness(active.cardId, {
        title: listingTitle,
        ...(generatedDescription.trim() === '' ? {} : { description: generatedDescription.trim() }),
        templateId: selectedListingTemplate.templateId,
        identitySource: ccgSavedCard
          ? ccgSavedCard.manualIdentityOverride?.status === 'VERIFIED'
            ? 'MANUAL_OVERRIDE'
            : 'CHECKLIST'
          : manualOverrideEnabled
            ? 'MANUAL_OVERRIDE'
            : 'CHECKLIST',
        ...(ccgSavedCard
          ? ccgSavedCard.manualIdentityOverride?.status === 'VERIFIED'
            ? {}
            : { checklistId: ccgSavedCard.checklistSourceLineage?.checklistId }
          : activeChecklistId === null || manualOverrideEnabled
            ? {}
            : { checklistId: activeChecklistId }),
        amountMinor: pricingAmountMinor,
        currency: 'USD',
        cardPresentation: cardPresentationDraft,
        ...(Object.keys(itemSpecifics).length === 0 ? {} : { itemSpecifics }),
        itemSpecificsCategory: itemSpecificsCategory!,
        ...(ccgReview === undefined ? {} : { ccgReview }),
      })
      .catch((e: unknown) => {
        if (itemSpecificsCategory !== 'CCG_SINGLES') throw e;
        return {
          ok: false as const,
          status: 0,
          error: 'ccg_readiness_status_unknown',
          message: 'Readiness save status is unconfirmed.',
          body: { action: 'CHECK_STATUS', message: 'Readiness save status is unconfirmed.' },
        };
      });
    if (!current()) return;
    if (!readinessResult.ok) {
      if (
        itemSpecificsCategory === 'CCG_SINGLES' &&
        ccgWorkflowFailure(readinessResult)?.action === 'CHECK_STATUS'
      ) {
        finish();
        setCcgReadyUnknownCard(active.cardId);
        setError(
          'Identity was saved. Ready to List status is unconfirmed. Check listing status before another queue request.',
        );
        return;
      }
      if (photoReview.required) photoReview.reload();
      finish();
      if (
        itemSpecificsCategory !== 'CCG_SINGLES' &&
        (imageCorrectionsSaved ||
          active.reviewChecklistSelection?.requiresReview ||
          replacesAssignment)
      ) {
        const refreshed = await refresh(current);
        if (!current()) return;
        const savedCard = refreshed?.find((card) => card.cardId === active.cardId);
        if (savedCard !== undefined) {
          setState(workbenchState(savedCard));
          setActiveId(savedCard.cardId);
        }
      }
      setError(
        `Identity was saved, but Ready to List was not queued: ${
          ccgWorkflowFailure(readinessResult)?.message ??
          readinessResult.message ??
          readinessResult.error
        }. Select Ready to List again to retry.`,
      );
      return;
    }
    if (itemSpecificsCategory === 'CCG_SINGLES') {
      await refresh(current);
      if (!current()) return;
    }
    finish();
    setNotice('Verified identity submitted and added to Ready to List');
    setEditingIdentityFor(null);
    setIdentityDraft(null);
    setIdentityDraftEdited(false);
    setManualOverrideEnabled(false);
    setManualOverrideReason('');
    setPricingDraft(null);
    setCardPresentationDraft(undefined);
    setItemSpecifics({});
    setItemDetailsMode(null);
    setImageIssue(false);
    setReviewPanelTab('identification');
    setPendingParallel('');
    setSelectedChecklistCandidate('');
    setStructuredParallelChoice(null);
    setExplicitSubset(null);
    if (itemSpecificsCategory !== 'CCG_SINGLES') {
      await refresh(current);
      if (!current()) return;
    }
    setActiveId(nextCardId);
  };

  const activeIndex =
    active === null ? -1 : visible.findIndex((card) => card.cardId === active.cardId);
  const removeFromReview = async (action: 'pick' | 'retire') => {
    if (active === null || busy !== null || inventoryActionPending.current) return;
    if (action === 'retire' && !isAdmin) return;
    const cardId = active.cardId;
    const label = active.shortCode ?? cardId;
    let reason = '';
    if (action === 'retire') {
      const input = window.prompt(`Reason for retiring ${label}:`);
      if (input === null) return;
      reason = input.trim();
      if (reason === '') {
        setError('A retirement reason is required.');
        return;
      }
    }
    const confirmation =
      action === 'pick'
        ? `Pick ${label}? Confirm this card has been physically removed from the batch. This is a manual inventory pull, not completion of a fulfillment picklist or an eBay order. It does not end marketplace listings. Unsaved review changes will be discarded.`
        : `Retire ${label}? Confirm this card has been physically removed from the batch. It will leave active inventory and reviews, but its record and images remain recoverable. Unsaved review changes will be discarded.`;
    if (!window.confirm(confirmation)) return;
    inventoryActionPending.current = true;
    setBusy(action);
    setError(null);
    setNotice(null);
    const nextId = visible[activeIndex + 1]?.cardId ?? visible[activeIndex - 1]?.cardId ?? null;
    try {
      const result =
        action === 'pick' ? await storage.pick(cardId) : await catalog.retire(cardId, reason);
      if (currentReviewBatch.current !== batchId) return;
      if (!result.ok) {
        setError(reviewSaveFailureMessage(result.message ?? result.error));
        return;
      }
      removedReviewCards.current.add(cardId);
      setCards((current) =>
        current === null
          ? null
          : positionBatchCards(current.filter((card) => card.cardId !== cardId)),
      );
      setActiveId((current) => (current === cardId || current === null ? nextId : current));
      setImagePopoutOpen(false);
      setBatchNotice(
        `${label} ${action === 'pick' ? 'picked' : 'retired'}. Removed from this batch.`,
      );
      await refresh();
    } catch {
      setError(
        'The inventory action could not be confirmed. Check the card status before retrying.',
      );
    } finally {
      inventoryActionPending.current = false;
      setBusy(null);
    }
  };
  // Keep the disabled state and its explanation derived from the same checks.
  const readyToListBlockers: string[] = [];
  if (listingEditLock !== null)
    readyToListBlockers.push('This card has an active or sold listing.');
  if (!photoReview.ready) readyToListBlockers.push('Wait for the current photo review to load.');
  if (active === null || identityDraft === null) {
    readyToListBlockers.push('Select a card and complete its identity.');
  } else {
    if (!retainVerifiedIdentity && !manualOverrideEnabled && structuredResolutionPending)
      readyToListBlockers.push('Wait for checklist matching to finish.');
    if (!identityDraftIsSubmittable(identityDraft))
      readyToListBlockers.push('Complete the required identity fields.');
    if (
      !retainVerifiedIdentity &&
      (unresolvedVerificationBlocked ||
        isChecklistPlaceholderLabel(identityDraft.subset) ||
        isChecklistPlaceholderLabel(identityDraft.variation))
    )
      readyToListBlockers.push('Resolve the uncertain checklist identity.');
    if (
      !retainVerifiedIdentity &&
      !manualOverrideEnabled &&
      (!activeParallelSelectionComplete ||
        !legacyParallelSelectionComplete(
          activeReviewCondition ?? 'ready',
          structuredSelectionRequired,
          pendingParallel,
          identityDraft.variation,
        ))
    )
      readyToListBlockers.push('Choose the card’s parallel or Base version.');
  }
  if (!identitySerialIsValid) readyToListBlockers.push('Correct the serial number and run size.');
  readyToListBlockers.push(...listingReadinessBlockers);
  if (busy !== null || imageAdjustmentBusySide !== null)
    readyToListBlockers.push('Wait for the current action to finish.');
  if (itemSpecificsCategory === 'CCG_SINGLES' && ccgReadyUnknownCard === active?.cardId)
    readyToListBlockers.push('Ready to List status is unconfirmed. Check listing status.');
  const readyToListSubmitDisabled = readyToListBlockers.length > 0;
  const batchChecklistSummary = (checklists ?? []).find(
    (item) => item.checklistId === batchChecklistId,
  );
  const draftSaveDisabled =
    pendingChecklistIdentity !== null ||
    active === null ||
    identityDraft === null ||
    !identitySerialIsValid ||
    (manualOverrideEnabled && manualOverrideReason.trim().length < 5) ||
    !pricingDraftIsValid ||
    busy !== null ||
    imageAdjustmentBusySide !== null ||
    listingEditLock !== null;
  const listedCardReviewLocked = listingEditLock !== null && !identityCorrectionMode;
  const importedResearch = getImportedCardResearch(active);
  const importedListing = importedResearch?.listing;
  const supportedImportedFeatures = new Set<string>(
    itemSpecificsCategory === 'SPORT_SINGLES'
      ? EBAY_SPORT_CARD_FEATURES
      : EBAY_NON_SPORT_CARD_FEATURES,
  );
  const importedResearchDisabled =
    listingCopyRestorationPending ||
    busy !== null ||
    listingEditLock !== null ||
    active === null ||
    editingIdentityFor !== active.cardId;
  const disabledImportedFields: ImportedListingField[] = [];
  if (listingContextLoading || researchCopyReadyFor !== active?.cardId)
    disabledImportedFields.push('title', 'description', 'price', 'features');
  if (selectedListingTemplate === undefined || generatingDescription)
    disabledImportedFields.push('title', 'description');
  if (
    importedListing?.description !== undefined &&
    escapeImportedDescription(importedListing.description).length > LISTING_DESCRIPTION_MAX_LENGTH
  )
    disabledImportedFields.push('description');
  if (
    importedListing?.title !== undefined &&
    importedListing.title.length > EBAY_LISTING_BASE_TITLE_MAX_LENGTH
  )
    disabledImportedFields.push('title');
  if (pricingDraft === null) disabledImportedFields.push('price');
  if (importedListing?.features?.some((feature) => !supportedImportedFeatures.has(feature)))
    disabledImportedFields.push('features');
  const adoptImportedListing = (field: ImportedListingField) => {
    if (
      reviewVisit.current !== currentVisit ||
      importedResearchDisabled ||
      disabledImportedFields.includes(field) ||
      importedListing === undefined
    )
      return;
    if (field === 'title' && importedListing.title !== undefined)
      setListingTitleOverride(importedListing.title);
    if (field === 'description' && importedListing.description !== undefined) {
      setGeneratedDescription(escapeImportedDescription(importedListing.description));
      setDescriptionUsesTemplateDefault(false);
      setDescriptionSourceTitle('');
    }
    if (field === 'price' && importedListing.suggestedPrice !== undefined) {
      setPricingDraft((current) =>
        current === null
          ? null
          : {
              ...current,
              price: centsToUsdInput(importedListing.suggestedPrice!.amountMinor),
              notes: (importedListing.priceBasis ?? '').slice(0, 500),
            },
      );
    }
    if (field === 'features' && importedListing.features !== undefined) {
      setItemSpecifics((current) => ({ ...current, Features: [...importedListing.features!] }));
    }
  };

  const moveActive = (offset: number) => {
    const next = visible[activeIndex + offset];
    if (next === undefined) return;
    setActiveId(next.cardId);
    setEditingIdentityFor(null);
    setIdentityDraft(null);
    setIdentityDraftEdited(false);
    setPricingDraft(null);
    setCardPresentationDraft(undefined);
    setItemSpecifics({});
    setItemDetailsMode(null);
    setImageIssue(false);
    setIdentityCorrectionMode(false);
    setIdentityCorrectionReason('');
    setPendingParallel('');
    setSelectedChecklistCandidate('');
    setStructuredParallelChoice(null);
    setExplicitSubset(null);
    setError(null);
    setNotice(null);
    setCardSuggestionNote(null);
    setCardSuggestionError(false);
  };

  const batchSettings = (
    <details className="tcvs-workbench-batch-settings">
      <summary>
        <strong>Batch settings</strong>
      </summary>
      <div className="tcvs-workbench-batch-settings-panel">
        <div className="tcvs-workbench-batch-settings-summary">
          <span>
            {batchChecklistSummary === undefined
              ? batchChecklistId === null
                ? 'No batch checklist'
                : 'Assigned checklist'
              : checklistProductLabel(batchChecklistSummary)}
          </span>
          <span>
            Default price:{' '}
            {batchDefaultPrice.trim() === ''
              ? 'Not set'
              : batchDefaultPriceIsValid
                ? `$${batchDefaultPrice}`
                : 'Invalid price'}
          </span>
          <span>
            {counts.completed} / {cards?.length ?? 0} completed
          </span>
        </div>
        <p>
          <Link
            to={`/storage/batches/${encodeURIComponent(batchId)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="tcvs-mono tcvs-linklike"
          >
            {batchId}
          </Link>{' '}
          <span className="tcvs-muted">· {cards?.length ?? 0} cards</span>
        </p>
        <OwnershipSummary cardIds={(cards ?? []).map((card) => card.cardId)} labels={ownership} />
        <div className="tcvs-workbench-batch-controls">
          <div className="tcvs-workbench-batch-checklist">
            <label>Batch checklist</label>
            <div className="tcvs-workbench-batch-checklist-action">
              <FilterableSelect
                value={batchChecklistDraftId}
                disabled={batchChecklistBusy}
                ariaLabel="Batch checklist"
                placeholder="Search checklists…"
                options={batchChecklistSelectOptions(checklists ?? [])}
                loading={checklistSearchLoading}
                searchError={checklistSearch.error}
                hasMore={checklistSearch.hasMore}
                onLoadMore={checklistSearch.loadMore}
                onRetry={checklistSearch.retry}
                onQueryChange={setChecklistSearchQuery}
                onChange={setBatchChecklistDraftId}
              />
              <button
                type="button"
                disabled={batchChecklistBusy || batchChecklistDraftId === ''}
                onClick={() => void applySelectedBatchChecklist()}
              >
                {batchChecklistBusy ? 'Applying…' : 'Apply to all cards'}
              </button>
            </div>
            <span className="tcvs-muted">
              Applies to unreviewed cards; reviewed cards stay intact.
            </span>
          </div>
          <div className="tcvs-workbench-batch-default-price">
            <div className="tcvs-field">
              <label htmlFor="batch-default-listing-price">Default price (USD)</label>
              <div className="tcvs-currency-stepper">
                <div className="tcvs-currency-input">
                  <span>$</span>
                  <input
                    id="batch-default-listing-price"
                    aria-label="Default batch listing price in USD"
                    aria-invalid={!batchDefaultPriceIsValid}
                    inputMode="decimal"
                    placeholder="0.00"
                    value={batchDefaultPrice}
                    disabled={busy !== null || listingCopyRestorationPending}
                    onChange={(event) => updateBatchDefaultPrice(event.currentTarget.value)}
                  />
                </div>
                <div className="tcvs-currency-stepper-controls">
                  <button
                    type="button"
                    className="tcvs-icon-button"
                    aria-label="Increase default batch price by one dollar"
                    title="Increase by $1"
                    disabled={busy !== null || listingCopyRestorationPending}
                    onClick={() =>
                      updateBatchDefaultPrice(adjustUsdInputByDollars(batchDefaultPrice, 1))
                    }
                  >
                    <span aria-hidden="true">&uarr;</span>
                  </button>
                  <button
                    type="button"
                    className="tcvs-icon-button"
                    aria-label="Decrease default batch price by one dollar"
                    title="Decrease by $1"
                    disabled={busy !== null || listingCopyRestorationPending}
                    onClick={() =>
                      updateBatchDefaultPrice(adjustUsdInputByDollars(batchDefaultPrice, -1))
                    }
                  >
                    <span aria-hidden="true">&darr;</span>
                  </button>
                </div>
              </div>
            </div>
            {!batchDefaultPriceIsValid ? (
              <span className="tcvs-error">Enter a valid USD price.</span>
            ) : (
              <span className="tcvs-muted">Fills blank card prices only.</span>
            )}
          </div>
        </div>
      </div>
    </details>
  );

  // Capture restored local review state once per card/verified listing reload. Local edits
  // are compared at action time so a pending bulk response cannot discard later edits.
  const bulkReviewSnapshot = JSON.stringify({
    identityDraft,
    pricingDraft,
    manualOverrideEnabled,
    manualOverrideReason,
    manualSourceSelection,
    manualClassificationConfirmed,
    pendingParallel,
    explicitSubset,
  });
  const bulkListingSnapshot = JSON.stringify({
    cardPresentationDraft,
    itemSpecifics,
    itemSpecificsCategory,
    selectedListingTemplateId,
    generatedDescription,
    listingTitleOverride,
  });
  // selectedChecklistCandidate/localStructuredParallelChoice are restored from the
  // persisted source asynchronously. Operator changes already set identityDraftEdited;
  // automatic restoration must not turn an untouched card into unsaved work.
  useEffect(() => {
    if (
      listingContextLoading ||
      (active !== null &&
        (researchCopyReadyFor !== active.cardId ||
          listingCopyRestoredGeneration !== listingCopyRefreshGeneration ||
          listingCopyReadyContext !== listingCopyContextKey))
    )
      return;
    if (bulkReviewBaseline.current?.key !== bulkReviewKey) {
      // Saved presentation/default copy can trigger derived-field effects after the
      // saved-copy response. Wait for those exact restored values before baselining.
      // Once captured, query/notes remain in the snapshot so operator edits stay dirty.
      if (
        identityDraft !== null &&
        pricingDraft !== null &&
        pricingDraft.query !==
          (active?.listingPrice?.researchQuery ??
            cardResearchQuery(identityDraft, cardPresentationDraft))
      )
        return;
      if (
        descriptionUsesTemplateDefault &&
        generatedDescription !== currentTemplateDefaultDescription
      )
        return;
      bulkReviewBaseline.current = {
        key: bulkReviewKey,
        snapshot: bulkReviewSnapshot,
        listingSnapshot: bulkListingSnapshot,
      };
      setBulkReviewReadyKey(bulkReviewKey);
    }
  }, [
    bulkReviewKey,
    bulkReviewSnapshot,
    bulkListingSnapshot,
    listingContextLoading,
    researchCopyReadyFor,
    active,
    listingCopyRestoredGeneration,
    listingCopyRefreshGeneration,
    listingCopyReadyContext,
    listingCopyContextKey,
    identityDraft,
    pricingDraft,
    cardPresentationDraft,
    descriptionUsesTemplateDefault,
    generatedDescription,
    currentTemplateDefaultDescription,
  ]);
  const hasUnsavedBulkReviewChanges = () =>
    identityDraftEdited ||
    imageRotations.front !== 0 ||
    imageRotations.back !== 0 ||
    (active !== null &&
      (listingCopyRestorationIncomplete ||
        bulkReviewBaseline.current?.key !== bulkReviewKey ||
        bulkReviewBaseline.current.snapshot !== bulkReviewSnapshot ||
        bulkReviewBaseline.current.listingSnapshot !== bulkListingSnapshot));
  bulkGuardRef.current = hasUnsavedBulkReviewChanges;
  const refreshAfterImportedListing = async () => {
    // Even a callback retained by an earlier render belongs only to that exact visit.
    if (reviewVisit.current !== currentVisit)
      throw new Error('The review changed while reloading. Your edits were preserved.');
    if (bulkGuardRef.current()) throw new Error('Unsaved review edits were preserved.');
    invalidateVariantOptions();
    const refreshedBatch = batchId;
    const refreshedCard = latestActive.current?.cardId;
    const refreshedVisit = currentVisit;
    const reloadGeneration = ++importedListingReloadGeneration.current;
    const acceptsReload = () =>
      currentReviewBatch.current === refreshedBatch &&
      latestActive.current?.cardId === refreshedCard &&
      reviewVisit.current === refreshedVisit &&
      importedListingReloadGeneration.current === reloadGeneration &&
      !bulkGuardRef.current();
    const staleReload = () =>
      new Error('The review changed while reloading. Your edits were preserved.');
    setImportedListingReloadPending(true);
    let updatedCards: Awaited<ReturnType<typeof refresh>>;
    try {
      // refresh checks the full visit/request fence before committing Card or error state.
      updatedCards = await refresh(acceptsReload);
    } catch (failure) {
      if (!acceptsReload()) throw staleReload();
      throw failure;
    }
    if (!acceptsReload() || bulkGuardRef.current()) throw staleReload();
    if (updatedCards === undefined) throw new Error('Cards could not be reloaded.');
    const updated = updatedCards.find((card) => card.cardId === refreshedCard);
    if (updated !== undefined) {
      setCardPresentationDraft(presentationDraftForCard(updated));
      setPricingDraft(
        pricingDraftWithBatchDefault(updated, identityDraftForCard(updated), batchDefaultPrice),
      );
    }
    // Reload saved listing copy even when the active card and checklist are unchanged.
    setListingCopyRefreshGeneration((current) => current + 1);
    setImportedListingReloadPending(false);
  };

  if (batchId === '') return <p className="tcvs-error">Batch id is required.</p>;

  return (
    <div className="tcvs-identification-workbench">
      <header className="tcvs-workbench-header">
        <div className="tcvs-workbench-heading">
          <div className="tcvs-workbench-heading-main">
            <Link to="/batches" state={reviewsReturnContext} className="tcvs-muted">
              Reviews
            </Link>
            <h2>Batch Identification</h2>
            <nav
              className="tcvs-workbench-tabs"
              aria-label="Identification states"
              hidden={workbenchView !== 'CARD'}
            >
              {STATE_ORDER.map((key) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={state === key}
                  disabled={busy !== null}
                  onClick={() => switchState(key)}
                >
                  {STATE_LABELS[key]} <span>{counts[key]}</span>
                </button>
              ))}
            </nav>
          </div>
          <div className="tcvs-workbench-heading-options">
            {batchSettings}
            <BatchTransferTools
              batchId={batchId}
              onImported={refreshAfterBulkEdit}
              disabled={busy !== null || batchChecklistBusy}
            />
            <div className="tcvs-workbench-view-switch" aria-label="Batch identification view">
              <button
                type="button"
                aria-pressed={workbenchView === 'CARD'}
                onClick={() => setWorkbenchView('CARD')}
              >
                Card Review
              </button>
              <button
                type="button"
                aria-pressed={workbenchView === 'GRID'}
                onClick={() => {
                  setImagePopoutOpen(false);
                  setWorkbenchView('GRID');
                }}
              >
                Batch Grid
              </button>
            </div>
            <BulkImportedListingTools
              batchId={batchId}
              cards={cards ?? []}
              disabled={
                busy !== null ||
                batchChecklistBusy ||
                cards === null ||
                imageAdjustmentBusySide !== null
              }
              hasUnsavedChanges={hasUnsavedBulkReviewChanges}
              reviewVisit={currentVisit.sequence}
              onApplied={refreshAfterImportedListing}
            />
            <label className="tcvs-workbench-match-details-toggle">
              <input
                type="checkbox"
                checked={showMatchDetails}
                onChange={(event) => {
                  const enabled = event.target.checked;
                  setShowMatchDetails(enabled);
                  writeShowMatchDetailsPreference(enabled);
                }}
              />
              Show match details
            </label>
          </div>
        </div>
      </header>

      {workbenchView === 'GRID' &&
        (cards === null ? (
          <p>Loading cards…</p>
        ) : (
          <BatchIdentificationGrid
            ownership={ownership}
            batchId={batchId}
            cards={cards.filter((card) => card.retiredAt === undefined)}
            checklists={checklists ?? []}
            batchChecklistId={batchChecklistId}
            checklistSearchLoading={checklistSearchLoading}
            checklistSearchError={checklistSearch.error}
            checklistSearchHasMore={checklistSearch.hasMore}
            onChecklistLoadMore={checklistSearch.loadMore}
            onChecklistRetry={checklistSearch.retry}
            onChecklistSearch={setChecklistSearchQuery}
            onUpdated={refreshAfterBulkEdit}
            onOpenCard={(cardId) => {
              const card = cards.find((candidate) => candidate.cardId === cardId);
              if (card !== undefined) setState(workbenchState(card));
              setActiveId(cardId);
              setWorkbenchView('CARD');
            }}
          />
        ))}

      {batchError !== null ? <p className="tcvs-error">Error: {batchError}</p> : null}
      {batchNotice !== null ? <p className="tcvs-workbench-notice">{batchNotice}</p> : null}
      {error !== null ? <p className="tcvs-error">Error: {error}</p> : null}
      {notice !== null ? <p className="tcvs-workbench-notice">{notice}</p> : null}

      <div
        className="tcvs-workbench-body"
        data-list-collapsed={listCollapsed ? 'true' : 'false'}
        hidden={workbenchView !== 'CARD'}
      >
        {!listCollapsed ? (
          <div className="tcvs-workbench-list">
            <div className="tcvs-workbench-list-heading">
              <strong>{STATE_LABELS[state]}</strong>
              <span className="tcvs-muted">{visible.length} cards</span>
              <button
                type="button"
                aria-label="Minimize card list"
                title="Minimize card list"
                onClick={() => setListCollapsed(true)}
              >
                &lt;
              </button>
            </div>
            {cards === null ? (
              <p>Loading cards…</p>
            ) : visible.length === 0 ? (
              <p className="tcvs-muted">No cards in {STATE_LABELS[state].toLowerCase()}.</p>
            ) : (
              <div className="tcvs-workbench-card-list">
                {visible.map((card) => {
                  const identity = identityForDisplay(card);
                  return (
                    <div
                      key={card.cardId}
                      className="tcvs-workbench-card-row"
                      data-active={active?.cardId === card.cardId ? 'true' : 'false'}
                      onClick={() => {
                        setActiveId(card.cardId);
                        setImagePopoutOpen(false);
                        if (editingIdentityFor !== card.cardId) {
                          setEditingIdentityFor(null);
                          setIdentityDraft(null);
                          setIdentityDraftEdited(false);
                          setPendingParallel('');
                          setSelectedChecklistCandidate('');
                          setStructuredParallelChoice(null);
                          setExplicitSubset(null);
                        }
                      }}
                    >
                      {card.imageUrls?.front !== undefined ? (
                        <img src={card.imageUrls.front} alt="" className="tcvs-workbench-thumb" />
                      ) : (
                        <span className="tcvs-workbench-thumb tcvs-workbench-thumb-empty" />
                      )}
                      <div className="tcvs-workbench-card-summary">
                        <span className="tcvs-muted">
                          Batch position {card.currentBatchPosition}
                        </span>
                        <span className="tcvs-mono">
                          {card.shortCode ?? card.cardId.slice(0, 8)}
                        </span>
                        <OwnershipLabel label={ownership[card.cardId]} />
                        {identity === undefined ? (
                          <span className="tcvs-muted">No checklist match</span>
                        ) : (
                          <>
                            <strong>{identity.name || 'Unknown name'}</strong>
                            <span className="tcvs-muted">#{identity.number || '—'}</span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}

        <aside className="tcvs-workbench-inspector">
          <VersionedImageReviewStatus {...photoReview} />
          {active !== null && workbenchState(active) === 'awaiting-images' ? (
            <p className="tcvs-muted" role="status">
              Identification is confirmed. Inspect the images, then choose Approve images. Approval
              does not queue a listing.
            </p>
          ) : null}
          {active === null ? (
            <div>
              <p className="tcvs-muted">Select a card to inspect it.</p>
              {listCollapsed ? (
                <button
                  type="button"
                  className="tcvs-btn-secondary"
                  onClick={() => setListCollapsed(false)}
                >
                  Show card list
                </button>
              ) : null}
            </div>
          ) : (
            <>
              <div className="tcvs-workbench-card-nav">
                <div className="tcvs-workbench-card-nav-leading">
                  {listCollapsed ? (
                    <button
                      type="button"
                      className="tcvs-btn-secondary"
                      onClick={() => setListCollapsed(false)}
                    >
                      Show card list
                    </button>
                  ) : null}
                  <Link
                    to={`/batches/${encodeURIComponent(batchId)}/images?card=${encodeURIComponent(
                      active.cardId,
                    )}`}
                    className="tcvs-linklike tcvs-review-images-link"
                  >
                    Review images
                  </Link>
                  <div className="tcvs-workbench-active-card-id">
                    <span className="tcvs-muted">
                      Batch position {sourceCard?.currentBatchPosition} of{' '}
                      {sourceCard?.currentBatchSize}
                    </span>
                    <span className="tcvs-muted">Card ID</span>
                    <span className="tcvs-mono">{active.cardId}</span>
                    <CopyIdButton value={active.cardId} label="Copy card ID" />
                    <OwnershipLabel label={ownership[active.cardId]} />
                  </div>
                </div>
                <div className="tcvs-workbench-review-toolbar">
                  <div className="tcvs-workbench-card-review-nav">
                    <div className="tcvs-workbench-card-submit-actions">
                      {editingIdentityFor === active.cardId && identityDraft !== null ? (
                        <button
                          type="button"
                          className="tcvs-btn-secondary"
                          disabled={draftSaveDisabled || listingCopyRestorationPending}
                          onClick={() => void saveIdentityDraft()}
                        >
                          {busy === 'identity-draft' ? 'Saving…' : 'Save'}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        disabled={readyToListSubmitDisabled}
                        aria-describedby={
                          readyToListSubmitDisabled ? 'review-readiness-reasons' : undefined
                        }
                        onClick={() => void submitIdentityAndNext()}
                      >
                        {busy === 'identity'
                          ? 'Submitting…'
                          : itemSpecificsCategory === 'CCG_SINGLES'
                            ? 'Review and queue'
                            : 'Ready to List'}
                      </button>
                      <details className="tcvs-workbench-secondary-actions" key={active.cardId}>
                        <summary>Card actions</summary>
                        <div>
                          {' '}
                          <button
                            type="button"
                            className="tcvs-btn-secondary tcvs-btn-compact"
                            title="Pick this card from inventory"
                            disabled={busy !== null || imageAdjustmentBusySide !== null}
                            onClick={() => void removeFromReview('pick')}
                          >
                            <PackageMinus size={16} aria-hidden="true" />
                            {busy === 'pick' ? 'Picking...' : 'Pick'}
                          </button>
                          {isAdmin ? (
                            <button
                              type="button"
                              className="tcvs-btn-danger tcvs-btn-compact"
                              title="Retire this card from active inventory"
                              disabled={busy !== null || imageAdjustmentBusySide !== null}
                              onClick={() => void removeFromReview('retire')}
                            >
                              <Archive size={16} aria-hidden="true" />
                              {busy === 'retire' ? 'Retiring...' : 'Retire'}
                            </button>
                          ) : null}
                        </div>
                      </details>
                    </div>
                    <div className="tcvs-workbench-card-pagination">
                      <button
                        type="button"
                        className="tcvs-btn-secondary"
                        aria-label="Previous card"
                        title="Previous card"
                        disabled={
                          activeIndex <= 0 || busy !== null || imageAdjustmentBusySide !== null
                        }
                        onClick={() => moveActive(-1)}
                      >
                        &lt;
                      </button>
                      <span>
                        Card {activeIndex + 1} of {visible.length}
                      </span>
                      <button
                        type="button"
                        className="tcvs-btn-secondary"
                        aria-label="Next card"
                        title="Next card"
                        disabled={
                          activeIndex < 0 ||
                          activeIndex >= visible.length - 1 ||
                          busy !== null ||
                          imageAdjustmentBusySide !== null
                        }
                        onClick={() => moveActive(1)}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                  {editingIdentityFor === active.cardId && pricingDraft !== null ? (
                    <section className="tcvs-review-listing-price" aria-label="Card listing price">
                      <div className="tcvs-review-listing-price-row">
                        <ListingPriceInput
                          cardId={active.cardId}
                          price={pricingDraft.price}
                          disabled={
                            listingEditLock !== null ||
                            busy !== null ||
                            listingCopyRestorationPending
                          }
                          onChange={(price) =>
                            setPricingDraft((current) =>
                              current === null ? null : { ...current, price },
                            )
                          }
                        />
                        <p className="tcvs-muted">
                          {active.listingPrice === undefined
                            ? 'No saved price. Save applies the current draft.'
                            : `Saved price: USD ${centsToUsdInput(active.listingPrice.amountMinor)}. Save applies any changes.`}
                        </p>
                      </div>
                      {!pricingDraftIsValid ? (
                        <p className="tcvs-error">Enter a price from $0.01 to $1,000,000.00.</p>
                      ) : null}
                      {importedResearch && importedListing?.suggestedPrice ? (
                        <>
                          <p className="tcvs-muted">
                            Imported price research · {importedResearch.producer.tool}
                          </p>
                          <ImportedPriceSuggestion
                            research={importedResearch}
                            compact
                            disabled={
                              importedResearchDisabled || disabledImportedFields.includes('price')
                            }
                            onAdopt={() => adoptImportedListing('price')}
                          />
                        </>
                      ) : null}
                    </section>
                  ) : null}
                  <div className="tcvs-workbench-action-status">
                    {busy === 'identity-draft' || identityDraftEdited ? (
                      <span role="status">
                        {busy === 'identity-draft' ? 'Saving draft…' : 'Unsaved identity edits'}
                      </span>
                    ) : null}
                    {heldChecklistReview ? (
                      <span role="note">
                        This card stays in this review while checklist identity edits are unsaved.
                        Save or reset fields to release it.
                      </span>
                    ) : null}
                    {readyToListSubmitDisabled ? (
                      <details className="tcvs-workbench-readiness">
                        <summary>
                          Not ready to list · {readyToListBlockers.length}{' '}
                          {readyToListBlockers.length === 1 ? 'requirement' : 'requirements'}
                        </summary>
                        <ul id="review-readiness-reasons">
                          {readyToListBlockers.map((reason) => (
                            <li key={reason}>{reason}</li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="tcvs-workbench-inspector-content">
                <div className="tcvs-workbench-media">
                  {autoStraightenNote !== null ? (
                    <p className="tcvs-notice" role="status">
                      {autoStraightenNote}
                    </p>
                  ) : null}
                  {autoStraightenError !== null ? (
                    <p className="tcvs-error" role="alert">
                      {autoStraightenError}
                    </p>
                  ) : null}
                  <div className="tcvs-workbench-images">
                    {(['front', 'back'] as const).map((side) => {
                      const url = active.imageUrls?.[side];
                      return url === undefined ? null : (
                        <div className="tcvs-workbench-image-side" key={side}>
                          <span className="tcvs-workbench-image-label">
                            {side === 'front' ? 'Front' : 'Back'}
                          </span>
                          <ReviewImageRotationEditor
                            side={side}
                            rotationDegrees={imageRotations[side]}
                            alignmentGrid={imageAlignmentGrid}
                            busySide={imageAdjustmentBusySide}
                            onRotationChange={changeImageRotation}
                          >
                            {(rotationDegrees, alignmentGrid) => (
                              <button
                                type="button"
                                className="tcvs-workbench-image-open"
                                aria-label={`Open ${side} card image review`}
                                disabled={imageAdjustmentBusySide !== null}
                                onClick={openImagePopout}
                              >
                                <MagnifiableCardImage
                                  key={`${currentVisit.key}|${currentVisit.sequence}|${side}|${url}`}
                                  src={url}
                                  alt={`${side} card`}
                                  imageClassName="tcvs-workbench-image-main"
                                  rotationDegrees={rotationDegrees}
                                  alignmentGrid={alignmentGrid}
                                  onImageLoadChange={(loaded) =>
                                    noteDisplayedImageLoad(side, url, loaded)
                                  }
                                />
                              </button>
                            )}
                          </ReviewImageRotationEditor>
                        </div>
                      );
                    })}
                  </div>
                  <div className="tcvs-workbench-image-batch-tools">
                    <div className="tcvs-workbench-image-batch-tools-leading">
                      <label>
                        <input
                          type="checkbox"
                          checked={batchAutoStraighten}
                          disabled={busy !== null}
                          onChange={(event) => setBatchAutoStraighten(event.currentTarget.checked)}
                        />
                        <span>Auto straighten</span>
                      </label>
                      <ReviewImageGridAction
                        alignmentGrid={imageAlignmentGrid}
                        busy={busy !== null || imageAdjustmentBusySide !== null}
                        onAlignmentGridChange={setImageAlignmentGrid}
                      />
                    </div>
                    <div className="tcvs-preliminary-image-review-status" aria-live="polite">
                      {active.imageQualityStatus === 'ACCEPTED' &&
                      active.pendingImageReplacement === undefined ? (
                        <span>
                          Images{' '}
                          <strong className="tcvs-preliminary-image-review-approved">
                            approved
                          </strong>
                        </span>
                      ) : active.preliminaryImageReview === undefined ? null : (
                        <span>
                          Preliminary images{' '}
                          <strong
                            className={
                              active.preliminaryImageReview.status === 'APPROVED'
                                ? 'tcvs-preliminary-image-review-approved'
                                : 'tcvs-preliminary-image-review-issue'
                            }
                          >
                            {active.preliminaryImageReview.status === 'APPROVED'
                              ? 'Approved'
                              : 'Issue noted'}
                          </strong>
                        </span>
                      )}
                    </div>
                    <div className="tcvs-workbench-image-batch-tools-trailing">
                      {imageAdjustmentBusySide !== null ? (
                        <span className="tcvs-muted" role="status">
                          Checking {imageAdjustmentBusySide} image…
                        </span>
                      ) : null}
                      {workbenchState(active) === 'awaiting-images' ? (
                        <button
                          type="button"
                          className="tcvs-btn-secondary"
                          disabled={
                            busy !== null ||
                            imageAdjustmentBusySide !== null ||
                            !photoReview.ready ||
                            !displayedImagesLoaded ||
                            imageRotations.front !== 0 ||
                            imageRotations.back !== 0 ||
                            (active.imageUrls?.front === undefined &&
                              active.imageUrls?.back === undefined)
                          }
                          onClick={() => void updateImageIssue(false, true)}
                        >
                          {busy === 'image-quality' ? 'Approving images…' : 'Approve images'}
                        </button>
                      ) : null}
                      <label className="tcvs-image-issue-toggle tcvs-review-image-issue-leading">
                        <input
                          type="checkbox"
                          checked={imageIssue}
                          disabled={busy !== null || imageAdjustmentBusySide !== null}
                          onChange={(event) => void updateImageIssue(event.target.checked)}
                        />
                        <span>
                          {busy === 'image-quality' ? 'Saving image issue…' : 'Image issue'}
                        </span>
                      </label>
                      <button
                        type="button"
                        className="tcvs-linklike tcvs-review-image-reset"
                        aria-label="Reset front and back image rotations"
                        disabled={
                          imageAdjustmentBusySide !== null ||
                          (imageRotations.front === 0 && imageRotations.back === 0)
                        }
                        onClick={resetImageRotations}
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                  <Link
                    to={`/catalog/${encodeURIComponent(active.cardId)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tcvs-linklike tcvs-workbench-full-card-link"
                  >
                    Open full card details
                  </Link>
                </div>
                <div className="tcvs-workbench-identity">
                  {pendingAssistedTreatments ? (
                    <section aria-label="Required treatment for matched card">
                      <p>
                        This match needs an eligible treatment. Choosing one opens a full identity
                        comparison; your current identity is unchanged.
                      </p>
                      {pendingAssistedTreatments.scope !== checklistChoiceScope ? (
                        <p role="alert">The review changed. Cancel and search again.</p>
                      ) : null}
                      {pendingAssistedTreatments.choices.map((choice) => (
                        <button
                          key={choice.id}
                          type="button"
                          disabled={pendingAssistedTreatments.scope !== checklistChoiceScope}
                          onClick={choice.choose}
                        >
                          {choice.label}
                        </button>
                      ))}
                      <button type="button" onClick={() => setPendingAssistedTreatments(null)}>
                        Cancel treatment choice
                      </button>
                    </section>
                  ) : null}

                  {variantWarning}
                  {pendingChecklistIdentity ? (
                    <section aria-label="Review proposed checklist identity">
                      <ChecklistSelectionChanges
                        beforeCard={active.identity ?? null}
                        beforeDraft={active.identityReviewDraft?.identity ?? null}
                        beforeLocal={identityDraft ?? undefined}
                        proposed={pendingChecklistIdentity.proposed}
                      />
                      {pendingChecklistIdentity.scope !== checklistChoiceScope ? (
                        <p role="alert">
                          The review or source changed. Cancel and choose again; the proposal was
                          not accepted.
                        </p>
                      ) : null}
                      <label>
                        <input
                          type="checkbox"
                          checked={checklistIdentityAccepted}
                          onChange={(event) => setChecklistIdentityAccepted(event.target.checked)}
                        />
                        I reviewed every displayed identity change, including removed physical
                        serial information, and accept this exact choice.
                      </label>
                      <button
                        type="button"
                        disabled={
                          !checklistIdentityAccepted ||
                          pendingChecklistIdentity.scope !== checklistChoiceScope
                        }
                        onClick={() => {
                          const pending = pendingChecklistIdentity;
                          if (
                            pending.scope !== latestChecklistChoiceScope.current ||
                            latestChecklistLocalEdits.current()
                          )
                            return;
                          if (listingTitleOverride === autoGeneratedListingTitle)
                            setListingTitleOverride(null);
                          setPendingChecklistIdentity(null);
                          setChecklistIdentityAccepted(false);
                          pending.commit();
                        }}
                      >
                        Use this checklist identity
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPendingChecklistIdentity(null);
                          setChecklistIdentityAccepted(false);
                        }}
                      >
                        Cancel identity changes
                      </button>
                    </section>
                  ) : null}
                  {importedResearch ? (
                    <details className="tcvs-imported-identification">
                      <summary>
                        Imported identification ·{' '}
                        {importedResearch.importMode === 'MANUAL_CONFIRMED'
                          ? 'manually confirmed'
                          : 'review draft'}
                      </summary>
                      <p>
                        {importedResearch.producer.tool} · run {importedResearch.runId}
                      </p>
                      {importedResearch.confidence === undefined ? null : (
                        <p>
                          Reported confidence: {Math.round(importedResearch.confidence * 100)}%
                          (advisory)
                        </p>
                      )}
                      <ImportedResearchDetails
                        key={`${batchId}|${active.cardId}|${importedResearch.importId}`}
                        research={importedResearch}
                        showPrice={false}
                        catalogWarnings={importedResearch.catalogWarnings}
                        disabled={importedResearchDisabled}
                        disabledFields={disabledImportedFields}
                        onAdopt={adoptImportedListing}
                        onReviewCatalogIssue={
                          manualOverrideEnabled || checklistIssueBusy
                            ? undefined
                            : (type, note) => {
                                if (
                                  reviewVisit.current !== currentVisit ||
                                  importedResearchDisabled ||
                                  checklistIssueBusy
                                )
                                  return;
                                setChecklistIssueType(type);
                                setChecklistIssueNote(note);
                                setChecklistIssueOpen(true);
                                setReviewPanelTab('identification');
                              }
                        }
                      />
                      {importedListing && selectedListingTemplate === undefined ? (
                        <p className="tcvs-muted">
                          Choose a checklist and listing template before using suggested title or
                          description.
                        </p>
                      ) : null}
                      {importedListing?.title !== undefined &&
                      importedListing.title.length > EBAY_LISTING_BASE_TITLE_MAX_LENGTH ? (
                        <p className="tcvs-muted">
                          Suggested title exceeds the {EBAY_LISTING_BASE_TITLE_MAX_LENGTH}-character
                          listing limit. Enter a shorter title in the listing editor.
                        </p>
                      ) : null}
                      {importedListing?.description !== undefined &&
                      escapeImportedDescription(importedListing.description).length >
                        LISTING_DESCRIPTION_MAX_LENGTH ? (
                        <p className="tcvs-muted">
                          Suggested description exceeds the listing limit after plain-text escaping.
                          Shorten it before adoption.
                        </p>
                      ) : null}
                      {importedListing?.features?.some(
                        (feature) => !supportedImportedFeatures.has(feature),
                      ) ? (
                        <p className="tcvs-muted">
                          Suggested features include values unavailable for this listing category.
                          Review features manually.
                        </p>
                      ) : null}
                      <ul>
                        {importedResearch.evidence?.map((note, index) => (
                          <li key={index}>{note}</li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                  {listingEditLock !== null ? (
                    <aside className="tcvs-listed-card-lock" role="status">
                      <div>
                        <strong>
                          {listingEditLock.state === 'SOLD'
                            ? 'Sold eBay card: review fields locked'
                            : 'Active eBay listing: review fields locked'}
                        </strong>
                        <p>
                          Ordinary review changes cannot re-queue this card or alter its eBay
                          listing.
                          {listingEditLock.externalListingId === undefined ? null : (
                            <>
                              {' '}
                              <a
                                href={`https://www.ebay.com/itm/${encodeURIComponent(listingEditLock.externalListingId)}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View eBay listing
                              </a>
                            </>
                          )}
                        </p>
                      </div>
                      {isAdmin && !identityCorrectionMode ? (
                        <button
                          type="button"
                          className="tcvs-btn-secondary tcvs-btn-compact"
                          disabled={busy !== null}
                          onClick={() => {
                            setIdentityCorrectionMode(true);
                            setReviewPanelTab('identification');
                            setError(null);
                          }}
                        >
                          Correct TCVS identity
                        </button>
                      ) : null}
                      {identityCorrectionMode ? (
                        <div className="tcvs-listed-card-correction">
                          <label>
                            <span>Correction reason</span>
                            <textarea
                              rows={2}
                              maxLength={500}
                              value={identityCorrectionReason}
                              placeholder="Explain why the verified identity is being corrected."
                              onChange={(event) =>
                                setIdentityCorrectionReason(event.currentTarget.value)
                              }
                            />
                          </label>
                          <div className="tcvs-workbench-review-actions">
                            <button
                              type="button"
                              disabled={
                                busy !== null ||
                                identityCorrectionReason.trim().length < 5 ||
                                (activeChecklistSourceSelection ??
                                  storedChecklistSourceSelection) === null
                              }
                              onClick={() => void applyListedCardIdentityCorrection()}
                            >
                              {busy === 'identity-correction'
                                ? 'Applying correction…'
                                : 'Apply identity correction'}
                            </button>
                            <button
                              type="button"
                              className="tcvs-btn-secondary"
                              disabled={listedCardReviewLocked || busy !== null}
                              onClick={() => {
                                setIdentityCorrectionMode(false);
                                setIdentityCorrectionReason('');
                                if (active !== null) {
                                  setIdentityDraft(identityDraftForCard(active));
                                  setIdentityDraftEdited(false);
                                }
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                          <p className="tcvs-muted">
                            This updates only TCVS identity and source lineage. It does not revise
                            eBay, change price, or place the card back in a listing queue.
                          </p>
                        </div>
                      ) : null}
                    </aside>
                  ) : null}
                  {active.captureHints?.thickCard === true ? (
                    <aside className="tcvs-capture-note" aria-label="Capture note">
                      <strong>Thick card</strong>
                      <span>Adjust shipping pricing accordingly</span>
                    </aside>
                  ) : null}
                  <nav className="tcvs-review-panel-tabs" aria-label="Card review sections">
                    <button
                      type="button"
                      aria-pressed={reviewPanelTab === 'identification'}
                      onClick={() => setReviewPanelTab('identification')}
                    >
                      Identification
                    </button>
                    <button
                      type="button"
                      aria-pressed={reviewPanelTab === 'pricing'}
                      onClick={() => setReviewPanelTab('pricing')}
                    >
                      Pricing
                    </button>
                  </nav>
                  {reviewPanelTab === 'identification' ? (
                    <>
                      <div className="tcvs-filterable-field">
                        <div className="tcvs-filterable-field-heading">
                          <span>Checklist for this card</span>
                          <button
                            type="button"
                            className="tcvs-link-button"
                            aria-expanded={cardLookupOpen}
                            disabled={listedCardReviewLocked || busy !== null}
                            onClick={() => setCardLookupOpen((current) => !current)}
                          >
                            Refine matches
                          </button>
                          {!manualOverrideEnabled ? (
                            <button
                              type="button"
                              className="tcvs-link-button"
                              aria-label="Re-check this card against the latest checklists"
                              disabled={
                                listedCardReviewLocked ||
                                busy !== null ||
                                suggestingCardId !== null ||
                                checklists === null ||
                                checklists.length === 0
                              }
                              onClick={() =>
                                void suggestActiveFromOcr(
                                  active,
                                  AUTOMATIC_OCR_SUGGESTION_MAX_ATTEMPTS,
                                  true,
                                )
                              }
                            >
                              {suggestingCardId === active.cardId
                                ? 'Re-checking…'
                                : 'Re-check latest checklists'}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="tcvs-link-button"
                            disabled={listedCardReviewLocked || busy !== null}
                            onClick={() => {
                              const nextEnabled = !manualOverrideEnabled;
                              setManualOverrideEnabled(nextEnabled);
                              setReplacementIntent(
                                nextEnabled && active.reviewChecklistSelection
                                  ? {
                                      cardId: active.cardId,
                                      revision: {
                                        checklistId: active.reviewChecklistSelection.checklistId,
                                        selectedAt: active.reviewChecklistSelection.selectedAt,
                                      },
                                      kind: 'MANUAL',
                                    }
                                  : null,
                              );
                              setManualSourceSelection(
                                nextEnabled
                                  ? (unresolvedSelection ?? manualSourceSelection)
                                  : undefined,
                              );
                              setManualClassificationConfirmed(false);
                              if (
                                nextEnabled &&
                                selectedChecklistSourceEntry?.unresolvedClassification !== undefined
                              ) {
                                setIdentityDraft((current) => {
                                  if (current === null) return current;
                                  const known = { ...current };
                                  if (isChecklistPlaceholderLabel(known.subset))
                                    delete known.subset;
                                  if (isChecklistPlaceholderLabel(known.variation))
                                    delete known.variation;
                                  return known;
                                });
                              }
                              setManualOverrideReason(
                                nextEnabled ? (active.manualIdentityOverride?.reason ?? '') : '',
                              );
                              setIdentityDraftEdited(true);
                              setListingTitleOverride(null);
                            }}
                          >
                            {manualOverrideEnabled ? 'Use checklist' : 'Use manual override'}
                          </button>
                          {
                            <button
                              type="button"
                              className="tcvs-link-button tcvs-checklist-issue-trigger"
                              aria-expanded={checklistIssueOpen}
                              disabled={checklistIssueBusy}
                              onClick={() => {
                                setChecklistIssueOpen((open) => !open);
                                if (
                                  !checklistIssueNote &&
                                  manualOverrideEnabled &&
                                  manualOverrideReason.trim()
                                )
                                  setChecklistIssueNote(manualOverrideReason);
                              }}
                            >
                              {checklistIssueOpen
                                ? 'Cancel checklist issue'
                                : 'Flag checklist issue'}
                            </button>
                          }
                        </div>
                        {cardLookupOpen && identityDraft !== null ? (
                          <CardLookupPanel
                            key={currentVisit.sequence}
                            initialFields={{
                              category: identityDraft.category,
                              number: identityDraft.number,
                              name: identityDraft.name,
                              year: identityDraft.year,
                              brand: identityDraft.brand,
                              product: identityDraft.set,
                            }}
                            categoryOptions={(checklists ?? []).map(
                              (checklist) => checklist.category,
                            )}
                            search={catalog.checklistCardSearch}
                            onApply={applyAssistedCardMatch}
                            disabled={listedCardReviewLocked || busy !== null}
                            onClose={() => setCardLookupOpen(false)}
                          />
                        ) : (
                          <>
                            <span className="tcvs-muted">Browse checklists</span>
                            <FilterableSelect
                              value={activeChecklistId ?? ''}
                              disabled={
                                listedCardReviewLocked || busy !== null || manualOverrideEnabled
                              }
                              ariaLabel="Checklist for this card"
                              placeholder="Search all checklists…"
                              options={cardChecklistSelectOptions(
                                checklists ?? [],
                                batchChecklistId,
                              )}
                              loading={checklistSearchLoading}
                              searchError={checklistSearch.error}
                              hasMore={checklistSearch.hasMore}
                              onLoadMore={checklistSearch.loadMore}
                              onRetry={checklistSearch.retry}
                              matchMode="checklist-fuzzy"
                              onQueryChange={setChecklistSearchQuery}
                              onChange={selectChecklistForActiveCard}
                            />
                          </>
                        )}
                      </div>
                      {unresolvedClassification !== undefined ? (
                        <section role="note" aria-label="Unresolved checklist classification">
                          <strong>Subset or parallel needs review</strong>
                          <p>
                            The source contains a real item with an unresolved classification. Known
                            values are retained. Save a draft, or use manual override to enter and
                            confirm what you can establish. Do not guess missing values.
                          </p>
                          <p>Source label: {unresolvedClassification.sourceLabel}</p>
                          {unresolvedClassification.sourceNotes ? (
                            <details>
                              <summary>Source notes</summary>
                              <p>{unresolvedClassification.sourceNotes}</p>
                            </details>
                          ) : null}
                        </section>
                      ) : null}
                      {manualOverrideEnabled ? (
                        <section
                          className="tcvs-manual-identity-override"
                          aria-label="Manual identity override"
                        >
                          <strong>Manual override</strong>
                          {manualEvidenceSelection !== undefined ? (
                            <label>
                              <input
                                type="checkbox"
                                checked={manualClassificationConfirmed}
                                onChange={(event) =>
                                  setManualClassificationConfirmed(event.target.checked)
                                }
                              />
                              I have reviewed and completed the uncertain classification using
                              evidence outside this checklist.
                            </label>
                          ) : null}
                          <p className="tcvs-muted">
                            Enter the known identity below. This card will not have checklist-based
                            pricing comparisons until it is linked to an imported checklist.
                          </p>
                          <label>
                            <span>Reason</span>
                            <textarea
                              value={manualOverrideReason}
                              maxLength={500}
                              disabled={listedCardReviewLocked || busy !== null}
                              placeholder="Why is a checklist-backed identity unavailable?"
                              onChange={(event) => setManualOverrideReason(event.target.value)}
                            />
                          </label>
                          {manualOverrideReason.trim().length > 0 &&
                          manualOverrideReason.trim().length < 5 ? (
                            <p className="tcvs-error">Enter at least five characters.</p>
                          ) : null}
                        </section>
                      ) : null}
                      {active.marketplaceSource?.title !== undefined ? (
                        <p className="tcvs-muted">
                          <strong>eBay listing title:</strong>{' '}
                          <a
                            href={`https://www.ebay.com/itm/${encodeURIComponent(active.marketplaceSource.externalListingId)}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {active.marketplaceSource.title}
                          </a>
                        </p>
                      ) : null}
                      {showMatchDetails &&
                      !manualOverrideEnabled &&
                      activeRankedChecklistCandidates.length > 0 ? (
                        <section
                          className="tcvs-checklist-candidate-review"
                          aria-label="Suggested checklist matches"
                        >
                          <label>
                            <span>Suggested matches</span>
                            <select
                              value={activeRankedChecklistId}
                              disabled={listedCardReviewLocked || busy !== null}
                              onChange={(event) => selectChecklistForActiveCard(event.target.value)}
                            >
                              <option value="" disabled>
                                Choose a suggested match
                              </option>
                              {activeRankedChecklistCandidates.map((candidate, index) => (
                                <option key={candidate.checklistId} value={candidate.checklistId}>
                                  {index + 1}. {checklistProductLabel(candidate)} ·{' '}
                                  {checklistCandidateStrength(candidate.confidence)}
                                </option>
                              ))}
                            </select>
                          </label>
                          <p className="tcvs-muted">
                            Ranked from stored card evidence. Review the selection before
                            submitting; suggestions never verify identity automatically.
                          </p>
                          <p className="tcvs-muted">
                            {activeRankedChecklistCandidates
                              .find((candidate) => candidate.checklistId === activeChecklistId)
                              ?.evidence.join(' · ')}
                          </p>
                        </section>
                      ) : null}
                      {checklistIssueDelivery ? (
                        <p role="status">{checklistIssueDelivery.message}</p>
                      ) : null}
                      {selectedChecklistEntry &&
                      !checklistEntryPolicy(selectedChecklistEntry) &&
                      !retainVerifiedIdentity ? (
                        <p role="note">
                          Current source policy is unavailable or unknown. Retry the checklist
                          lookup or use an explicit manual override. Existing review facts are
                          preserved.
                        </p>
                      ) : null}
                      {checklistSelectionConflictMessages(
                        selectedChecklistEntry,
                        activeParallelDefinitions,
                      ).map((message) => (
                        <p role="note" key={message}>
                          {message}
                        </p>
                      ))}
                      {selectedChecklistEntry?.selectionPolicy?.plainVersion.decision ===
                      'requires-parallel' ? (
                        <p role="note">
                          This subset has no plain version. Choose one eligible treatment; no
                          parallel is selected automatically.
                        </p>
                      ) : null}
                      {savedHierarchy?.parallel &&
                      !displayedParallelOptions.some(
                        (option) => option.value === structuredParallelChoice,
                      ) ? (
                        <p role="note">
                          The saved treatment is historical and is not currently eligible for a new
                          source selection. Keep untouched verified identity, choose a current
                          eligible treatment, or use explicit manual review.
                        </p>
                      ) : null}
                      {subsetCatalog.policyError ? (
                        <p role="note">
                          {subsetCatalog.policyError}
                          <button type="button" onClick={subsetCatalog.retryPolicy}>
                            Retry subset policy
                          </button>
                        </p>
                      ) : null}
                      {checklistIssueOpen ? (
                        <section className="tcvs-checklist-issue-form">
                          {checklistIssueGuidanceLoading ? (
                            <p className="tcvs-muted">Checking known checklist guidance…</p>
                          ) : checklistIssueGuidance.length > 0 ? (
                            <div>
                              <strong>Known guidance</strong>
                              {checklistIssueGuidance.map((guidance, index) => (
                                <p key={`${guidance.status}-${guidance.issueType}-${index}`}>
                                  <strong>
                                    {
                                      CHECKLIST_ISSUE_OPTIONS.find(
                                        (option) => option.value === guidance.issueType,
                                      )?.label
                                    }
                                  </strong>
                                  {` (${guidance.status === 'OPEN' ? 'open' : 'resolved'}): ${guidance.guidance}`}
                                </p>
                              ))}
                            </div>
                          ) : null}
                          <label>
                            <span>Issue</span>
                            <select
                              value={checklistIssueType}
                              disabled={checklistIssueBusy}
                              onChange={(event) =>
                                setChecklistIssueType(event.target.value as ChecklistIssueType)
                              }
                            >
                              {CHECKLIST_ISSUE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <span>Details for checklist engineering</span>
                            <textarea
                              value={checklistIssueNote}
                              maxLength={1_000}
                              rows={3}
                              disabled={checklistIssueBusy}
                              placeholder="Describe what is missing or incorrect."
                              onChange={(event) => {
                                setChecklistIssueNote(event.target.value);
                                if (checklistIssueDelivery?.status === 'SENT')
                                  setChecklistIssueDelivery(null);
                              }}
                            />
                          </label>
                          <button
                            type="button"
                            className="tcvs-btn-compact"
                            disabled={
                              checklistIssueBusy ||
                              checklistIssueDelivery?.status === 'UNKNOWN' ||
                              checklistIssueDelivery?.status === 'SENT'
                            }
                            onClick={() => void reportChecklistIssue()}
                          >
                            {checklistIssueBusy ? 'Sending…' : 'Send to checklist engineering'}
                          </button>
                        </section>
                      ) : null}
                      {!manualOverrideEnabled && suggestingCardId === active.cardId ? (
                        <p className="tcvs-muted">
                          {active.marketplaceSource?.title === undefined
                            ? 'Checking stored OCR…'
                            : 'Checking eBay listing and stored OCR…'}
                        </p>
                      ) : !manualOverrideEnabled &&
                        cardSuggestionNote !== null &&
                        (cardSuggestionError || showMatchDetails) ? (
                        <p className={cardSuggestionError ? 'tcvs-error' : 'tcvs-muted'}>
                          {cardSuggestionNote}
                        </p>
                      ) : showMatchDetails &&
                        !manualOverrideEnabled &&
                        active.checklistProductSuggestion?.candidates[0] !== undefined ? (
                        <p
                          className="tcvs-muted"
                          title={active.checklistProductSuggestion.candidates[0].evidence.join(
                            ' · ',
                          )}
                        >
                          {active.checklistProductSuggestion.candidates[0].evidence.includes(
                            'eBay listing title',
                          )
                            ? 'eBay listing suggestion: '
                            : 'Card OCR suggestion: '}
                          {active.checklistProductSuggestion.candidates[0].product.year}{' '}
                          {active.checklistProductSuggestion.candidates[0].product.brand}{' '}
                          {active.checklistProductSuggestion.candidates[0].product.product} (
                          {Math.round(
                            active.checklistProductSuggestion.candidates[0].confidence * 100,
                          )}
                          %)
                        </p>
                      ) : null}
                      {identityForDisplay(active) !== undefined ? (
                        <p>{formatIdentity(identityForDisplay(active) as CardIdentity)}</p>
                      ) : null}
                      {!manualOverrideEnabled &&
                      activeReviewCondition === 'needs-checklist' &&
                      autoOcrAttempted.current.has(active.cardId) &&
                      suggestingCardId !== active.cardId ? (
                        <p className="tcvs-error">
                          No checklist is selected for this card. Choose one above or enter the
                          identity manually.
                        </p>
                      ) : activeReviewCondition === 'missing-number' ? (
                        <p className="tcvs-muted">
                          The card number is missing. You may enter it below or submit the identity
                          without one.
                        </p>
                      ) : activeReviewCondition === 'no-match' &&
                        activeParallelMatch?.tier !== 2 &&
                        !resolving.has(active.cardId) &&
                        candidates[activeResolutionKey] !== undefined ? (
                        <p className="tcvs-error">
                          No checklist match was found. Correct the identity or search a different
                          checklist entry.
                        </p>
                      ) : null}
                      {manualOverrideEnabled || activeParallelMatch?.tier === 3 ? null : (
                        <ChecklistParallelFallback
                          match={activeParallelMatch}
                          policy={subsetCatalog.subsetPolicy}
                        />
                      )}
                      {!manualOverrideEnabled &&
                      activeReviewCondition === 'parallel-decision' &&
                      !showStructuredSelectors ? (
                        <div className="tcvs-filterable-field">
                          <span>Exact parallel</span>
                          <FilterableSelect
                            value={pendingParallel}
                            disabled={
                              listedCardReviewLocked ||
                              resolving.has(active.cardId) ||
                              busy !== null
                            }
                            ariaLabel="Exact parallel"
                            placeholder={
                              resolving.has(active.cardId)
                                ? 'Loading parallels…'
                                : 'Select variation'
                            }
                            options={(candidates[resolveKey(active.cardId)] ?? []).reduce<
                              FilterableSelectOption[]
                            >((options, entry) => {
                              const value = variationOf(entry);
                              if (
                                value !== '' &&
                                !options.some((option) => option.value === value)
                              ) {
                                options.push({
                                  value,
                                  label: parallelLabel(entry),
                                  searchText:
                                    entry.identity.serialTo === undefined
                                      ? undefined
                                      : String(entry.identity.serialTo),
                                });
                              }
                              return options;
                            }, [])}
                            onChange={chooseActiveParallel}
                          />
                        </div>
                      ) : null}
                      {editingIdentityFor === active.cardId && identityDraft !== null ? (
                        <section className="tcvs-workbench-review-editor">
                          {retainVerifiedIdentity ? (
                            <p className="tcvs-muted" role="status">
                              Retaining the saved verified identity. Review changes to photos,
                              presentation and price do not require its original checklist row.
                            </p>
                          ) : null}

                          {!manualOverrideEnabled && structuredResolutionPending ? (
                            <p className="tcvs-muted">Loading checklist options…</p>
                          ) : null}
                          {!manualOverrideEnabled && showStructuredSelectors ? (
                            <div className="tcvs-workbench-checklist-choice">
                              <div className="tcvs-filterable-field">
                                <span>Set/subset</span>
                                <FilterableSelect
                                  value={
                                    activeChecklistIsSourceBacked
                                      ? selectedSubsetValue
                                      : selectedCandidateIndex
                                  }
                                  disabled={
                                    listedCardReviewLocked ||
                                    busy !== null ||
                                    numberLookupBusy ||
                                    structuredResolutionPending
                                  }
                                  ariaLabel="Set or subset"
                                  showAllOnFocus={activeChecklistIsSourceBacked}
                                  placeholder={
                                    structuredResolutionPending
                                      ? 'Loading checklist options…'
                                      : 'Select set/subset…'
                                  }
                                  loading={activeChecklistIsSourceBacked && subsetCatalog.loading}
                                  searchError={
                                    activeChecklistIsSourceBacked ? subsetCatalog.error : undefined
                                  }
                                  onRetry={
                                    activeChecklistIsSourceBacked ? subsetCatalog.retry : undefined
                                  }
                                  options={
                                    activeChecklistIsSourceBacked
                                      ? subsetOptions
                                      : activeChecklistSubsetChoices.map(({ entry, index }) => ({
                                          value: String(index),
                                          label: checklistCandidateLabel(entry, activeCandidates),
                                        }))
                                  }
                                  onChange={(value) =>
                                    activeChecklistIsSourceBacked
                                      ? void chooseChecklistSubset(value)
                                      : chooseChecklistCandidate(value)
                                  }
                                />
                              </div>
                              {activeChecklistIsSourceBacked &&
                              (selectedSubsetRosterChoices.length > 1 ||
                                (savedHierarchy && selectedSubsetRosterChoices.length > 0)) ? (
                                <div className="tcvs-filterable-field">
                                  <span>Matching checklist card</span>
                                  <FilterableSelect
                                    value={selectedCandidateIndex}
                                    ariaLabel="Matching checklist card"
                                    disabled={
                                      listedCardReviewLocked ||
                                      busy !== null ||
                                      structuredResolutionPending
                                    }
                                    placeholder="Select the matching card…"
                                    options={selectedSubsetRosterChoices.map(
                                      ({ entry, index }) => ({
                                        value: String(index),
                                        label: checklistCandidateLabel(entry, activeCandidates),
                                      }),
                                    )}
                                    onChange={chooseChecklistCandidate}
                                  />
                                </div>
                              ) : null}
                              <div className="tcvs-filterable-field">
                                <span>Parallel</span>
                                <FilterableSelect
                                  value={structuredParallelChoice ?? ''}
                                  disabled={
                                    listedCardReviewLocked ||
                                    busy !== null ||
                                    structuredResolutionPending ||
                                    (selectedChecklistEntry === undefined &&
                                      activeParallelMatch?.tier !== 2)
                                  }
                                  ariaLabel="Parallel"
                                  placeholder={
                                    structuredResolutionPending
                                      ? 'Loading parallels…'
                                      : identityDraft.variation
                                        ? `${identityDraft.variation} (awaiting checklist options)`
                                        : 'Select parallel…'
                                  }
                                  options={displayedParallelOptions.map((option) => {
                                    const definition = applicableDefinitions.find(
                                      (candidate) => candidate.definitionId === option.value,
                                    );
                                    return {
                                      ...option,
                                      searchText:
                                        definition === undefined
                                          ? undefined
                                          : [
                                              definition.serialTo,
                                              definition.printRunText,
                                              definition.applicability.kind,
                                            ]
                                              .filter((value) => value !== undefined)
                                              .join(' '),
                                    };
                                  })}
                                  onChange={chooseStructuredParallel}
                                />
                              </div>
                              {resolutionErrors[activeResolutionKey] !== undefined ? (
                                <div role="alert">
                                  <p className="tcvs-error">
                                    Checklist options could not be loaded. Your saved selection is
                                    unchanged.
                                  </p>
                                  <button
                                    type="button"
                                    disabled={structuredResolutionPending || busy !== null}
                                    onClick={() => void loadCandidates(active.cardId, true)}
                                  >
                                    Retry checklist options
                                  </button>
                                </div>
                              ) : !structuredResolutionPending && !structuredSelectionRequired ? (
                                <div>
                                  <p className="tcvs-muted">
                                    No applicable parallel options loaded. Current parallel:{' '}
                                    {identityDraft.variation || 'None'}.
                                  </p>
                                  <button
                                    type="button"
                                    disabled={busy !== null}
                                    onClick={() => void loadCandidates(active.cardId, true)}
                                  >
                                    Retry checklist options
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          <IdentityForm
                            key={active.cardId}
                            value={identityDraft}
                            onChange={(next) => {
                              invalidateVariantOptions();
                              setIdentityDraft(next);
                              setIdentityDraftEdited(true);
                            }}
                            disabled={listedCardReviewLocked || busy !== null}
                            hiddenFields={
                              manualOverrideEnabled ||
                              (active.identityReviewDraft?.importedFrom !== undefined &&
                                activeChecklistId === null)
                                ? ([] as const)
                                : (['category', 'set', 'brand', 'subset', 'variation'] as const)
                            }
                            compact
                            numberLookup={
                              manualOverrideEnabled
                                ? undefined
                                : {
                                    busy: numberLookupBusy,
                                    onLookup: (number) => void lookupCardNumber(number),
                                  }
                            }
                            serialNumberedHint={active.captureHints?.serialNumbered === true}
                          />
                          {showIdentityDraftValidation(identityDraftEdited, identityDraft) ? (
                            <p className="tcvs-error">Set and name are required.</p>
                          ) : null}
                          {!identitySerialIsValid ? (
                            <p className="tcvs-error">
                              Enter a valid serial number that is no greater than the run size.
                            </p>
                          ) : null}
                          <div className="tcvs-workbench-review-actions">
                            <button
                              type="button"
                              className="tcvs-btn-secondary"
                              disabled={busy !== null}
                              onClick={resetIdentityReview}
                            >
                              Reset fields
                            </button>
                          </div>
                        </section>
                      ) : null}
                      {editingIdentityFor === active.cardId && identityDraft !== null ? (
                        <CardPresentationEditor
                          compact
                          value={cardPresentationDraft}
                          disabled={
                            listingEditLock !== null ||
                            busy !== null ||
                            listingCopyRestorationPending
                          }
                          onChange={(value) => {
                            setCardPresentationDraft(value);
                            setIdentityDraftEdited(true);
                          }}
                        />
                      ) : null}
                      {editingIdentityFor === active.cardId && identityDraft !== null ? (
                        <section className="tcvs-ebay-features" aria-label="eBay card features">
                          <div className="tcvs-ebay-features-heading">
                            <div>
                              <strong>eBay features</strong>
                              <span className="tcvs-muted">
                                {itemSpecificsCategory === null
                                  ? 'Choose listing category'
                                  : itemSpecificsCategory === 'CCG_SINGLES'
                                    ? 'Collectible card game single'
                                    : itemSpecificsCategory === 'SPORT_SINGLES'
                                      ? 'Sport single'
                                      : 'Non-sport single'}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="tcvs-btn-secondary tcvs-btn-compact"
                              disabled={
                                listingEditLock !== null ||
                                busy !== null ||
                                listingCopyRestorationPending
                              }
                              onClick={() => setItemDetailsMode('FEATURES')}
                            >
                              Edit features ({itemSpecifics.Features?.length ?? 0})
                            </button>
                          </div>
                          {(itemSpecifics.Features?.length ?? 0) > 0 ? (
                            <div className="tcvs-ebay-feature-summary">
                              {itemSpecifics.Features?.map((feature) => (
                                <span key={feature}>{feature}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="tcvs-muted">No features selected</span>
                          )}
                        </section>
                      ) : null}
                      {editingIdentityFor === active.cardId && identityDraft !== null ? (
                        <section className="tcvs-listing-copy-preview">
                          <div className="tcvs-listing-copy-heading">
                            <h4>Listing copy</h4>
                            <button
                              type="button"
                              className="tcvs-btn-secondary tcvs-btn-compact"
                              disabled={
                                listingEditLock !== null ||
                                busy !== null ||
                                listingCopyRestorationPending ||
                                (!manualOverrideEnabled && activeChecklistId === null)
                              }
                              onClick={() => setItemDetailsMode('ALL')}
                            >
                              eBay item details ({Object.keys(itemSpecifics).length})
                            </button>
                          </div>
                          {!manualOverrideEnabled && activeChecklistId === null ? (
                            <p className="tcvs-muted">
                              Select the card checklist to load listing templates.
                            </p>
                          ) : listingCopyRestorationPending ? (
                            <p className="tcvs-muted">Loading listing templates…</p>
                          ) : listingTemplates.length === 0 ? (
                            <>
                              <p className="tcvs-muted">
                                {listingTemplateAvailabilityMessage(
                                  assignedListingTemplateCount,
                                  listingTemplates.length,
                                )}
                              </p>
                              {assignedListingTemplateCount > 0 ? (
                                <Link to="/sales/templates" className="tcvs-linklike">
                                  Open listing templates
                                </Link>
                              ) : null}
                            </>
                          ) : (
                            <>
                              <div className="tcvs-workbench-listing-fields">
                                <label>
                                  <span className="tcvs-field-label-row">
                                    <span>Generated listing title</span>
                                    <span
                                      className={
                                        listingTitle.length > EBAY_LISTING_BASE_TITLE_MAX_LENGTH
                                          ? 'tcvs-error'
                                          : 'tcvs-muted'
                                      }
                                    >
                                      {listingTitle.length}/{EBAY_LISTING_BASE_TITLE_MAX_LENGTH}
                                    </span>
                                  </span>
                                  <input
                                    value={listingTitle}
                                    maxLength={EBAY_LISTING_BASE_TITLE_MAX_LENGTH}
                                    disabled={
                                      listingEditLock !== null ||
                                      busy !== null ||
                                      generatingDescription
                                    }
                                    onChange={(event) =>
                                      setListingTitleOverride(event.target.value)
                                    }
                                  />
                                </label>
                                {!listingCopyRestorationPending &&
                                pendingChecklistIdentity === null &&
                                requestedResearchTreatment(identityDraft) &&
                                !titleContainsFullTreatment(
                                  listingTitle,
                                  requestedResearchTreatment(identityDraft)!,
                                ) ? (
                                  <p role="note">
                                    This title omits the selected treatment:{' '}
                                    {requestedResearchTreatment(identityDraft)}. Review the title
                                    before listing.
                                  </p>
                                ) : null}
                                <label>
                                  <span className="tcvs-field-label-row">
                                    <span>Listing template</span>
                                    {selectedListingTemplate !== undefined &&
                                    activeChecklistId !== null &&
                                    !manualOverrideEnabled &&
                                    active !== null ? (
                                      <span className="tcvs-template-review-shortcuts">
                                        <Link
                                          to={reviewTemplateEditorHref({
                                            checklistId: activeChecklistId,
                                            sourceTemplateId: selectedListingTemplate.templateId,
                                            mode: 'edit',
                                            returnTo: `${location.pathname}?card=${encodeURIComponent(active.cardId)}`,
                                          })}
                                        >
                                          Edit template
                                        </Link>
                                        <button
                                          type="button"
                                          className="tcvs-link-button"
                                          disabled={
                                            listingEditLock !== null ||
                                            busy !== null ||
                                            listingCopyRestorationPending
                                          }
                                          onClick={() => {
                                            const cloneName = window
                                              .prompt(
                                                'Name for the cloned template',
                                                `${selectedListingTemplate.name} copy`,
                                              )
                                              ?.trim();
                                            if (cloneName === undefined || cloneName === '') return;
                                            navigate(
                                              reviewTemplateEditorHref({
                                                checklistId: activeChecklistId,
                                                sourceTemplateId:
                                                  selectedListingTemplate.templateId,
                                                mode: 'clone',
                                                cloneName,
                                                returnTo: `${location.pathname}?card=${encodeURIComponent(active.cardId)}`,
                                              }),
                                            );
                                          }}
                                        >
                                          Clone template
                                        </button>
                                      </span>
                                    ) : null}
                                  </span>
                                  <select
                                    value={selectedListingTemplateId}
                                    disabled={
                                      listingEditLock !== null ||
                                      busy !== null ||
                                      generatingDescription
                                    }
                                    onChange={(event) => {
                                      const templateId = event.target.value;
                                      setSelectedListingTemplateId(templateId);
                                      setGeneratedDescription(
                                        listingDescriptionForTemplate(
                                          undefined,
                                          listingTemplates.find(
                                            (template) => template.templateId === templateId,
                                          ),
                                          identityDraft ?? undefined,
                                          active ?? undefined,
                                        ),
                                      );
                                      setDescriptionUsesTemplateDefault(true);
                                      setDescriptionSourceTitle('');
                                      setListingTitleOverride(null);
                                    }}
                                  >
                                    {listingTemplates.map((template) => (
                                      <option key={template.templateId} value={template.templateId}>
                                        {template.name}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              </div>
                              {listingTemplateSource === 'DEFAULT' ? (
                                <p className="tcvs-muted tcvs-template-note">
                                  {manualOverrideEnabled
                                    ? 'Manual overrides use the global default listing template.'
                                    : 'Using the default template because this checklist has no assignment.'}
                                </p>
                              ) : null}

                              {listingTitle.length > EBAY_LISTING_BASE_TITLE_MAX_LENGTH ? (
                                <p className="tcvs-error">
                                  Shorten the listing title to {EBAY_LISTING_BASE_TITLE_MAX_LENGTH}{' '}
                                  characters or fewer before Ready to List.
                                </p>
                              ) : null}
                              {listingTitleOverride !== null ? (
                                <button
                                  type="button"
                                  className="tcvs-link-button"
                                  disabled={
                                    listingEditLock !== null ||
                                    busy !== null ||
                                    generatingDescription
                                  }
                                  onClick={() => setListingTitleOverride(null)}
                                >
                                  Use generated title
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="tcvs-btn-compact"
                                disabled={
                                  busy !== null ||
                                  listingEditLock !== null ||
                                  generatingDescription ||
                                  itemSpecificsCategory === null ||
                                  itemSpecificsCategory === 'CCG_SINGLES' ||
                                  listingTitle === '' ||
                                  listingTitle.length > EBAY_LISTING_BASE_TITLE_MAX_LENGTH
                                }
                                onClick={() => void generateDescription()}
                              >
                                {generatingDescription ? 'Generating…' : 'Generate description'}
                              </button>
                              {itemSpecificsCategory === 'CCG_SINGLES' ||
                              itemSpecificsCategory === null ? (
                                <p className="tcvs-muted">
                                  For CCG, use the template description or edit the copy, then save
                                  the reviewed details explicitly.
                                </p>
                              ) : null}
                              <label>
                                <span className="tcvs-field-label-row">
                                  <span>Listing description</span>
                                  <span className="tcvs-muted">
                                    {generatedDescription.length}/{LISTING_DESCRIPTION_MAX_LENGTH}
                                  </span>
                                </span>
                                <textarea
                                  rows={6}
                                  maxLength={LISTING_DESCRIPTION_MAX_LENGTH}
                                  value={generatedDescription}
                                  disabled={
                                    listingEditLock !== null ||
                                    busy !== null ||
                                    generatingDescription
                                  }
                                  onChange={(event) => {
                                    setGeneratedDescription(event.target.value);
                                    setDescriptionUsesTemplateDefault(false);
                                    setDescriptionSourceTitle('');
                                  }}
                                />
                              </label>
                              {generatedDescriptionIsStale ? (
                                <p className="tcvs-template-warning">
                                  Card fields changed after this description was generated.
                                </p>
                              ) : null}
                            </>
                          )}
                          {itemSpecificsCategory === 'CCG_SINGLES' ? (
                            <section aria-label="CCG listing review">
                              <p>
                                Saving or queueing explicitly reviews all displayed CCG details.
                                Values are never confirmed on load.
                              </p>
                              {currentCcgPolicy ? (
                                <CcgItemDetails
                                  policy={currentCcgPolicy}
                                  value={itemSpecifics}
                                  onChange={ccgSetSpecifics}
                                  disabled={
                                    busy !== null ||
                                    listingEditLock !== null ||
                                    listingCopyRestorationPending
                                  }
                                  currentResearch={
                                    active.importedIdentification?.listing?.itemSpecifics
                                  }
                                  draftResearch={
                                    active.identityReviewDraft?.importedFrom?.listing?.itemSpecifics
                                  }
                                  decisions={
                                    ccgDecisions.scope === ccgScope ? ccgDecisions.fields : {}
                                  }
                                  onUseResearch={(name, value) => {
                                    const next = { ...itemSpecifics, [name]: [value] };
                                    const scope = ccgReviewScopeKey({
                                      cardId: active.cardId,
                                      contextKey: JSON.stringify([
                                        ccgPolicyContext,
                                        identityDraft,
                                        manualOverrideEnabled,
                                        manualSourceSelection,
                                        listingTitle,
                                        generatedDescription,
                                      ]),
                                      policyReviewIdentity: ebayCategoryPolicyReviewIdentity(
                                        currentCcgPolicy.binding,
                                      ),
                                      specifics: next,
                                      presentation: cardPresentationDraft,
                                      templateId: selectedListingTemplateId,
                                    });
                                    setItemSpecifics(next);
                                    setCcgDecisions({
                                      scope,
                                      fields: {
                                        ...Object.fromEntries(
                                          Object.entries(ccgDecisions.fields).map(
                                            ([field, decision]) => [
                                              field,
                                              ccgDecisions.scope === ccgScope
                                                ? decision
                                                : { ...decision, confirmed: false },
                                            ],
                                          ),
                                        ),
                                        [name]: {
                                          value,
                                          origin: 'IMPORTED_RESEARCH',
                                          confirmed: true,
                                        },
                                      },
                                    });
                                  }}
                                />
                              ) : (
                                <p>
                                  Current policy: {ccgPolicy.state.status.toLowerCase()}. Reload
                                  before saving.
                                </p>
                              )}
                              {ccgCopyUnknownCard === active.cardId ||
                              ccgReadyUnknownCard === active.cardId ? (
                                <>
                                  <p>
                                    Listing save status is unconfirmed. Local edits are preserved.
                                  </p>
                                  <button
                                    type="button"
                                    disabled={busy !== null}
                                    onClick={() => void checkCcgListingStatus()}
                                  >
                                    Check listing status
                                  </button>
                                  {isAdmin ? (
                                    <Link to="/sales/ready-to-list">Open Queue for release</Link>
                                  ) : (
                                    <p>Ask an administrator to inspect Queue for release.</p>
                                  )}
                                </>
                              ) : null}
                              {ccgStatusNotice ? <p role="status">{ccgStatusNotice}</p> : null}
                              <button
                                type="button"
                                disabled={busy !== null || ccgPolicy.state.status === 'LOADING'}
                                onClick={ccgPolicy.reload}
                              >
                                Reload CCG policy
                              </button>
                              <p>
                                {ccgListingSavedScope === ccgScope
                                  ? 'Current listing details saved.'
                                  : 'Local CCG listing details are unsaved.'}
                              </p>
                              <button
                                type="button"
                                disabled={
                                  busy !== null ||
                                  !currentCcgPolicy ||
                                  listingCopyRestorationPending ||
                                  listingEditLock !== null ||
                                  listingTitle.trim() === '' ||
                                  listingTitle.length > EBAY_LISTING_BASE_TITLE_MAX_LENGTH
                                }
                                onClick={() => void saveReviewedCcgDetails()}
                              >
                                Save reviewed CCG details
                              </button>
                              <Link
                                to={`/catalog/${encodeURIComponent(active.cardId)}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Review identity in full card details
                              </Link>
                              <p>
                                Opening full details does not save these local listing edits. Return
                                and reload the card after identity review.
                              </p>
                            </section>
                          ) : null}
                          <EbayItemDetailsModal
                            open={itemDetailsMode !== null}
                            fieldName={itemDetailsMode === 'FEATURES' ? 'Features' : undefined}
                            category={itemSpecificsCategory}
                            policy={currentCcgPolicy}
                            value={itemSpecifics}
                            disabled={
                              listingEditLock !== null ||
                              busy !== null ||
                              listingCopyRestorationPending
                            }
                            onCategoryChange={(category) => {
                              setItemSpecificsCategory(category);
                              setItemSpecifics(
                                itemSpecificsDraftForReview(undefined, identityDraft, category),
                              );
                            }}
                            onChange={
                              itemSpecificsCategory === 'CCG_SINGLES'
                                ? ccgSetSpecifics
                                : setItemSpecifics
                            }
                            onClose={() => setItemDetailsMode(null)}
                          />
                        </section>
                      ) : null}
                    </>
                  ) : editingIdentityFor === active.cardId &&
                    identityDraft !== null &&
                    pricingDraft !== null ? (
                    <div className="tcvs-review-pricing-panel">
                      <CardResearchPricing
                        card={active}
                        identity={identityDraft}
                        onChanged={async () => {
                          await refresh();
                        }}
                        embedded
                        draft={pricingDraft}
                        onDraftChange={setPricingDraft}
                        submitWithReview
                        showPriceEditor={false}
                        disabled={
                          listingEditLock !== null || busy !== null || listingCopyRestorationPending
                        }
                        cardPresentation={cardPresentationDraft}
                      />
                      <SameParallelComparables
                        result={comparables}
                        loading={comparablesLoading}
                        error={comparablesError}
                        onPriceSelect={(amountMinor) => {
                          if (
                            listingEditLock !== null ||
                            listingCopyRestorationPending ||
                            busy !== null
                          )
                            return;
                          setPricingDraft((current) =>
                            current === null
                              ? current
                              : { ...current, price: centsToUsdInput(amountMinor) },
                          );
                        }}
                      />
                      <EbayActiveComparables
                        result={visibleEbayObservation ? ebayActiveComparables : null}
                        requestedTreatment={visibleEbayObservation?.treatment}
                        loading={ebayActiveComparablesLoading}
                        error={ebayActiveComparablesError}
                        canLookup={canLookupEbayPrices}
                        onLookup={() => void lookupEbayActiveComparables()}
                        onRefresh={() => void lookupEbayActiveComparables(true)}
                        onPriceSelect={(amountMinor) => {
                          if (
                            listingEditLock !== null ||
                            listingCopyRestorationPending ||
                            busy !== null ||
                            !visibleEbayObservation ||
                            latestEbayObservation.current !== visibleEbayObservation ||
                            ebayOwner.current.epoch !== visibleEbayObservation.owner ||
                            currentPricingTab.current !== 'pricing'
                          )
                            return;
                          setPricingDraft((current) =>
                            current === null
                              ? current
                              : { ...current, price: centsToUsdInput(amountMinor) },
                          );
                        }}
                      />
                    </div>
                  ) : (
                    <p className="tcvs-muted">Pricing is available while reviewing a card.</p>
                  )}
                </div>
              </div>
            </>
          )}
        </aside>
      </div>

      {workbenchView === 'CARD' && imagePopoutOpen && active !== null ? (
        <WorkbenchImageReview
          card={active}
          imageIssue={imageIssue}
          imageIssueSaving={busy === 'image-quality'}
          imageAdjustmentBusySide={imageAdjustmentBusySide}
          imageRotations={imageRotations}
          imageAlignmentGrid={imageAlignmentGrid}
          onImageIssueChange={(checked) => void updateImageIssue(checked)}
          onImageRotationChange={changeImageRotation}
          onImageAlignmentGridChange={setImageAlignmentGrid}
          onImageRotationsReset={resetImageRotations}
          onClose={closeImagePopout}
        />
      ) : null}
    </div>
  );
}
